import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Space, Tag, message, Divider, Descriptions, Tooltip, Alert, Steps, Modal, Input, InputNumber, Select, Switch, Table, Pagination, Tabs } from 'antd';
import { ExclamationCircleOutlined, CheckCircleFilled, InfoCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import request from '../../../utils/request';
import PickStepRenderer from './renderers/PickStepRenderer';
import IocPickStepRenderer from './renderers/IocPickStepRenderer';
import SubmitWrapUpRenderer from './renderers/SubmitWrapUpRenderer';
import CombinedRelabelBoxRenderer from './renderers/CombinedRelabelBoxRenderer';
import RelabelStepRenderer from './renderers/RelabelStepRenderer';
import SingleDropshipPackRenderer from './renderers/SingleDropshipPackRenderer';
import RegularOutboundPackRenderer from './renderers/RegularOutboundPackRenderer';
import SortAndCountStepRenderer from './renderers/SortAndCountStepRenderer';
import NonStdRecordRenderer from './renderers/NonStdRecordRenderer';
import NonStdSnapshotView from '../../nonstandard-ops/NonStdSnapshotView';
import NonStdSubmitRenderer from './renderers/NonStdSubmitRenderer';

// 渲染器注册表：renderer_key -> 组件
const STEP_RENDERERS: Record<string, any> = {
  PICK: PickStepRenderer,
  SUBMIT_AND_WRAPUP: SubmitWrapUpRenderer,
  WRAP_UP: SubmitWrapUpRenderer,
  RELABEL: RelabelStepRenderer,
  SORT_AND_COUNT: SortAndCountStepRenderer,
  SORT_COUNT: SortAndCountStepRenderer,
  // 库存非标准操作指令（通用指令）
  NONSTD_RECORD: NonStdRecordRenderer,
  NONSTD_SUBMIT: NonStdSubmitRenderer,
  'BOX/BAG_PACK': SingleDropshipPackRenderer,
  // 兼容历史键
  BOX_PACK: SingleDropshipPackRenderer,
  BAG_PACK: SingleDropshipPackRenderer,
};

const StaffInstructionExecutePage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const instrId = Number(id);
  const [loading, setLoading] = useState(false);
  const [instr, setInstr] = useState<any>(null);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [execSteps, setExecSteps] = useState<any[]>([]);
  const [taskId, setTaskId] = useState<number | null>(null);
  const [taskExecutionSessionId, setTaskExecutionSessionId] = useState<string>('');
  // 当前阶段 UI schema：stepId -> schema
  const [stageUISchemas, setStageUISchemas] = useState<Record<string, any>>({});
  // 当前阶段 数据源：stepId -> data
  const [stageUIData, setStageUIData] = useState<Record<string, any>>({});
  // 表单草稿：stepId -> metrics 对象
  const [metricsDraft, setMetricsDraft] = useState<Record<string, any>>({});

  // ========= 修复：收尾阶段导致“标签页切换后 URL 已变但页面仍停留在执行页” =========
  // 背景：
  // - SubmitWrapUpRenderer 会在 useEffect 中调用 onChange 同步父级 metricsDraft
  // - 若 Execute 每次 render 都传入新的 onChange 函数引用，会导致收尾页 effect 因 onChange 变化而重复执行 -> setState 高频
  // - 同时 TabsManager 的导航使用 startTransition(navigate)，属于低优先级更新，容易被上述高频更新“饿死”，表现为 URL 已切走但 UI 仍旧停留
  // 方案：
  // - 为每个 stepId(sid) 缓存一个稳定引用的 onChange 函数，避免无意义的引用变化
  const setMetricByStep = useCallback((sid: string, key: string, val: any) => {
    const sidStr = String(sid ?? '');
    if (!sidStr) return;
    setMetricsDraft(prev => ({ ...prev, [sidStr]: { ...(prev?.[sidStr] || {}), [key]: val } }));
  }, []);
  const onChangeByStepRef = useRef<Map<string, (k: string, v: any) => void>>(new Map());
  // 库存非标准操作 · 确认完成：容器核对进度（按步骤ID），由步骤组件上报；单独存放，不会被步骤草稿的重新加载覆盖
  const [nonstdConfirmGates, setNonstdConfirmGates] = useState<Record<string, any>>({});
  const getOnChangeForStep = useCallback((sid: string) => {
    const sidStr = String(sid ?? '');
    if (!sidStr) return (k: string, v: any) => {};
    const existed = onChangeByStepRef.current.get(sidStr);
    if (existed) return existed;
    const fn = (k: string, v: any) => setMetricByStep(sidStr, k, v);
    onChangeByStepRef.current.set(sidStr, fn);
    return fn;
  }, [setMetricByStep]);
  // 当任务/指令切换时清空缓存，避免 stepId 复用导致误写
  useEffect(() => {
    try { onChangeByStepRef.current.clear(); } catch {}
  }, [taskId, instrId]);
  // 执行提醒折叠状态（不可彻底关闭，仅可折叠/展开）
  const [tipsCollapsed, setTipsCollapsed] = useState<boolean>(false);
  // 当前操作阶段：使用说明弹窗
  const [stageHelpOpen, setStageHelpOpen] = useState<boolean>(false);
  // 当前阶段索引（左→右）
  const [currentStageIdx, setCurrentStageIdx] = useState<number>(0);
  // 拣货快照查看
  const [snapshotVisible, setSnapshotVisible] = useState<boolean>(false);
  const [snapshotLoading, setSnapshotLoading] = useState<boolean>(false);
  const [snapshotData, setSnapshotData] = useState<any>(null);
  // 换标+装箱快照查看
  const [rpSnapshotVisible, setRpSnapshotVisible] = useState<boolean>(false);
  const [rpSnapshotLoading, setRpSnapshotLoading] = useState<boolean>(false);
  const [rpSnapshotData, setRpSnapshotData] = useState<any>(null);
  // 单指令代发“配货|装箱|贴单”快照查看
  const [sdpSnapshotVisible, setSdpSnapshotVisible] = useState<boolean>(false);
  const [sdpSnapshotLoading, setSdpSnapshotLoading] = useState<boolean>(false);
  const [sdpSnapshotData, setSdpSnapshotData] = useState<any>(null);
  const [sdpSnapshotOpPage, setSdpSnapshotOpPage] = useState<number>(1);
  const [sdpSnapshotOpPageSize, setSdpSnapshotOpPageSize] = useState<number>(10);
  // 常规出库“配货|装箱|贴单”快照查看
  const [ropSnapshotVisible, setRopSnapshotVisible] = useState<boolean>(false);
  const [ropSnapshotLoading, setRopSnapshotLoading] = useState<boolean>(false);
  const [ropSnapshotData, setRopSnapshotData] = useState<any>(null);
  useEffect(() => {
    if (!sdpSnapshotVisible) return;
    setSdpSnapshotOpPage(1);
  }, [sdpSnapshotVisible, sdpSnapshotData]);
  // 收尾快照查看
  const [wuSnapshotVisible, setWuSnapshotVisible] = useState<boolean>(false);
  // 库存非标准操作：阶段快照（执行记录 / 确认完成），数据来自步骤提交时后端固化的 collected_metrics.nonstd_snapshot
  const [nonstdSnapshotView, setNonstdSnapshotView] = useState<any>(null);
  const [wuSnapshotLoading, setWuSnapshotLoading] = useState<boolean>(false);
  const [wuSnapshotData, setWuSnapshotData] = useState<any>(null);
  // 分拣清点快照查看
  const [scSnapshotVisible, setScSnapshotVisible] = useState<boolean>(false);
  const [scSnapshotLoading, setScSnapshotLoading] = useState<boolean>(false);
  const [scSnapshotData, setScSnapshotData] = useState<any>(null);
  // 调试：跟踪拣货快照弹窗状态
  useEffect(() => {
    try { console.debug('[PickSnap][STATE] visible=', snapshotVisible, 'hasData=', !!snapshotData); } catch {}
  }, [snapshotVisible, snapshotData]);

  // 统一复用：快照与确认弹窗使用的汇总结果
  const [snapshotSummary, setSnapshotSummary] = useState<{
    targetSummary: { rows: Array<any>; totals: { need: number; got: number; diff: number; doneCount: number; total: number } };
    containerSummary: { byCont: Record<string, { units: Record<string, number> }>; offShelves: Set<string>; stats: { total_qty: number; kinds: number; containers: number } };
    flags: { hasOver: boolean; hasUnder: boolean; allZero: boolean };
  } | null>(null);
  const [snapshotIocPreview, setSnapshotIocPreview] = useState<{
    rows: Array<{
      code: string;
      location: string;
      instructionCode: string;
      containerType: string;
      contentType: string;
      contentSummary: string;
      contentDetail: string;
      picked: boolean;
    }>;
    stats: { total: number; picked: number; todo: number };
    headerRecordCount: number;
    detailPickLogs: any[];
  } | null>(null);

  // 构建器：由 pick_rows 与 pickLogs 生成目标汇总
  const buildTargetSummary = (pickRows: any[], pickLogs: any[]) => {
    const targetNeed: Record<string, { target: string; need: number; mark?: string }> = {};
    const actualPick: Record<string, number> = {};
    (Array.isArray(pickRows) ? pickRows : []).forEach((row:any) => {
      const t = String(row?.target_label || '').trim();
      if (!t) return;
      const need = Number(row?.target_qty || 0);
      const mark = (row?.product_sorting_code || row?.sorting_code || '') as string;
      if (!targetNeed[t]) targetNeed[t] = { target: t, need: 0, mark };
      targetNeed[t].need += (Number.isFinite(need) ? need : 0);
      if (mark && !targetNeed[t].mark) targetNeed[t].mark = mark;
    });
    (Array.isArray(pickLogs) ? pickLogs : []).forEach((r:any) => {
      const t = String(r?.target_code || '').trim();
      const q = Number(r?.qty || 0);
      if (!t || !(q > 0)) return;
      actualPick[t] = (actualPick[t] || 0) + q;
    });
    const rows = Object.keys(targetNeed).map((k) => {
      const need = Number(targetNeed[k].need || 0);
      const got = Number(actualPick[k] || 0);
      const diff = need - got;
      return { target: k, mark: targetNeed[k].mark || '-', need, got, diff, done: diff <= 0 };
    });
    const totals = rows.reduce((acc, r) => {
      acc.need += r.need; acc.got += r.got;
      if (r.done) acc.doneCount += 1;
      return acc;
    }, { need: 0, got: 0, diff: 0, doneCount: 0, total: rows.length } as any);
    totals.diff = totals.need - totals.got;
    return { rows, totals };
  };
  // 构建器：由 pickLogs 生成容器汇总
  const buildContainerSummary = (pickLogs: any[]) => {
    const byCont: Record<string, { units: Record<string, number> }> = {};
    const offShelves = new Set<string>();
    (Array.isArray(pickLogs) ? pickLogs : []).forEach((r:any) => {
      const c = String(r?.container_code || '').trim();
      const u = String(r?.target_code || '').trim();
      const q = Number(r?.qty || 0);
      if (!c || !u || !(q > 0)) return;
      if (!byCont[c]) byCont[c] = { units: {} };
      byCont[c].units[u] = (byCont[c].units[u] || 0) + q;
      if (r?.off_shelf) offShelves.add(c);
    });
    const totalQty = (Array.isArray(pickLogs) ? pickLogs : []).reduce((s:number, r:any)=> s + (Number(r?.qty||0) || 0), 0);
    const kinds = new Set((Array.isArray(pickLogs) ? pickLogs : []).map((r:any)=> String(r?.target_code || '').trim()).filter(Boolean)).size;
    const containers = new Set((Array.isArray(pickLogs) ? pickLogs : []).map((r:any)=> String(r?.container_code || '').trim()).filter(Boolean)).size;
    return { byCont, offShelves, stats: { total_qty: totalQty, kinds, containers } };
  };
  // 标志位
  const computeFlags = (targetSummary: { rows: Array<any> }) => {
    const hasOver = targetSummary.rows.some((r:any) => r.diff < 0);
    const hasUnder = targetSummary.rows.some((r:any) => r.diff > 0);
    const allZero = targetSummary.rows.length > 0 && targetSummary.rows.every((r:any) => r.diff === 0);
    return { hasOver, hasUnder, allZero };
  };

  // 阶段显示名称：单指令中间“配货|装箱|贴单”统一命名
  const getStageDisplayName = (steps: any[], maxNames = 3) => {
    try {
      const arr = Array.isArray(steps) ? steps : [];
      const types = arr.map((s:any) => String(s?.step_type || s?.renderer_key || '').trim().toUpperCase());
      const hasRelabel = types.includes('RELABEL') || arr.some((s:any) => String(s?.title || '').includes('换标'));
      const hasPackLike = types.some((t:string) => (
        t === 'BOX/BAG_PACK' || t === 'BOX_PACK' || t === 'BAG_PACK' || t === 'BOX' || t === 'BAG' || t === 'DROPSHIP_PACK'
      ));
      if (hasPackLike && !hasRelabel) return '配货|装箱|贴单';
      const names = arr.map((s:any) => String((s?.title || s?.step_type || '')).trim()).filter(Boolean);
      const uniq = Array.from(new Set(names));
      const head = uniq.slice(0, maxNames).join('、');
      const more = uniq.length > maxNames ? ` 等${uniq.length}步` : '';
      return head + more;
    } catch {
      return '';
    }
  };

  // ========= 换标+装箱：方案B 分页渲染（预览 & 审计查看共用） =========
  const RelabelPackSnapshotBlock: React.FC<{
    overall: any | null;
    mappingRows: any[];
    boxRows: any[];
    pool: any | null;
    // 可选：操作记录（用于在“按容器统计”中展开显示每个容器的内容物明细：新条码+数量）
    records?: any[];
    // 可选：系统生成标签（PDF）归档：new_code -> { file_url, ... }
    genLabelMap?: Record<string, any>;
    emptyHint?: string;
  }> = ({ overall, mappingRows, boxRows, pool, records, genLabelMap, emptyHint }) => {
    const [mapPage, setMapPage] = useState<number>(1);
    const [mapPageSize, setMapPageSize] = useState<number>(50);
    const [boxPage, setBoxPage] = useState<number>(1);
    const [boxPageSize, setBoxPageSize] = useState<number>(20);
    const [boxCancelledPage, setBoxCancelledPage] = useState<number>(1);
    const [boxCancelledPageSize, setBoxCancelledPageSize] = useState<number>(20);
    const [poolPage, setPoolPage] = useState<number>(1);
    const [poolPageSize, setPoolPageSize] = useState<number>(20);

    const safeMappingRows = Array.isArray(mappingRows) ? mappingRows : [];
    const safeBoxRows = Array.isArray(boxRows) ? boxRows : [];
    const poolRowsByNew: any[] = Array.isArray(pool?.rowsByNew) ? pool.rowsByNew : [];
    const poolTotalPos = Math.floor(Number(pool?.totalPos || 0) || 0);
    const poolNewKinds = Math.floor(Number(pool?.newKinds || 0) || 0);
    const hasPool = poolTotalPos > 0 && poolRowsByNew.length > 0;

    const mappingDonePieces = useMemo(() => {
      return (safeMappingRows || []).reduce((s:number, r:any)=> s + Math.max(0, Math.floor(Number(r?.used || 0) || 0)), 0);
    }, [safeMappingRows]);

    // 快照用：IU 已完成数量（用于“操作对象匹配信息”里展示“已xx”）
    const doneByIuId = useMemo(() => {
      const m = new Map<number, number>();
      try {
        const recs: any[] = Array.isArray(records) ? records : [];
        recs.forEach((r: any) => {
          if (String(r?.event || '').trim() !== '换标') return;
          const q = Math.floor(Number(r?.qty || 0) || 0);
          if (!Number.isFinite(q) || q <= 0) return;
          const iu = Math.floor(Number(r?.op_iu_id || 0));
          if (!Number.isFinite(iu) || iu <= 0) return;
          m.set(iu, (m.get(iu) || 0) + q);
        });
      } catch {}
      return m;
    }, [records]);

    const normCode = (v: any) => String(v || '').trim().toUpperCase();
    const genMap = (genLabelMap && typeof genLabelMap === 'object') ? genLabelMap : {};
    const getGenPdfUrl = (newCode: string): string => {
      try {
        const k = normCode(newCode);
        const it = (genMap as any)?.[k];
        const u = String(it?.file_url || it?.pdf_file || '').trim();
        return u;
      } catch {
        return '';
      }
    };
    const previewPdf = (url: string) => {
      const u = String(url || '').trim();
      if (!u) return;
      const finalUrl = `${u}${u.includes('?') ? '&' : '?'}t=${Date.now()}`;
      try { window.open(finalUrl, '_blank', 'noreferrer'); } catch {}
    };
    const downloadPdf = async (url: string, fileName: string) => {
      const u = String(url || '').trim();
      if (!u) return;
      const finalUrl = `${u}${u.includes('?') ? '&' : '?'}t=${Date.now()}`;
      try {
        const resp = await fetch(finalUrl, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('下载失败');
        const blob = await resp.blob();
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName || '系统生成标签.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(href), 1500);
      } catch {
        message.error('下载失败');
      }
    };

    const renderMatchTargets = (txtRaw: any) => {
      const txt = String(txtRaw ?? '').trim();
      const isMiss = (!txt || txt === '-' || txt === '[]');
      if (isMiss) return <span style={{ color:'#cf1322', fontWeight: 700 }}>-</span>;
      let arr: any[] = [];
      try { arr = JSON.parse(txt); } catch { arr = []; }
      const list = Array.isArray(arr) ? arr : [];
      if (!list.length) return <span style={{ color:'#cf1322', fontWeight: 700 }}>-</span>;
      try {
        list.sort((a:any, b:any) => {
          const ua = a?.use_all_available ? 1 : 0;
          const ub = b?.use_all_available ? 1 : 0;
          if (ua !== ub) return ua - ub; // 否在前
          const ea = (a?.expected_qty == null || !Number.isFinite(Number(a.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(a.expected_qty);
          const eb = (b?.expected_qty == null || !Number.isFinite(Number(b.expected_qty))) ? Number.MAX_SAFE_INTEGER : Number(b.expected_qty);
          if (ea !== eb) return ea - eb;
          return String(a?.system_barcode || '').localeCompare(String(b?.system_barcode || ''));
        });
      } catch {}
      const renderOne = (x:any, i:number) => {
        const iuId = Math.floor(Number(x?.iu_id || 0));
        const sys = String(x?.system_barcode || '').trim() || (iuId ? `IU#${iuId}` : '-');
        const useAll = !!x?.use_all_available;
        const eq = (x?.expected_qty == null || x?.expected_qty === '') ? null : Number(x.expected_qty);
        const eqTxt = (eq == null || !Number.isFinite(eq)) ? '-' : String(eq);
        const done = (Number.isFinite(iuId) && iuId > 0) ? Math.floor(Number(doneByIuId.get(iuId) || 0) || 0) : 0;
        // 颜色：是=蓝色系；否=橘红色背景（与执行页一致）
        const bg = useAll ? '#e6f4ff' : '#ff7a45';
        const bd = useAll ? '#91caff' : '#ff7a45';
        const fg = useAll ? '#0958d9' : '#ffffff';
        return (
          <Tag
            key={`snap_mt_${i}`}
            style={{
              marginInlineEnd: 0,
              marginBottom: 4,
              background: bg,
              borderColor: bd,
              color: fg,
              borderRadius: 999,
              fontWeight: 700,
              padding: '0 8px',
              lineHeight: '20px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              maxWidth: 320,
            }}
          >
            <span style={{ fontFamily:'monospace', maxWidth: 140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sys}</span>
            <span style={{ opacity: 0.9 }}>{useAll ? '是' : '否'}</span>
            <span style={{ opacity: 0.9 }}>{`预${eqTxt}`}</span>
            <span style={{ color: fg, background: useAll ? 'rgba(9,88,217,0.10)' : 'rgba(255,255,255,0.18)', borderRadius: 999, padding: '0 6px', lineHeight: '18px' }}>
              {`已${done}`}
            </span>
          </Tag>
        );
      };
      const maxShow = 3;
      const short = list.slice(0, maxShow);
      const more = list.length > maxShow ? list.slice(maxShow) : [];
      const nodes: any[] = [];
      short.forEach((x:any, i:number) => { nodes.push(renderOne(x, i)); });
      if (more.length > 0) {
        const full = (
          <div style={{ maxWidth: 560 }}>
            <div style={{ display:'flex', flexWrap:'wrap', gap: 6 }}>
              {list.map((x:any, idx:number) => renderOne(x, idx))}
            </div>
          </div>
        );
        nodes.push(
          <Tooltip title={full}>
            <Tag
              style={{
                marginInlineEnd: 0,
                marginBottom: 4,
                borderRadius: 999,
                cursor: 'pointer',
                color: '#0958d9',
                borderColor: '#91caff',
                background: '#e6f4ff',
                fontWeight: 700,
              }}
            >
              {`+${more.length}`}
            </Tag>
          </Tooltip>
        );
      }
      return <span style={{ display:'inline-flex', flexWrap:'wrap', gap: 6, alignItems:'center' }}>{nodes}</span>;
    };

    const boxDonePieces = useMemo(() => {
      return (safeBoxRows || []).reduce((s:number, r:any) => {
        const q = Math.floor(Number(r?.qty || 0) || 0);
        return s + (Number.isFinite(q) && q > 0 ? q : 0);
      }, 0);
    }, [safeBoxRows]);

    // 容器列表：按容器编码递增排序（自然排序：BOX2 < BOX10）
    const sortedBoxRows = useMemo(() => {
      const arr = [...safeBoxRows];
      const norm = (v:any) => String(v ?? '').trim();
      arr.sort((a:any, b:any) => {
        const ca = norm(a?.container_code);
        const cb = norm(b?.container_code);
        return ca.localeCompare(cb, undefined, { numeric: true, sensitivity: 'base' });
      });
      return arr;
    }, [safeBoxRows]);
    const sortedActiveBoxRows = useMemo(() => (
      (Array.isArray(sortedBoxRows) ? sortedBoxRows : []).filter((r: any) => {
        const st = String(r?.status || '').trim().toLowerCase();
        const stZh = String(r?.status_zh || r?.status_text || '').trim();
        return st !== 'cancelled' && stZh !== '已作废';
      })
    ), [sortedBoxRows]);
    const sortedCancelledBoxRows = useMemo(() => (
      (Array.isArray(sortedBoxRows) ? sortedBoxRows : []).filter((r: any) => {
        const st = String(r?.status || '').trim().toLowerCase();
        const stZh = String(r?.status_zh || r?.status_text || '').trim();
        return st === 'cancelled' || stZh === '已作废';
      })
    ), [sortedBoxRows]);

    // 容器内容物明细：从 records 聚合出 container_code -> [{new_code, qty}]
    const itemsByContainer = useMemo(() => {
      try {
        const recs: any[] = Array.isArray(records) ? records : [];
        const by = new Map<string, Map<string, number>>();
        recs.forEach((r: any) => {
          const cc = String(r?.container_code || '').trim();
          if (!cc) return;
          // 按照“容器统计(不含调整池)”口径：忽略调整池
          if (cc.toUpperCase().startsWith('POOL-')) return;
          const newCode = String(r?.new_code || '').trim().toUpperCase();
          if (!newCode) return;
          const q = Math.trunc(Number(r?.qty || 0) || 0);
          if (!Number.isFinite(q) || q === 0) return;
          const inner = by.get(cc) || new Map<string, number>();
          inner.set(newCode, (inner.get(newCode) || 0) + q);
          by.set(cc, inner);
        });
        // 输出：仅保留 >0 的净额
        const out = new Map<string, Array<{ new_code: string; qty: number }>>();
        by.forEach((inner, cc) => {
          const rows = Array.from(inner.entries())
            .map(([new_code, qty]) => ({ new_code, qty: Math.floor(Number(qty || 0) || 0) }))
            .filter((x) => x.new_code && Number.isFinite(x.qty) && x.qty > 0)
            .sort((a, b) => (b.qty - a.qty) || a.new_code.localeCompare(b.new_code));
          out.set(cc, rows);
        });
        return out;
      } catch {
        return new Map<string, Array<{ new_code: string; qty: number }>>();
      }
    }, [records]);

    const fmtTime = (ts: any) => {
      try {
        const n = Number(ts);
        if (!Number.isFinite(n) || n <= 0) return '-';
        return new Date(n).toLocaleString('zh-CN');
      } catch {
        return '-';
      }
    };
    const fmtDur = (ms: any) => {
      try {
        const n = Math.floor(Number(ms || 0) || 0);
        if (!Number.isFinite(n) || n <= 0) return '-';
        const s = Math.floor(n / 1000);
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (h > 0) return `${h}h${m}m${sec}s`;
        if (m > 0) return `${m}m${sec}s`;
        return `${sec}s`;
      } catch {
        return '-';
      }
    };
    // 容器时间范围：container_code -> {startTs,endTs,durMs}
    const boxTimeByContainer = useMemo(() => {
      const m = new Map<string, { startTs: number; endTs: number; durMs: number }>();
      try {
        const recs: any[] = Array.isArray(records) ? records : [];
        recs.forEach((r: any) => {
          const cc = String(r?.container_code || '').trim();
          if (!cc) return;
          if (cc.toUpperCase().startsWith('POOL-')) return;
          const ts = Number(r?.ts || 0) || 0;
          if (!Number.isFinite(ts) || ts <= 0) return;
          const cur = m.get(cc) || { startTs: ts, endTs: ts, durMs: 0 };
          cur.startTs = Math.min(cur.startTs, ts);
          cur.endTs = Math.max(cur.endTs, ts);
          cur.durMs = Math.max(0, cur.endTs - cur.startTs);
          m.set(cc, cur);
        });
      } catch {}
      return m;
    }, [records]);

    const mapPaged = useMemo(() => {
      const p = Math.max(1, mapPage);
      const ps = Math.max(1, mapPageSize);
      const start = (p - 1) * ps;
      return safeMappingRows.slice(start, start + ps);
    }, [safeMappingRows, mapPage, mapPageSize]);

    const boxPaged = useMemo(() => {
      const p = Math.max(1, boxPage);
      const ps = Math.max(1, boxPageSize);
      const start = (p - 1) * ps;
      return sortedActiveBoxRows.slice(start, start + ps);
    }, [sortedActiveBoxRows, boxPage, boxPageSize]);
    const boxCancelledPaged = useMemo(() => {
      const p = Math.max(1, boxCancelledPage);
      const ps = Math.max(1, boxCancelledPageSize);
      const start = (p - 1) * ps;
      return sortedCancelledBoxRows.slice(start, start + ps);
    }, [sortedCancelledBoxRows, boxCancelledPage, boxCancelledPageSize]);

    const poolPaged = useMemo(() => {
      const p = Math.max(1, poolPage);
      const ps = Math.max(1, poolPageSize);
      const start = (p - 1) * ps;
      return poolRowsByNew.slice(start, start + ps);
    }, [poolRowsByNew, poolPage, poolPageSize]);

    if (!overall) {
      return <div style={{ color:'#999' }}>{emptyHint || '（未采集到总进度表快照）'}</div>;
    }

    const target = (() => {
      const t = Number(String(overall?.targetQtyText ?? '').replace(/[^\d.-]/g, ''));
      return Number.isFinite(t) && t > 0 ? Math.floor(t) : 0;
    })();
    const done = Math.floor(Number(overall?.doneQty || 0) || 0);
    const rateNum = target > 0 ? (done / target) * 100 : NaN;
    const rateText = Number.isFinite(rateNum) ? `${Number(rateNum.toFixed(1))}%` : (String(overall?.rateText ?? '-') || '-');
    const tone = (() => {
      if (!Number.isFinite(rateNum)) return { bg:'#fafafa', bd:'#d9d9d9', text:'#595959' };
      if (Math.abs(rateNum - 100) < 0.05) return { bg:'#f6ffed', bd:'#b7eb8f', text:'#135200' };
      if (rateNum < 100) return { bg:'#e6f4ff', bd:'#91caff', text:'#0958d9' };
      return { bg:'#fff2e8', bd:'#ffbb96', text:'#ad4e00' };
    })();

    const boxRateNum = target > 0 ? (boxDonePieces / target) * 100 : NaN;
    const boxRateText = Number.isFinite(boxRateNum) ? `${Number(boxRateNum.toFixed(1))}%` : '-';
    const boxTone = (() => {
      if (!Number.isFinite(boxRateNum)) return { bg:'#fafafa', bd:'#d9d9d9', text:'#595959' };
      if (Math.abs(boxRateNum - 100) < 0.05) return { bg:'#f6ffed', bd:'#b7eb8f', text:'#135200' };
      if (boxRateNum < 100) return { bg:'#e6f4ff', bd:'#91caff', text:'#0958d9' };
      return { bg:'#fff2e8', bd:'#ffbb96', text:'#ad4e00' };
    })();

    return (
      <div>
        {/* 1) 总进度概览（左右两块：总进度/按容器统计） */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {/* 左：总进度概览(含调整池) */}
          <div style={{ flex: '1 1 520px', border:'1px solid #f0f0f0', borderRadius: 8, padding: 10, background:'#ffffff' }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>总进度概览(含调整池)</div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 16, flexWrap:'wrap' }}>
              <div style={{ display:'inline-flex', gap: 12, alignItems:'center', flexWrap:'wrap' }}>
                <span style={{ color:'#595959' }}>总目标 <b style={{ color:'#000' }}>{target > 0 ? target : '-'}</b></span>
                <span style={{ color:'#595959' }}>已完成 <b style={{ color:'#000' }}>{done}</b></span>
                <span style={{ color:'#595959' }}>完成率</span>
                <span
                  style={{
                    display:'inline-flex',
                    alignItems:'center',
                    justifyContent:'center',
                    padding:'6px 12px',
                    borderRadius: 10,
                    border:`2px solid ${tone.bd}`,
                    background: tone.bg,
                  }}
                >
                  <span style={{ fontSize: 34, fontWeight: 900, lineHeight: 1, color: tone.text }}>
                    {rateText}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* 右：按容器统计(不含调整池) */}
          <div style={{ flex: '1 1 520px', border:'1px solid #f0f0f0', borderRadius: 8, padding: 10, background:'#ffffff' }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>按容器统计(不含调整池)</div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 16, flexWrap:'wrap' }}>
              <div style={{ display:'inline-flex', gap: 12, alignItems:'center', flexWrap:'wrap' }}>
                <span style={{ color:'#595959' }}>总目标 <b style={{ color:'#000' }}>{target > 0 ? target : '-'}</b></span>
                <span style={{ color:'#595959' }}>已装入容器 <b style={{ color:'#000' }}>{boxDonePieces}</b></span>
                <span style={{ color:'#595959' }}>完成率</span>
                <span
                  style={{
                    display:'inline-flex',
                    alignItems:'center',
                    justifyContent:'center',
                    padding:'6px 12px',
                    borderRadius: 10,
                    border:`2px solid ${boxTone.bd}`,
                    background: boxTone.bg,
                  }}
                >
                  <span style={{ fontSize: 34, fontWeight: 900, lineHeight: 1, color: boxTone.text }}>
                    {boxRateText}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* 2) 按照条码映射统计 */}
        <div style={{ border:'1px solid #f0f0f0', borderRadius: 8, padding: 10, background:'#ffffff' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700 }}>按照条码映射统计(含调整池)</div>
            <div style={{ color:'#595959' }}>
              共 <b>{safeMappingRows.length}</b> 条映射记录 已完成 <b>{mappingDonePieces}</b> 件
            </div>
          </div>
          <div style={{ maxHeight: 260, overflow:'auto', border:'1px solid #f0f0f0', borderRadius: 6 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, width: 70, textAlign:'center' }}>序号</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, width: 260, textAlign:'center' }}>操作对象匹配信息</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, width: 220, textAlign:'center' }}>旧条码</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, width: 220, textAlign:'center' }}>新条码</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, width: 120, textAlign:'center' }}>数量分配</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, width: 120, textAlign:'center' }}>已完成</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, width: 180, textAlign:'center' }}>系统生成标签(PDF)</th>
                </tr>
              </thead>
              <tbody>
                {mapPaged.map((r:any, i:number) => (
                  <tr key={`${String(r?.__key || '')}|mp|${i}`}>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{(mapPage - 1) * mapPageSize + i + 1}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{renderMatchTargets((r as any)?.matchTargets)}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontFamily:'monospace' }}>{String(r?.old || '')}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontFamily:'monospace' }}>{String(r?.newer || '')}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{(r?.limitQty == null || r?.limitQty === '' || r?.limitQty === undefined) ? '-' : String(r.limitQty)}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontWeight: 700 }}>{Math.floor(Number(r?.used || 0) || 0)}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>
                      {(() => {
                        const code = String(r?.newer || '').trim();
                        const url = getGenPdfUrl(code);
                        if (!url) return <span style={{ color:'#8c8c8c' }}>-</span>;
                        const name = `系统生成标签-${normCode(code)}.pdf`;
                        return (
                          <span style={{ display:'inline-flex', gap: 8, alignItems:'center', justifyContent:'center' }}>
                            <Button size="small" onClick={() => previewPdf(url)}>预览</Button>
                            <Button size="small" onClick={() => downloadPdf(url, name)}>下载</Button>
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
                {safeMappingRows.length === 0 ? (
                  <tr><td colSpan={7} style={{ border:'1px solid #d9d9d9', padding:6, color:'#999' }}>无映射数据</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={mapPage}
              pageSize={mapPageSize}
              total={safeMappingRows.length}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['20','50','100','200'] as any}
              onChange={(p, ps) => { setMapPage(p); setMapPageSize(ps); }}
              showTotal={(t, range) => `共 ${t} 条，显示 ${range[0]}-${range[1]} 条`}
            />
          </div>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* 3) 按照容器统计（不含调整池） */}
        <div style={{ border:'1px solid #f0f0f0', borderRadius: 8, padding: 10, background:'#ffffff' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700 }}>按照容器统计(不含调整池)</div>
            <div style={{ color:'#595959' }}>
              已完成容器 <b>{Math.floor(Number(overall?.closedCount || 0) || 0)}</b>　
              总体积 <b>{String(overall?.volM3Text ?? '0')}m³</b>　
              总重量 <b>{(() => { const k = Number(overall?.kgText); return Number.isFinite(k) ? `${Math.ceil(k)}kg` : `${String(overall?.kgText ?? '0')}kg`; })()}</b>　
              已装入容器 <b>{boxDonePieces}件</b>　
              已作废容器数量 <b>{sortedCancelledBoxRows.length}</b>
            </div>
          </div>
          <div style={{ marginBottom: 6, color: '#595959', fontWeight: 600 }}>可用容器</div>
          <div style={{ maxHeight: 260, overflow:'auto', border:'1px solid #f0f0f0', borderRadius: 6 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 70 }}>序号</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 220 }}>容器</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 110 }}>新标签种类</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 90 }}>总件数</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 110 }}>重量(kg)</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 140 }}>尺寸(cm)</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 160 }}>开始时间</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 160 }}>结束时间</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 120 }}>总用时</th>
                  <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', minWidth: 520 }}>内容物明细（新条码×数量）</th>
                </tr>
              </thead>
              <tbody>
                {boxPaged.map((r:any, i:number) => (
                  <tr key={`${String(r?.container_code || '')}|bp|${i}`}>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{(boxPage - 1) * boxPageSize + i + 1}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontFamily:'monospace' }}>{String(r?.container_code || '')}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{Math.floor(Number(r?.newKinds || 0) || 0)}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontWeight: 700 }}>{Math.floor(Number(r?.qty || 0) || 0)}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{(r?.weight_kg != null ? String(r.weight_kg) : '-')}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{String(r?.dims_cm_text || '-')}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontFamily:'monospace' }}>
                      {(() => {
                        const cc = String(r?.container_code || '').trim();
                        const t = boxTimeByContainer.get(cc);
                        return t ? fmtTime(t.startTs) : '-';
                      })()}
                    </td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontFamily:'monospace' }}>
                      {(() => {
                        const cc = String(r?.container_code || '').trim();
                        const t = boxTimeByContainer.get(cc);
                        return t ? fmtTime(t.endTs) : '-';
                      })()}
                    </td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontFamily:'monospace' }}>
                      {(() => {
                        const cc = String(r?.container_code || '').trim();
                        const t = boxTimeByContainer.get(cc);
                        return t ? fmtDur(t.durMs) : '-';
                      })()}
                    </td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6 }}>
                      {(() => {
                        const cc = String(r?.container_code || '').trim();
                        const items = itemsByContainer.get(cc) || [];
                        if (!items.length) return <span style={{ color:'#999' }}>-</span>;
                        const show = items.slice(0, 10);
                        const full = (
                          <div style={{ maxWidth: 720, maxHeight: 260, overflow: 'auto' }}>
                            <div style={{ display:'flex', flexWrap:'wrap', gap: 6 }}>
                              {items.map((x:any, idx2:number) => (
                                <Tag key={`full_${cc}_${x.new_code}_${idx2}`} style={{ marginInlineEnd: 0, borderRadius: 999 }}>
                                  <span style={{ fontFamily:'monospace' }}>{String(x.new_code || '-')}</span>
                                  <span style={{ marginLeft: 6, fontWeight: 800, color:'#cf1322' }}>{String(Math.floor(Number(x.qty||0)||0))}</span>
                                </Tag>
                              ))}
                            </div>
                          </div>
                        );
                        return (
                          <span style={{ display:'inline-flex', flexWrap:'wrap', gap: 6 }}>
                            {show.map((x:any, idx2:number) => (
                              <Tag key={`${cc}_${x.new_code}_${idx2}`} style={{ marginInlineEnd: 0, borderRadius: 999 }}>
                                <span style={{ fontFamily:'monospace' }}>{String(x.new_code || '-')}</span>
                                <span style={{ marginLeft: 6, fontWeight: 800, color:'#cf1322' }}>{String(Math.floor(Number(x.qty||0)||0))}</span>
                              </Tag>
                            ))}
                            {items.length > 10 ? (
                              <Tooltip title={full}>
                                <Tag style={{ marginInlineEnd: 0, borderRadius: 999, cursor: 'pointer' }}>{`+${items.length - 10}`}</Tag>
                              </Tooltip>
                            ) : null}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
                {sortedActiveBoxRows.length === 0 ? (
                  <tr><td colSpan={10} style={{ border:'1px solid #d9d9d9', padding:6, color:'#999' }}>无容器数据</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={boxPage}
              pageSize={boxPageSize}
              total={sortedActiveBoxRows.length}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['10','20','50','100'] as any}
              onChange={(p, ps) => { setBoxPage(p); setBoxPageSize(ps); }}
              showTotal={(t, range) => `共 ${t} 条，显示 ${range[0]}-${range[1]} 条`}
            />
          </div>
          <div style={{ marginTop: 10, marginBottom: 6, color: '#8c8c8c', fontWeight: 600 }}>已作废容器（仅展示系统编码）</div>
          <div style={{ maxHeight: 220, overflow:'auto', border:'1px solid #f0f0f0', borderRadius: 6, background: '#fafafa', padding: 10 }}>
            {boxCancelledPaged.length > 0 ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 8,
                }}
              >
                {boxCancelledPaged.map((r: any, i: number) => (
                  <Tag
                    key={`${String(r?.container_code || '')}|bpc-code|${i}`}
                    style={{
                      marginInlineEnd: 0,
                      width: '100%',
                      borderRadius: 6,
                      background: '#d9d9d9',
                      borderColor: '#bfbfbf',
                      color: '#595959',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      padding: '2px 6px',
                    }}
                  >
                    {String(r?.container_code || '-')}
                  </Tag>
                ))}
              </div>
            ) : (
              <div style={{ color:'#999' }}>暂无已作废容器</div>
            )}
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={boxCancelledPage}
              pageSize={boxCancelledPageSize}
              total={sortedCancelledBoxRows.length}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['10','20','50','100'] as any}
              onChange={(p, ps) => { setBoxCancelledPage(p); setBoxCancelledPageSize(ps); }}
              showTotal={(t, range) => `共 ${t} 条，显示 ${range[0]}-${range[1]} 条`}
            />
          </div>

          {/* 调整池暂存：有数据才显示 */}
          {hasPool ? (
            <div style={{ marginTop: 10, border:'1px solid #f0f0f0', borderRadius: 6, padding: 10, background:'#fafafa' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
                <div style={{ fontWeight: 700 }}>调整池暂存</div>
                <div style={{ color:'#595959' }}>
                  池内可用 <b style={{ color:'#000' }}>{poolTotalPos}</b> 件
                  <span style={{ display:'inline-block', height: 14, borderLeft:'2px solid #d9d9d9', margin:'0 10px' }} />
                  新标签种类 <b style={{ color:'#000' }}>{poolNewKinds}</b>
                </div>
              </div>
              <div style={{ maxHeight: 220, overflow:'auto', border:'1px solid #f0f0f0', borderRadius: 6, background:'#fff' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 70 }}>序号</th>
                      <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>新标签</th>
                      <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 120 }}>数量</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poolPaged.map((r:any, i:number) => (
                      <tr key={`${String(r?.new_code || '')}|pp|${i}`}>
                        <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{(poolPage - 1) * poolPageSize + i + 1}</td>
                        <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontFamily:'monospace' }}>{String(r?.new_code || '')}</td>
                        <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontWeight: 700 }}>{Math.floor(Number(r?.qty || 0) || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
                <Pagination
                  size="small"
                  current={poolPage}
                  pageSize={poolPageSize}
                  total={poolRowsByNew.length}
                  showSizeChanger
                  showQuickJumper
                  pageSizeOptions={['10','20','50','100'] as any}
                  onChange={(p, ps) => { setPoolPage(p); setPoolPageSize(ps); }}
                  showTotal={(t, range) => `共 ${t} 条，显示 ${range[0]}-${range[1]} 条`}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // 换标+装箱：操作记录快照（只读，前端分页/搜索）
  const RelabelPackRecordsSnapshotBlock: React.FC<{
    records: any[];
    // 可选：用于将 op_iu_id 转成库存单元系统编码（从“按条码映射统计”的 matchTargets 解析）
    mappingRows?: any[];
    emptyHint?: string;
  }> = ({ records, mappingRows, emptyHint }) => {
    const [q, setQ] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);

    const safe = useMemo(() => (Array.isArray(records) ? records : []), [records]);

    const iuIdToSys = useMemo(() => {
      const m = new Map<number, string>();
      try {
        const rows = Array.isArray(mappingRows) ? mappingRows : [];
        rows.forEach((r: any) => {
          const txt = String(r?.matchTargets ?? '').trim();
          if (!txt || txt === '-' || txt === '[]') return;
          let arr: any[] = [];
          try { arr = JSON.parse(txt); } catch { arr = []; }
          const list = Array.isArray(arr) ? arr : [];
          list.forEach((x: any) => {
            const id = Math.floor(Number(x?.iu_id || 0));
            const sys = String(x?.system_barcode || '').trim();
            if (Number.isFinite(id) && id > 0 && sys) m.set(id, sys);
          });
        });
      } catch {}
      return m;
    }, [mappingRows]);

    const resolveOpIuSys = (r: any) => {
      const direct = String(r?.op_iu_code || '').trim();
      if (direct) return direct;
      const id = Math.floor(Number(r?.op_iu_id || 0));
      if (Number.isFinite(id) && id > 0) {
        const s = iuIdToSys.get(id);
        if (s) return s;
      }
      return '';
    };
    const filtered = useMemo(() => {
      const key = String(q || '').trim().toUpperCase();
      if (!key) return safe;
      return safe.filter((r: any) => {
        const opSys = resolveOpIuSys(r);
        const hay = [
          r?.event,
          r?.container_code,
          r?.old_code,
          r?.new_code,
          opSys,
        ].map((x) => String(x || '').toUpperCase()).join(' | ');
        return hay.includes(key);
      });
    }, [safe, q, iuIdToSys]);

    const paged = useMemo(() => {
      const p = Math.max(1, page);
      const ps = Math.max(1, pageSize);
      const start = (p - 1) * ps;
      return filtered.slice(start, start + ps);
    }, [filtered, page, pageSize]);

    useEffect(() => { setPage(1); }, [q, pageSize, records?.length]);

    const fmtTs = (ts: any) => {
      try {
        const n = Number(ts);
        if (!Number.isFinite(n) || n <= 0) return '-';
        return new Date(n).toLocaleString('zh-CN');
      } catch {
        return '-';
      }
    };

    return (
      <Card
        size="small"
        title="操作记录（快照）"
        style={{ marginTop: 12 }}
        headStyle={{ background: '#fff1f0' }}
        extra={<span style={{ color: '#8c8c8c' }}>记录数：{filtered.length}</span>}
      >
        {filtered.length === 0 ? (
          <div style={{ color: '#999' }}>{emptyHint || '暂无操作记录快照'}</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <Input
                size="small"
                allowClear
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="搜索：事件/容器编码/旧条码/新条码/操作对象（模糊，不区分大小写）"
                style={{ width: 560 }}
              />
              <span style={{ color: '#8c8c8c' }}>当前显示 {paged.length} 条</span>
            </div>
            <Table
              size="small"
              rowKey={(r: any, idx?: number) => String(r?.id || `${r?.ts || ''}_${idx || 0}`)}
              dataSource={paged}
              pagination={{
                current: page,
                pageSize,
                total: filtered.length,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '20', '50', '100'] as any,
                showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
                onChange: (p: number, ps: number) => { setPage(p); setPageSize(ps); },
              }}
              columns={[
                { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => (page - 1) * pageSize + idx + 1 },
                { title: '时间', dataIndex: 'ts', width: 170, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{fmtTs(v)}</span> },
                { title: '事件', dataIndex: 'event', width: 120, render: (v: any) => <Tag color={String(v || '') === '扫码失败' ? 'red' : 'blue'}>{String(v || '-')}</Tag> },
                { title: '容器', dataIndex: 'container_code', width: 200, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                { title: '旧条码', dataIndex: 'old_code', width: 180, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                { title: '新条码', dataIndex: 'new_code', width: 180, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                { title: '数量', dataIndex: 'qty', width: 90, align: 'center' as const, render: (v: any) => <span style={{ fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
                { title: '操作对象', key: 'op_iu', width: 180, render: (_: any, r: any) => {
                  const s = resolveOpIuSys(r);
                  return s ? <span style={{ fontFamily: 'monospace' }}>{s}</span> : <span style={{ color: '#999' }}>-</span>;
                }},
              ] as any}
              scroll={{ x: 1200 }}
              bordered
            />
          </>
        )}
      </Card>
    );
  };
  // 标题渲染（图标+芯片+文案）
  const renderResultTitle = (titleText: string, flags: { hasOver: boolean; hasUnder: boolean; allZero: boolean }) => {
    const { hasOver, hasUnder, allZero } = flags;
    const chipText = hasOver ? '存在超额拣货' : (hasUnder ? '存在拣货不足' : '拣货目标正好完成');
    const chipStyle: React.CSSProperties = hasOver
      ? { marginLeft: 12, fontWeight: 600, color:'#a8071a', background:'#fff1f0', border:'1px solid #ffa39e', padding:'2px 8px', borderRadius: 4 }
      : hasUnder
        ? { marginLeft: 12, fontWeight: 600, color:'#0958d9', background:'#e6f4ff', border:'1px solid #91caff', padding:'2px 8px', borderRadius: 4 }
        : { marginLeft: 12, fontWeight: 600, color:'#135200', background:'#f6ffed', border:'1px solid #b7eb8f', padding:'2px 8px', borderRadius: 4 };
    return (
      <div style={{ display:'flex', alignItems:'center' }}>
        {hasOver ? (
          <CloseCircleFilled style={{ color:'#ff4d4f', marginRight: 8, fontSize: 18 }} />
        ) : hasUnder ? (
          <InfoCircleFilled style={{ color:'#1677ff', marginRight: 8, fontSize: 18 }} />
        ) : (
          <CheckCircleFilled style={{ color:'#52c41a', marginRight: 8, fontSize: 18 }} />
        )}
        <span style={{ fontWeight: 600 }}>{titleText}</span>
        <span style={chipStyle}>{chipText}</span>
      </div>
    );
  };
  // 拣货快照：两块列表分页（仅分页，不改其它内容/样式）
  const PickSnapshotPagedContent: React.FC<{
    targetSummary: any;
    containerSummary: any;
    headerRecordCount: React.ReactNode;
    detailPickLogs: any[];
  }> = ({ targetSummary, containerSummary, headerRecordCount, detailPickLogs }) => {
    const [targetPage, setTargetPage] = useState<number>(1);
    const [targetPageSize, setTargetPageSize] = useState<number>(50);
    const [contPage, setContPage] = useState<number>(1);
    const [contPageSize, setContPageSize] = useState<number>(10);

    const targetRowsAll: any[] = Array.isArray(targetSummary?.rows) ? targetSummary.rows : [];
    const targetPaged = useMemo(() => {
      const p = Math.max(1, targetPage);
      const ps = Math.max(1, targetPageSize);
      const start = (p - 1) * ps;
      return targetRowsAll.slice(start, start + ps);
    }, [targetRowsAll, targetPage, targetPageSize]);

    const contListAll = useMemo(() => {
      try {
        const keys = Object.keys(containerSummary?.byCont || {});
        return keys.sort((a, b) => {
          const aOff = containerSummary?.offShelves?.has?.(a) ? 1 : 0;
          const bOff = containerSummary?.offShelves?.has?.(b) ? 1 : 0;
          return bOff - aOff || String(a).localeCompare(String(b));
        });
      } catch {
        return [];
      }
    }, [containerSummary]);
    const contListPaged = useMemo(() => {
      const p = Math.max(1, contPage);
      const ps = Math.max(1, contPageSize);
      const start = (p - 1) * ps;
      return contListAll.slice(start, start + ps);
    }, [contListAll, contPage, contPageSize]);

    return (
      <div style={{ lineHeight: 1.6, display:'flex', flexDirection:'column', gap:12 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700 }}>拣货结果汇总（目标物）</div>
            <div style={{ color:'#595959' }}>
              目标数 {targetSummary.totals.total} ｜ 全部拣出 {targetSummary.totals.doneCount} ｜ 目标合计 {targetSummary.totals.need} ｜ 实际拣出 {targetSummary.totals.got} ｜ 不足合计 {targetSummary.totals.diff}
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left' }}>目标物</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left' }}>分拣标识</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>目标拣货数量</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>实际拣货数量</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>拣货差额</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>已完成</th>
              </tr>
            </thead>
            <tbody>
              {targetRowsAll.length === 0 ? (
                <tr><td colSpan={6} style={{ border:'1px solid #d9d9d9', padding:6, color:'#999' }}>无目标数据</td></tr>
              ) : targetPaged.map((r:any, idx:number) => {
                const isOver = r.diff < 0;
                const diffStyle = r.diff > 0 ? { color:'#cf1322', fontWeight:700 } : undefined;
                const rowBg = r.done ? { background:'#f6ffed' } : undefined;
                const rowText = isOver ? { color:'#cf1322', fontWeight:700 } : undefined;
                return (
                  <tr key={`${r.target}|${(targetPage - 1) * targetPageSize + idx}`} style={{ ...(rowBg||{}), ...(rowText||{}) }}>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace' }}>
                      <span>{r.target}</span>
                      {isOver ? <Tag color="processing" style={{ marginLeft: 8 }}>超额</Tag> : null}
                    </td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{r.mark || '-'}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>{r.need}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>{r.got}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', ...(diffStyle||{}) }}>{r.diff}</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>
                      <Tag color={r.done ? 'green' : 'default'}>{r.done ? '已完成' : '未完成'}</Tag>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontWeight:700 }}>Sum</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', fontWeight:700 }}>{targetSummary.totals.need}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', fontWeight:700 }}>{targetSummary.totals.got}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', fontWeight:700 }}>{targetSummary.totals.diff}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }} />
              </tr>
            </tfoot>
          </table>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={targetPage}
              pageSize={targetPageSize}
              total={targetRowsAll.length}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['20','50','100','200'] as any}
              onChange={(p, ps) => { setTargetPage(p); setTargetPageSize(ps); }}
            />
          </div>
        </div>

        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700 }}>涉及拣货记录汇总</div>
            <div style={{ color:'#595959' }}>
              记录 {headerRecordCount} ｜ 容器 {containerSummary.stats.containers} ｜ 数量 {containerSummary.stats.total_qty} ｜ <span style={{ color:'#a8071a', fontWeight: 700 }}>已下架容器 {containerSummary.offShelves.size}</span>
            </div>
          </div>
          {Object.keys(containerSummary.byCont || {}).length === 0 ? (
            <div style={{ color:'#999' }}>无拣货记录</div>
          ) : (
            <>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 220 }}>扫描容器</th>
                    <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 260 }}>目标物</th>
                    <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 240 }}>目标物标识</th>
                    <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', width: 120 }}>拣货数量</th>
                  </tr>
                </thead>
                <tbody>
                  {contListPaged.map((c, ci) => {
                    const rows = (() => {
                      const logs: any[] = Array.isArray(detailPickLogs) ? detailPickLogs : [];
                      const filtered = logs.filter((r:any) => String(r?.container_code || '').trim() === c);
                      if (filtered.length > 0) return filtered.map((r:any) => ({ target_code: String(r?.target_code||''), sorting_mark: String(r?.sorting_mark||'-'), qty: Number(r?.qty||0) }));
                      const units = containerSummary.byCont[c]?.units || {};
                      return Object.entries(units).map(([u, q]: any) => ({ target_code: u, sorting_mark: '-', qty: Number(q || 0) }));
                    })();
                    const groupBg = ((contPage - 1) * contPageSize + ci) % 2 === 0 ? { background:'#fffbe6' } : { background:'#e6f7ff' };
                    return rows.length === 0 ? null : (
                      <React.Fragment key={c}>
                        {rows.map((r:any, idx:number) => (
                          <tr key={`${c}|row|${idx}`} style={groupBg}>
                            {idx === 0 ? (
                              <td rowSpan={rows.length} style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace', verticalAlign:'top' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <span>{c}</span>
                                  {containerSummary.offShelves.has(c) ? (
                                    <Tag style={{ background:'#fff1f0', borderColor:'#ffa39e', color:'#a8071a' }}>已下架</Tag>
                                  ) : null}
                                </div>
                              </td>
                            ) : null}
                            <td style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace' }}>{String(r?.target_code || '')}</td>
                            <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{String(r?.sorting_mark || '-') || '-'}</td>
                            <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right' }}>{Number(r?.qty || 0)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', fontWeight:700 }}>Sum</td>
                    <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'right', fontWeight:700 }}>{containerSummary.stats.total_qty}</td>
                  </tr>
                </tfoot>
              </table>
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
                <Pagination
                  size="small"
                  current={contPage}
                  pageSize={contPageSize}
                  total={contListAll.length}
                  showSizeChanger
                  showQuickJumper
                  pageSizeOptions={['5','10','20','50'] as any}
                  onChange={(p, ps) => { setContPage(p); setContPageSize(ps); }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };
  const IocPickSnapshotContent: React.FC<{
    rows: Array<{
      code: string;
      location: string;
      instructionCode: string;
      containerType: string;
      contentType: string;
      contentSummary: string;
      contentDetail: string;
      picked: boolean;
    }>;
    stats: { total: number; picked: number; todo: number };
    headerRecordCount: React.ReactNode;
    detailPickLogs: any[];
  }> = ({ rows, stats, headerRecordCount, detailPickLogs }) => {
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [logsPage, setLogsPage] = useState<number>(1);
    const [logsPageSize, setLogsPageSize] = useState<number>(10);
    const paged = useMemo(() => {
      const start = (page - 1) * pageSize;
      return rows.slice(start, start + pageSize);
    }, [rows, page, pageSize]);
    const logsAll = Array.isArray(detailPickLogs) ? detailPickLogs : [];
    const logsPaged = useMemo(() => {
      const start = (logsPage - 1) * logsPageSize;
      return logsAll.slice(start, start + logsPageSize);
    }, [logsAll, logsPage, logsPageSize]);
    const parseContentLines = (raw: any): string[] => {
      const txt = String(raw || '').trim();
      if (!txt || txt === '-') return [];
      const byLine = txt.split(/\r?\n/).map((s) => String(s || '').trim()).filter(Boolean);
      if (byLine.length > 1) return byLine;
      const bySemi = txt.split(/[；;]+/).map((s) => String(s || '').trim()).filter(Boolean);
      if (bySemi.length > 1) return bySemi;
      const byPipe = txt.split(/\s*\|\s*/).map((s) => String(s || '').trim()).filter(Boolean);
      if (byPipe.length > 1) return byPipe;
      return [txt];
    };
    const openFullContentModal = (containerCode: string, lines: string[]) => {
      Modal.info({
        title: `容器内容完整记录（${containerCode || '-'}）`,
        width: 760,
        content: (
          <div style={{ maxHeight: 420, overflow: 'auto', paddingRight: 8 }}>
            {lines.map((line, idx) => (
              <div key={`${containerCode}-${idx}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                {idx + 1}. {line}
              </div>
            ))}
          </div>
        ),
      });
    };
    const renderContentDetail = (containerCode: string, raw: any) => {
      const lines = parseContentLines(raw);
      if (lines.length === 0) return '-';
      const preview = lines.slice(0, 3);
      const hasMore = lines.length > 3;
      return (
        <div>
          {preview.map((line, idx) => (
            <div key={`${containerCode}-preview-${idx}`} style={{ marginBottom: idx === preview.length - 1 ? 0 : 2, wordBreak: 'break-all' }}>
              {line}
            </div>
          ))}
          {hasMore ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', lineHeight: 1.2, marginTop: 2 }}
              onClick={() => openFullContentModal(containerCode, lines)}
            >
              [...]
            </Button>
          ) : null}
        </div>
      );
    };
    return (
      <div style={{ lineHeight: 1.6, display:'flex', flexDirection:'column', gap:12 }}>
        <div style={{ background:'#f0f9ff', border:'1px solid #91caff', borderRadius: 8, padding: 10 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 700 }}>仓位表与拣货结果</div>
            <div style={{ color:'#595959' }}>
              目标IOC {stats.total} ｜ 已拣出 {stats.picked} ｜ 待拣出 {stats.todo} ｜ 记录 {headerRecordCount}
            </div>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 70 }}>序号</th>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 220 }}>IOC系统编码</th>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 120 }}>仓位</th>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 180 }}>来源历史指令</th>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 120 }}>容器类型</th>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 120 }}>内容类型</th>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 120 }}>容器内容统计</th>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 360 }}>容器内容</th>
              <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 110 }}>拣出状态</th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={9} style={{ border:'1px solid #d9d9d9', padding:6, color:'#999' }}>暂无IOC数据</td></tr>
            ) : paged.map((r, idx) => (
              <tr key={`${r.code}|${(page - 1) * pageSize + idx}`} style={r.picked ? { background:'#f6ffed' } : undefined}>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{(page - 1) * pageSize + idx + 1}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace' }}>{r.code || '-'}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{r.location || '-'}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace' }}>{r.instructionCode || '-'}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{r.containerType || '-'}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{r.contentType || '-'}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6 }}>{r.contentSummary || '-'}</td>
                <td style={{ border:'1px solid #d9d9d9', padding:6 }}>
                  {renderContentDetail(String(r.code || '-'), r.contentDetail)}
                </td>
                <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>
                  <Tag color={r.picked ? 'green' : 'default'}>{r.picked ? '已拣出' : '待拣出'}</Tag>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={page}
              pageSize={pageSize}
              total={rows.length}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['10','20','50','100'] as any}
              onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
            />
          </div>
        </div>
        <div style={{ background:'#fff7e6', border:'1px solid #ffd591', borderRadius: 8, padding: 10 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 700 }}>拣货操作记录</div>
            <div style={{ color:'#595959' }}>记录总数 {logsAll.length}</div>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 70 }}>序号</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left', width: 180 }}>时间</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'left' }}>IOC系统编码</th>
                <th style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center', width: 120 }}>动作</th>
              </tr>
            </thead>
            <tbody>
              {logsPaged.length === 0 ? (
                <tr><td colSpan={4} style={{ border:'1px solid #d9d9d9', padding:6, color:'#999' }}>暂无拣货操作记录</td></tr>
              ) : logsPaged.map((r:any, idx:number) => (
                <tr key={`${String(r?.id || '')}|${(logsPage - 1) * logsPageSize + idx}`}>
                  <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>{(logsPage - 1) * logsPageSize + idx + 1}</td>
                  <td style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace' }}>{String(r?.operated_at || '-')}</td>
                  <td style={{ border:'1px solid #d9d9d9', padding:6, fontFamily:'monospace' }}>{String(r?.container_code || '-')}</td>
                  <td style={{ border:'1px solid #d9d9d9', padding:6, textAlign:'center' }}>扫码拣出</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={logsPage}
              pageSize={logsPageSize}
              total={logsAll.length}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['5','10','20','50'] as any}
              onChange={(p, ps) => { setLogsPage(p); setLogsPageSize(ps); }}
            />
          </div>
        </div>
      </div>
    );
  };

  // 复用的内容渲染：两块表格（内部使用分页组件）
  const renderPickResultContent = (targetSummary: any, containerSummary: any) => {
    const headerCount = (Array.isArray((window as any)?.__PICK_LOGS__) ? (window as any).__PICK_LOGS__.length : '-');
    const detailLogs: any[] = Array.isArray(snapshotData?.pick_logs)
      ? snapshotData.pick_logs
      : (Array.isArray((window as any)?.__PICK_LOGS__) ? (window as any).__PICK_LOGS__ : []);
    return (
      <PickSnapshotPagedContent
        targetSummary={targetSummary}
        containerSummary={containerSummary}
        headerRecordCount={headerCount}
        detailPickLogs={detailLogs}
      />
    );
  };

  // 拉取某指令的全部工单（跨分页聚合）
  const fetchAllWorkOrders = async (instructionId: number): Promise<any[]> => {
    const pageSize = 200;
    let page = 1;
    const all: any[] = [];
    // 跨页聚合：不依赖 count，直到返回条数小于 pageSize
    while (true) {
      const resp = await request.get('/api/v1/workorders/workorders/', { params: { instruction_id: instructionId, page, page_size: pageSize } });
      const data = resp?.data;
      // 两种返回格式兼容：{results,count} 或 直接数组
      if (Array.isArray(data)) {
        return data; // 已是全量
      }
      const results = Array.isArray(data?.results) ? data.results : [];
      all.push(...results);
      if (results.length < pageSize) break;
      page += 1;
    }
    return all;
  };

  const load = async () => {
    if (!Number.isFinite(instrId)) return;
    setLoading(true);
    try {
      const [iRes, allWOs, etRes] = await Promise.all([
        request.get(`/api/v1/instruction/instructions/${instrId}/`),
        fetchAllWorkOrders(instrId),
        request.get('/api/v1/instruction/exec/tasks/', { params: { instruction: instrId } }),
      ]);
      setInstr(iRes?.data || null);
      setWorkOrders(Array.isArray(allWOs) ? allWOs : []);
      const tasks = Array.isArray(etRes?.data?.results) ? etRes.data.results : (Array.isArray(etRes?.data) ? etRes.data : []);
      const firstTask = tasks && tasks.length > 0 ? tasks[0] : null;
      setTaskId(firstTask?.id ?? null);
      setTaskExecutionSessionId(String(firstTask?.meta?.execution_session_id || '').trim());
      const steps = Array.isArray(firstTask?.steps) ? firstTask.steps : [];
      setExecSteps(steps);
      // 重新定位当前阶段（优先第一个未完成的阶段）
      try {
        const stages = computeStages(steps);
        const firstUnfinished = stages.findIndex(g => !isStageCompleted(g.steps));
        setCurrentStageIdx(firstUnfinished >= 0 ? firstUnfinished : 0);
      } catch {}
    } catch {
      message.error('加载指令/工单失败');
      setInstr(null);
      setWorkOrders([]);
      setTaskExecutionSessionId('');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [instrId]);

  const claim = async () => {
    try {
      // 领取前尝试“计费计划预览”（软校验）：ok=false 且 code=baseline_not_ready 时不阻断，仅提示
      try {
        const pv = await request.post(`/api/v1/instruction/instructions/${instrId}/plan/preview/`, {});
        const ok = !!pv?.data?.ok;
        const code = pv?.data?.code;
        if (!ok && code === 'baseline_not_ready') {
          message.warning('基准数据未就绪，计费预览不可用，不影响执行');
        }
      } catch {
        // 预览异常不阻断领取
      }
      await request.post(`/api/v1/instruction/instructions/${instrId}/claim/`, {});
      message.success('已领取整单');
      load();
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '领取失败');
    }
  };
  const startStage = async () => {
    try {
      await request.post(`/api/v1/instruction/instructions/${instrId}/start-current-stage/`, {});
    } catch (e:any) {
      // 忽略重复开始等错误
    }
  };

  // 工具：整理阶段与完成判定
  const computeStages = (steps: any[]) => {
    const map: Map<number, any[]> = new Map();
    (steps || []).forEach((st:any) => {
      const key = Number.isFinite(Number(st?.stage)) ? Number(st.stage) : 0;
      const arr = map.get(key) || [];
      arr.push(st);
      map.set(key, arr);
    });
    const entries = Array.from(map.entries()).sort((a,b)=> a[0]-b[0]).map(([stage, arr]) => {
      arr.sort((a:any,b:any)=> (a.sort_order??0)-(b.sort_order??0));
      const allOptional = arr.length > 0 && arr.every((x:any)=> x?.required === false);
      return { stage, steps: arr, optional: allOptional };
    });
    return entries;
  };
  const isStepDone = (st:any) => String(st?.status||'') === 'done';
  const isStageCompleted = (arr:any[]) => (arr || []).every((x:any)=> isStepDone(x) || x?.meta?.skipped === true);
  const stages = useMemo(() => {
    if (Array.isArray(execSteps) && execSteps.length > 0) {
      return computeStages(execSteps);
    }
    return [];
  }, [execSteps]);
  const getWrapUpPutAwayGate = useCallback((stage: any) => {
    const steps = Array.isArray(stage?.steps) ? stage.steps : [];
    const wrapStep = steps.find((s: any) => {
      const t = String(s?.step_type || s?.renderer_key || '').trim().toUpperCase();
      return t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP';
    });
    if (!wrapStep) {
      return { hasWrapUp: false, blocked: false, total: 0, done: 0, todo: 0 };
    }
    const sid = String(Number(wrapStep?.id || 0) || '');
    const md = (sid && metricsDraft?.[sid]) ? (metricsDraft[sid] || {}) : (wrapStep?.collected_metrics || {});
    const gate = (md && typeof md === 'object' && md.put_away_gate && typeof md.put_away_gate === 'object')
      ? md.put_away_gate
      : {};
    const total = Number(gate?.total || 0);
    const done = Number(gate?.done || 0);
    const todo = Number(gate?.todo || 0);
    const blocked = total > 0 && (todo > 0 || done < total);
    return { hasWrapUp: true, blocked, total, done, todo };
  }, [metricsDraft]);

  // “使用说明”仅适用于“换标-装箱/装袋”阶段（组合视图）
  const showStageHelp = useMemo(() => {
    try {
      const cur = stages[currentStageIdx];
      const steps = Array.isArray(cur?.steps) ? cur.steps : [];
      if (!steps.length) return false;
      const types = steps.map((s:any) => String(s?.step_type || '').trim().toUpperCase());
      const titles = steps.map((s:any) => String(s?.title || '').trim());
      const hasRelabel = types.includes('RELABEL') || titles.some((t:string) => t.includes('换标'));
      const hasPack = types.some((t:string) => (
        t === 'BOX/BAG_PACK' || t === 'BOX_PACK' || t === 'BAG_PACK' || t === 'BOX' || t === 'BAG'
      )) || titles.some((t:string) => t.includes('装箱') || t.includes('装袋'));
      return hasRelabel && hasPack;
    } catch {
      return false;
    }
  }, [stages, currentStageIdx]);

  useEffect(() => {
    if (stageHelpOpen && !showStageHelp) {
      setStageHelpOpen(false);
    }
  }, [stageHelpOpen, showStageHelp]);

  // 加载当前阶段的 UI schema（后端提供 stage-ui 接口）
  useEffect(() => {
    (async () => {
      try {
        if (!taskId) return;
        const cur = stages[currentStageIdx];
        const stageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : null;
        const params: any = {};
        if (stageNo != null) params.stage = stageNo;
        const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/stage-ui/`, { params });
        const payload = resp?.data || {};
        const list = Array.isArray(payload?.steps) ? payload.steps : [];
        const map: Record<string, any> = {};
        const dataMap: Record<string, any> = {};
        list.forEach((s: any) => {
          const sid = String(s?.step_id ?? s?.id ?? '');
          if (!sid) return;
          map[sid] = s?.ui_schema || s?.ui_schema_effective || {};
          dataMap[sid] = s?.data || {};
        });
        setStageUISchemas(map);
        setStageUIData(dataMap);
        // 初始化草稿值：以 collected_metrics 为初始值
        const draft: Record<string, any> = {};
        (cur?.steps || []).forEach((st: any) => {
          const sid = String(st?.id ?? '');
          if (!sid) return;
          draft[sid] = { ...(st?.collected_metrics || {}) };
        });
        setMetricsDraft(draft);
        // 兜底：若 stage-ui 返回的 ui_schema 为空，则尝试根据 step_template 拉取模板侧的有效 UI
        // 这样老指令也能看到模板或库默认的 UI，而无需重建
        const needFetchTpl: Array<{ sid: string; tplId: number }> = [];
        (cur?.steps || []).forEach((st: any) => {
          const sid = String(st?.id ?? '');
          if (!sid) return;
          const schema = map[sid] || {};
          const comps = Array.isArray(schema?.components) ? schema.components : [];
          const tplId = Number(st?.step_template);
          if (comps.length === 0 && Number.isFinite(tplId)) {
            needFetchTpl.push({ sid, tplId });
          }
        });
        if (needFetchTpl.length > 0) {
          await Promise.all(needFetchTpl.map(async ({ sid, tplId }) => {
            try {
              const t = await request.get(`/api/v1/instruction/exec/step-templates/${tplId}/`);
              const data = t?.data || {};
              const ui = (data?.ui_schema && Object.keys(data.ui_schema || {}).length > 0)
                ? data.ui_schema
                : (data?.ui_schema_effective || {});
              if (ui && Object.keys(ui || {}).length > 0) {
                setStageUISchemas(prev => ({ ...prev, [sid]: ui }));
              }
            } catch { /* ignore */ }
          }));
        }
      } catch {
        // 忽略加载 UI 失败，仍然可以仅按阶段按钮运作
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, currentStageIdx, stages.length]);

  // 旧视图（无执行步骤）分组回退
  // ========= 收尾：四板块快照（预览 & 审计查看共用） =========
  const WrapUpSnapshotBlock: React.FC<{ snapshot: any }> = ({ snapshot }) => {
    const snap = snapshot || {};
    const sections = (snap?.sections || snap?.snapshot?.sections || {}) as any;
    const s1 = sections?.step1_unoperable || {};
    const s2 = sections?.step2_overpick || {};
    const s3 = sections?.step3_put_away || {};
    const s4 = sections?.step4_remarks_attachments || {};
    const exc = Array.isArray(s1?.exception_containers) ? s1.exception_containers : [];
    const overGroupsRaw = Array.isArray(s2?.over_pick_groups) ? s2.over_pick_groups : [];
    const putAway = Array.isArray(s3?.put_away_ops) ? s3.put_away_ops : [];
    const iocNormalCodesRaw = Array.isArray(s3?.ioc_normal_codes) ? s3.ioc_normal_codes : [];
    const operationGeneratedCodesRaw = Array.isArray(s3?.operation_generated_codes) ? s3.operation_generated_codes : iocNormalCodesRaw;
    const operationGeneratedKindRaw = String(s3?.operation_generated_kind || '').trim().toLowerCase();
    const iocCancelledCodesRaw = Array.isArray(s3?.ioc_cancelled_codes) ? s3.ioc_cancelled_codes : [];
    const remarks = String(s4?.remarks || '').trim();
    const attachments = Array.isArray(s4?.attachments) ? s4.attachments : [];

    const cardStyle: React.CSSProperties = { background:'#ffffff', border:'1px solid #f0f0f0', borderRadius: 8, padding: 10, marginBottom: 10 };
    const empty = (txt: string) => <div style={{ color:'#bfbfbf' }}>{txt}</div>;

    const titleBar = (args: { title: string; bg: string; border: string; right?: React.ReactNode }) => (
      <div
        style={{
          background: args.bg,
          border: `1px solid ${args.border}`,
          borderRadius: 8,
          padding: '8px 10px',
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span style={{ fontWeight: 800 }}>{args.title}</span>
        <span style={{ color:'#595959', fontWeight: 600, whiteSpace:'nowrap' }}>{args.right}</span>
      </div>
    );

    const excTotalQty = (exc || []).reduce((s:number, r:any) => s + (Number(r?.total_qty || 0) || 0), 0);
    const overGroups = (overGroupsRaw || []).filter((g:any) => String(g?.created_container_code || '').trim());
    const overTotalQty = (overGroups || []).reduce((s:number, g:any) => {
      const items = Array.isArray(g?.items) ? g.items : [];
      return s + items.reduce((ss:number, it:any) => ss + (Number(it?.qty || 0) || 0), 0);
    }, 0);

    // ====== step3: 所有容器放入仓位（单列表 + 类型区分 + 行底色 + 分页）======
    const normCode = (v:any) => String(v ?? '').trim().toUpperCase();
    const locMap = useMemo(() => {
      const m: Record<string, string> = {};
      (putAway || []).forEach((r:any) => {
        const c = normCode(r?.container_code);
        const loc = String(r?.location_code ?? '').trim().toUpperCase();
        if (c) m[c] = loc;
      });
      return m;
    }, [putAway]);

    const operationGeneratedCodes = useMemo(() => {
      const arr = (operationGeneratedCodesRaw || []).map(normCode).filter(Boolean) as string[];
      const out = Array.from(new Set(arr)) as string[];
      out.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
      return out;
    }, [operationGeneratedCodesRaw]);
    const operationGeneratedKind = operationGeneratedKindRaw === 'storage' ? 'storage' : 'output';

    const iocCancelledCodeSet = useMemo(() => {
      const arr = (iocCancelledCodesRaw || []).map(normCode).filter(Boolean) as string[];
      return new Set(arr);
    }, [iocCancelledCodesRaw]);

    const excCodes = useMemo(() => {
      const arr = (exc || []).map((r:any) => normCode(r?.code)).filter(Boolean) as string[];
      const out = Array.from(new Set(arr)).filter((c) => !iocCancelledCodeSet.has(String(c))) as string[];
      out.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
      return out;
    }, [exc, iocCancelledCodeSet]);

    const overpickContainerCodes = useMemo(() => {
      const arr = (overGroups || []).map((g:any) => normCode(g?.created_container_code)).filter(Boolean) as string[];
      const out = Array.from(new Set(arr)) as string[];
      out.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
      return out;
    }, [overGroups]);

    const allCodes = useMemo(() => {
      const set = new Set<string>();
      (operationGeneratedCodes as string[]).forEach((c: string) => {
        const cc = String(c);
        if (iocCancelledCodeSet.has(cc)) return;
        set.add(cc);
      });
      (excCodes as string[]).forEach((c: string) => set.add(String(c)));
      (overpickContainerCodes as string[]).forEach((c: string) => set.add(String(c)));
      return Array.from(set) as string[];
    }, [operationGeneratedCodes, excCodes, overpickContainerCodes, iocCancelledCodeSet]);

    const putAwayDoneCount = useMemo(() => {
      return allCodes.reduce((s:number, c:string) => s + (locMap[c] ? 1 : 0), 0);
    }, [allCodes, locMap]);
    const putAwayNotDoneCount = Math.max(allCodes.length - putAwayDoneCount, 0);

    const mkRows = (codes: string[], kindLabel: string) => {
      return codes.map((c) => ({
        code: c,
        kind: kindLabel,
        location_code: locMap[c] || '',
      }));
    };
    const KIND_OPERATION_GENERATED = operationGeneratedKind === 'storage'
      ? '操作过程生成的容器SC'
      : '操作过程生成的容器IOC';
    const KIND_EXCEPTION_IOC = '无法操作产品生成的容器';
    const KIND_OVERPICK_SC = '多拣货产品生成的容器';
    const rowsOperationGenerated = useMemo(
      () => mkRows(operationGeneratedCodes, KIND_OPERATION_GENERATED),
      [operationGeneratedCodes, locMap, KIND_OPERATION_GENERATED],
    );
    const rowsIocException = useMemo(() => mkRows(excCodes, KIND_EXCEPTION_IOC), [excCodes, locMap]);
    const rowsOverpick = useMemo(() => mkRows(overpickContainerCodes, KIND_OVERPICK_SC), [overpickContainerCodes, locMap]);

    const allPutAwayRows = useMemo(() => {
      // 单列表展示；同一 code 若同时出现，按优先级去重：
      // 无法操作产品容器 > 多拣货容器 > 操作过程生成容器
      const arr = [...rowsIocException, ...rowsOverpick, ...rowsOperationGenerated];
      const dedupMap = new Map<string, any>();
      arr.forEach((r: any) => {
        const code = String(r?.code || '').trim().toUpperCase();
        if (!code) return;
        if (!dedupMap.has(code)) dedupMap.set(code, r);
      });
      const rows = Array.from(dedupMap.values());
      rows.sort((a:any, b:any) => String(a?.code || '').localeCompare(String(b?.code || ''), undefined, { numeric: true, sensitivity:'base' }));
      return rows;
    }, [rowsOperationGenerated, rowsIocException, rowsOverpick]);

    const [pAll, setPAll] = useState<number>(1);
    const [psAll, setPsAll] = useState<number>(20);
    useEffect(() => {
      const max = Math.max(1, Math.ceil(allPutAwayRows.length / psAll));
      if (pAll > max) setPAll(1);
    }, [allPutAwayRows.length, psAll]); // eslint-disable-line react-hooks/exhaustive-deps

    // 附件分页（预览 & 查看共用；避免附件很多导致页面过长）
    const [attPage, setAttPage] = useState<number>(1);
    const [attPageSize, setAttPageSize] = useState<number>(10);
    useEffect(() => {
      const max = Math.max(1, Math.ceil((attachments || []).length / attPageSize));
      if (attPage > max) setAttPage(1);
    }, [attachments.length, attPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const sliceRows = (rows:any[], page:number, pageSize:number) => {
      const start = (page - 1) * pageSize;
      return rows.slice(start, start + pageSize);
    };
    const colsPutAway = [
      { title: '序号', key: '__idx', width: 70, align: 'center' as any, render: (_:any, __:any, idx:number) => idx + 1 },
      { title: '容器类型', dataIndex: 'kind', key: 'kind', width: 220, align: 'center' as any, render: (v:any) => String(v || '-') },
      { title: '容器', dataIndex: 'code', key: 'code', width: 220, align: 'center' as any, render: (v:any) => <span style={{ fontFamily:'monospace', color:'#595959' }}>{String(v || '-')}</span> },
      { title: '放入仓位', dataIndex: 'location_code', key: 'location_code', width: 220, align: 'center' as any, render: (v:any) => <span style={{ fontFamily:'monospace', color:'#595959' }}>{String(v || '-') || '-'}</span> },
    ] as any;

    return (
      <div>
        <div style={cardStyle}>
          {titleBar({
            title: '无法操作产品生成容器',
            bg: '#fff7e6',
            border: '#ffd591',
            right: <>容器数量 {exc.length}　内容物总数量 {excTotalQty}</>,
          })}
          <Table
            size="small"
            rowKey={(r:any, idx?:number) => String(r?.code || (idx ?? 0))}
            pagination={false}
            dataSource={exc}
            columns={[
              { title: '序号', key: '__idx', width: 70, align: 'center' as any, render: (_:any, __:any, idx:number) => idx + 1 },
              { title: '容器系统编码', dataIndex: 'code', key: 'code', width: 220, align: 'center' as any, render: (v:any) => (
                <span style={{ fontFamily:'monospace', color:'#595959' }}>{String(v || '-')}</span>
              )},
              { title: '内容物总数量', dataIndex: 'total_qty', key: 'total_qty', width: 120, align: 'center' as any, render: (v:any) => Number(v || 0) },
              { title: '匹配对象分配', dataIndex: 'allocations', key: 'allocations', align: 'center' as any, render: (_:any, r:any) => {
                const allocs = Array.isArray(r?.allocations) ? r.allocations : [];
                if (!allocs || allocs.length === 0) return '-';
                const lines = allocs
                  .map((a:any) => `${String(a?.unit_code || '').trim()} × ${Number(a?.quantity || 0) || 0}`)
                  .filter((x:string) => x.trim() && !x.trim().startsWith('×'));
                return (
                  <div style={{ fontFamily:'monospace', color:'#595959', whiteSpace:'pre-wrap', textAlign:'center' }}>
                    {lines.length > 0 ? lines.join('\n') : '-'}
                  </div>
                );
              }},
            ] as any}
            locale={{ emptyText: empty('（无数据）') }}
          />
        </div>

        <div style={cardStyle}>
          {titleBar({
            title: '多拣货产品生成容器',
            bg: '#f9f0ff',
            border: '#d3adf7',
            right: <>容器数量 {overGroups.length}　内容物总数量 {overTotalQty}</>,
          })}
          <Table
            size="small"
            rowKey={(r:any, idx?:number) => String(r?.created_container_code || r?.group_code || (idx ?? 0))}
            pagination={false}
            dataSource={overGroups}
            columns={[
              { title: '序号', key: '__idx', width: 70, align: 'center' as any, render: (_:any, __:any, idx:number) => idx + 1 },
              { title: '生成容器', dataIndex: 'created_container_code', key: 'created_container_code', width: 200, align: 'center' as any, render: (v:any) => <span style={{ fontFamily:'monospace', color:'#595959' }}>{String(v || '-')}</span> },
              { title: '分拣组', dataIndex: 'group_code', key: 'group_code', width: 180, align: 'center' as any, render: (v:any) => <span style={{ fontFamily:'monospace', color:'#595959' }}>{String(v || '-')}</span> },
              { title: '内容物总数量', key: 'picked_qty', width: 140, align: 'center' as any, render: (_:any, r:any) => {
                const items = Array.isArray(r?.items) ? r.items : [];
                return items.reduce((s:number, it:any) => s + (Number(it?.qty || 0) || 0), 0);
              }},
              { title: '库存单元', key: 'ius', align: 'center' as any, render: (_:any, r:any) => {
                const items = Array.isArray(r?.items) ? r.items : [];
                if (!items || items.length === 0) return '-';
                const map: Record<string, number> = {};
                items.forEach((it:any) => {
                  const iu = String(it?.inventory_unit_code || '').trim();
                  const q = Number(it?.qty || 0) || 0;
                  if (!iu) return;
                  map[iu] = (map[iu] || 0) + q;
                });
                const lines = Object.keys(map)
                  .sort((a,b)=> a.localeCompare(b, undefined, { numeric: true, sensitivity:'base' }))
                  .map((k) => `${k} × ${map[k]}`);
                return (
                  <div style={{ fontFamily:'monospace', color:'#595959', whiteSpace:'pre-wrap', textAlign:'center' }}>
                    {lines.length > 0 ? lines.join('\n') : '-'}
                  </div>
                );
              }},
            ] as any}
            locale={{ emptyText: empty('（无数据）') }}
          />
        </div>

        <div style={cardStyle}>
          {titleBar({
            title: '所有容器放入仓位',
            bg: '#e6f4ff',
            border: '#91caff',
            right: <>所有容器数量 {allCodes.length}　已放入仓位 {putAwayDoneCount}　未放入仓位 {putAwayNotDoneCount}</>,
          })}
          <style>{`
            .wrapup-putaway-row-normal td { background: #e6f4ff !important; }
            .wrapup-putaway-row-exception td { background: #fff1f0 !important; }
            .wrapup-putaway-row-overpick td { background: #f9f0ff !important; }
          `}</style>
          <Table
            size="small"
            rowKey={(r:any, idx?:number) => String(r?.code || (idx ?? 0))}
            pagination={false}
            dataSource={sliceRows(allPutAwayRows, pAll, psAll)}
            columns={colsPutAway}
            rowClassName={(r:any) => {
              const k = String(r?.kind || '');
              if (k === KIND_OPERATION_GENERATED) return 'wrapup-putaway-row-normal';
              if (k === KIND_EXCEPTION_IOC) return 'wrapup-putaway-row-exception';
              if (k === KIND_OVERPICK_SC) return 'wrapup-putaway-row-overpick';
              return '';
            }}
            locale={{ emptyText: empty('（无数据）') }}
          />
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={pAll}
              pageSize={psAll}
              total={allPutAwayRows.length}
              showSizeChanger
              showQuickJumper
              onChange={(p, ps)=> { setPAll(p); setPsAll(ps); }}
              showTotal={(total)=> `共 ${total} 条`}
            />
          </div>
        </div>

        <div style={cardStyle}>
          {titleBar({
            title: '备注与附件',
            bg: '#f6ffed',
            border: '#b7eb8f',
          })}
          <div style={{ marginBottom: 8 }}>
            <div style={{ color:'#595959', fontWeight: 600, marginBottom: 4 }}>备注</div>
            {remarks ? <div style={{ whiteSpace:'pre-wrap', color:'#fa541c' }}>{remarks}</div> : empty('（无）')}
          </div>
          <div>
            <div style={{ color:'#595959', fontWeight: 600, marginBottom: 4 }}>附件</div>
            {attachments.length === 0 ? empty('（无）') : (
              <div>
                <Table
                  size="small"
                  rowKey={(r:any, idx?:number) => String(r?.id || r?.file || (idx ?? 0))}
                  pagination={false}
                  dataSource={sliceRows(attachments, attPage, attPageSize)}
                  columns={[
                    { title: '序号', key: '__idx', width: 70, align: 'center' as any, render: (_:any, __:any, idx:number) => ((attPage - 1) * attPageSize) + idx + 1 },
                    { title: '文件名', dataIndex: 'file_name', key: 'file_name', render: (v:any) => String(v || '-') },
                    { title: '类型', dataIndex: 'kind', key: 'kind', width: 120, align: 'center' as any, render: (v:any) => String(v || '-') },
                  ] as any}
                  locale={{ emptyText: empty('（无）') }}
                />
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
                  <Pagination
                    size="small"
                    current={attPage}
                    pageSize={attPageSize}
                    total={attachments.length}
                    showSizeChanger
                    showQuickJumper
                    onChange={(p, ps)=> { setAttPage(p); setAttPageSize(ps); }}
                    showTotal={(total)=> `共 ${total} 条`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========= 分拣清点：快照渲染（提交前预览 & 提交后回看共用） =========
  const SortCountSnapshotBlock: React.FC<{ snapshotEnvelope: any }> = ({ snapshotEnvelope }) => {
    const [presetPage, setPresetPage] = useState<number>(1);
    const [presetPageSize, setPresetPageSize] = useState<number>(10);
    const [sortDbPage, setSortDbPage] = useState<number>(1);
    const [sortDbPageSize, setSortDbPageSize] = useState<number>(10);
    const [operationPage, setOperationPage] = useState<number>(1);
    const [operationPageSize, setOperationPageSize] = useState<number>(10);
    const [byMarkPage, setByMarkPage] = useState<number>(1);
    const [byMarkPageSize, setByMarkPageSize] = useState<number>(10);
    const [containerPage, setContainerPage] = useState<number>(1);
    const [containerPageSize, setContainerPageSize] = useState<number>(10);
    const root = snapshotEnvelope || {};
    const snap = (root && root.snapshot) ? root.snapshot : root;
    const stats = root?.stats || snap?.stats || {};
    const ctx = snap?.instruction_context || {};
    const sections = snap?.sections || {};
    const operationRows = Array.isArray(sections?.operation_records?.rows) ? sections.operation_records.rows : [];
    const sortDatabaseRows = Array.isArray(sections?.sort_database_rows?.rows) ? sections.sort_database_rows.rows : [];
    const byMarkRows = Array.isArray(sections?.stats_by_mark?.rows) ? sections.stats_by_mark.rows : [];
    const generatedDetailRows = Array.isArray(sections?.generated_containers?.detail_rows) ? sections.generated_containers.detail_rows : [];
    const generatedContainerRows = Array.isArray(sections?.generated_containers?.rows) ? sections.generated_containers.rows : [];
    const containerInfoMap = new Map<string, any>();
    generatedContainerRows.forEach((c: any) => {
      const code = String(c?.system_code || '').trim();
      if (code) containerInfoMap.set(code, c);
    });
    const containerGroupKeys = Array.from(new Set(
      (Array.isArray(generatedDetailRows) ? generatedDetailRows : [])
        .map((r: any) => String(r?.system_code || r?.id || '').trim())
        .filter(Boolean),
    ));
    const containerSeqMap = new Map<string, number>();
    containerGroupKeys.forEach((k: string, idx: number) => containerSeqMap.set(k, idx + 1));
    const containerGroupsForSnapshot = containerGroupKeys.map((groupKey: string, groupIdx: number) => {
      const rows = (Array.isArray(generatedDetailRows) ? generatedDetailRows : [])
        .filter((r: any) => String(r?.system_code || r?.id || '').trim() === groupKey);
      const first = rows[0] || {};
      const code = String(first?.system_code || '').trim();
      const info = code ? containerInfoMap.get(code) : null;
      const totalQty = Math.max(
        0,
        Number(first?.total_qty || info?.total_qty || rows.reduce((sum: number, r: any) => sum + Math.max(0, Number(r?.qty || 0)), 0)),
      );
      return {
        key: groupKey,
        seq: groupIdx + 1,
        bg: groupIdx % 2 === 0 ? '#f0f9ff' : '#f6ffed',
        container_type: String(first?.container_type || info?.container_type_display || info?.container_type || ''),
        total_qty: totalQty,
        rows,
      };
    });
    const containerPageStart = (Math.max(1, Number(containerPage || 1)) - 1) * Math.max(1, Number(containerPageSize || 10));
    const pagedContainerGroupsForSnapshot = containerGroupsForSnapshot.slice(
      containerPageStart,
      containerPageStart + Math.max(1, Number(containerPageSize || 10)),
    );
    const containerDisplayRows = pagedContainerGroupsForSnapshot.flatMap((group: any) => {
      const rows = Array.isArray(group?.rows) && group.rows.length > 0 ? group.rows : [{}];
      return rows.map((r: any, idx: number) => ({
        ...(r || {}),
        __containerGroupKey: group.key,
        __containerSeq: group.seq,
        __containerRowSpanForSnapshot: idx === 0 ? rows.length : 0,
        __containerBg: group.bg,
        container_type: group.container_type,
        total_qty: group.total_qty,
      }));
    });
    const targetRows = Array.isArray(sections?.operation_targets?.rows) ? sections.operation_targets.rows : [];
    const presetRows = Array.isArray(sections?.preset_group_list?.rows) ? sections.preset_group_list.rows : [];
    const sortGroupCount = Array.from(new Set(
      (Array.isArray(sortDatabaseRows) ? sortDatabaseRows : [])
        .map((r: any) => String(r?.group_code || r?.sorting_group || r?.sorting_group_code || '').trim())
        .filter(Boolean),
    )).length;
    const buildSnapshotPagination = (
      total: number,
      current: number,
      pageSize: number,
      setPage: (v: number) => void,
      setPageSize: (v: number) => void,
    ) => ({
      size: 'small' as const,
      current,
      pageSize,
      total,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ['5', '10', '20', '50', '100'] as any,
      onChange: (page: number, nextPageSize: number) => {
        setPage(page);
        if (nextPageSize !== pageSize) {
          setPageSize(nextPageSize);
        }
      },
      showTotal: (count: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${count}条记录`,
    });
    const demandModeText = (() => {
      const v = String(ctx?.demand_mode || '').trim();
      if (v === 'need1') return '需求1';
      if (v === 'need2') return '需求2';
      if (v === 'need3') return '需求3';
      return v || '-';
    })();
    const snapshotLocationBadgeStyle: React.CSSProperties = {
      display: 'inline-block',
      minWidth: 44,
      padding: '2px 10px',
      borderRadius: 6,
      background: '#ffeb3b',
      border: '1px solid #fadb14',
      color: '#cf1322',
      fontWeight: 800,
      lineHeight: 1.2,
      textAlign: 'center',
    };
    const snapshotSortMarkBadgeStyle: React.CSSProperties = {
      display: 'inline-block',
      minWidth: 64,
      padding: '2px 10px',
      borderRadius: 6,
      background: '#e6f7ff',
      border: '1px solid #91d5ff',
      color: '#0050b3',
      fontWeight: 700,
      lineHeight: 1.2,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
    };
    const renderSnapshotLocationBadge = (v: any) => {
      const txt = String(v ?? '').trim();
      if (!txt) return '-';
      return <span style={snapshotLocationBadgeStyle}>{txt}</span>;
    };
    const renderSnapshotSortMarkBadge = (v: any) => {
      const txt = String(v ?? '').trim().toUpperCase();
      if (!txt) return '-';
      const shown = txt.length > 20 ? `${txt.slice(0, 10)}...${txt.slice(-10)}` : txt;
      return (
        <span style={snapshotSortMarkBadgeStyle} title={txt}>
          {shown}
        </span>
      );
    };
    const renderSnapshotHeaderDescription = () => {
      const text = String(ctx?.header_display || ctx?.sort_mode_label || '').trim();
      if (!text) return '本快照固化提交时的分拣清点操作记录和已装入容器数据。';
      const parts = text
        .split('|')
        .map((x: string) => String(x || '').trim())
        .filter((x: string) => !!x && !x.includes('库存转换'));
      if (parts.length <= 0) return '本快照固化提交时的分拣清点操作记录和已装入容器数据。';
      return (
        <span>
          {parts.map((part: string, idx: number) => (
            <React.Fragment key={`${part}-${idx}`}>
              {idx > 0 ? <span style={{ margin: '0 10px', color: '#8c8c8c' }}>|</span> : null}
              <span>{part}</span>
            </React.Fragment>
          ))}
        </span>
      );
    };
    const renderSortCountTargetContentItems = (row: any) => {
      const extractItems = (): Array<{ barcode: string; qty: number; productName?: string; customCode?: string }> => {
        try {
          const snap = row?.target?.snapshot || row?.snapshot || {};
          const fromInventoryContentItems = Array.isArray(row?.inventory_content_items) ? row.inventory_content_items : [];
          if (fromInventoryContentItems.length > 0) {
            return fromInventoryContentItems.map((it: any) => {
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              const productName = String(it?.product_name || it?.productName || it?.name || '').trim();
              const customCode = String(it?.product_custom_code || it?.customCode || it?.product_code || '').trim();
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(
                  it?.product_sorting_barcode
                  || it?.sorting_barcode
                  || it?.barcode
                  || it?.product_system_code
                  || it?.system_code
                  || it?.product_code
                  || ''
                ).trim();
              }
              return { barcode, qty, productName, customCode };
            }).filter((x: any) => x.barcode || x.qty);
          }
          const candidates: any[] = [
            snap?.content?.items,
            row?.content?.items,
            row?.target?.content?.items,
            row?.items,
          ].filter(Boolean);
          for (const cand of candidates) {
            const arr = Array.isArray(cand) ? cand : [];
            if (arr.length > 0) {
              return arr.map((it: any) => {
                const p = it?.product || {};
                const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
                const productName = String(it?.product_name || p?.name || '').trim();
                const customCode = String(
                  it?.product_custom_code
                  || p?.custom_code
                  || it?.product_code
                  || ''
                ).trim();
                let barcode = '';
                try {
                  const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                  if (sc.length > 0) {
                    const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                    if (codes.length > 0) barcode = codes.join('/');
                  }
                } catch {}
                if (!barcode) {
                  barcode = String(
                    it?.product_system_code
                    || it?.system_code
                    || it?.product_code
                    || ''
                  );
                }
                return { barcode, qty, productName, customCode };
              }).filter((x: any) => x.barcode || x.qty);
            }
          }
        } catch {}
        return [];
      };
      const items = extractItems();
      if (!items || items.length === 0) return <span style={{ color: '#999' }}>-</span>;
      return (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {items.map((it: any, idx: number) => {
            const productName = String(it?.productName || '').trim();
            const customCode = String(it?.customCode || '').trim();
            const barcode = String(it?.barcode || '').trim();
            const meta = [customCode, barcode].filter(Boolean).join(' ');
            let head = '-';
            if (productName && meta) head = `${productName}(${meta})`;
            else if (productName) head = productName;
            else if (meta) head = meta;
            return (
              <div key={`target-item-${idx}`}>
                {`${head} × ${Math.max(0, Number(it?.qty || 0))}`}
              </div>
            );
          })}
        </div>
      );
    };
    const extractSortCountTargetItems = (row: any): Array<{ qty: number }> => {
      try {
        const snap = row?.target?.snapshot || row?.snapshot || {};
        const fromInventoryContentItems = Array.isArray(row?.inventory_content_items) ? row.inventory_content_items : [];
        if (fromInventoryContentItems.length > 0) {
          return fromInventoryContentItems.map((it: any) => ({ qty: Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0 }));
        }
        const candidates: any[] = [
          snap?.content?.items,
          row?.content?.items,
          row?.target?.content?.items,
          row?.items,
        ].filter(Boolean);
        for (const cand of candidates) {
          const arr = Array.isArray(cand) ? cand : [];
          if (arr.length > 0) {
            return arr.map((it: any) => ({ qty: Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0 }));
          }
        }
      } catch {}
      return [];
    };
    const calcEstimatedPendingQtyForTarget = (row: any): number | null => {
      const unitQty = Math.max(0, Number(row?.expected_qty ?? row?.target_qty ?? 0) || 0);
      const items = extractSortCountTargetItems(row);
      if (!Array.isArray(items) || items.length <= 0) return null;
      const perUnit = items.reduce((acc: number, it: any) => {
        const q = Number(it?.qty ?? 0);
        if (!Number.isFinite(q) || q <= 0) return acc;
        return acc + q;
      }, 0);
      return Math.max(0, Math.floor(perUnit * unitQty));
    };
    const getSortCountTargetUnitType = (row: any): string => {
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const direct = String(
        row?.unit_type
        || row?.unit_type_display
        || snap?.unit_type
        || snap?.unit_type_display
        || '',
      ).trim().toUpperCase();
      if (direct === 'BIU' || direct.includes('箱库存单元')) return 'BIU';
      if (direct === 'LIU' || direct.includes('散货库存单元')) return 'LIU';
      if (direct === 'PIU' || direct.includes('托盘库存单元')) return 'PIU';
      const iuCode = String(
        row?.inventory_unit_system_barcode
        || row?.inventory_unit_code
        || row?.target_code
        || snap?.system_barcode
        || snap?.system_code
        || '',
      ).trim().toUpperCase();
      if (iuCode.startsWith('BIU')) return 'BIU';
      if (iuCode.startsWith('LIU')) return 'LIU';
      if (iuCode.startsWith('PIU')) return 'PIU';
      return '';
    };
    const calcEstimatedSortCountTargetQtyForDisplay = (row: any): number | null => {
      if (String(ctx?.demand_mode || '').trim() === 'need1' && getSortCountTargetUnitType(row) === 'BIU') {
        const v = Number(row?.expected_qty ?? row?.target_qty ?? 0);
        if (!Number.isFinite(v)) return 0;
        return Math.max(0, Math.floor(v));
      }
      return calcEstimatedPendingQtyForTarget(row);
    };
    const estimatedPendingProductTotal = (() => {
      let sum = 0;
      let missing = false;
      (Array.isArray(targetRows) ? targetRows : []).forEach((row: any) => {
        const v = calcEstimatedSortCountTargetQtyForDisplay(row);
        if (v == null) {
          missing = true;
          return;
        }
        sum += v;
      });
      if (missing) return null;
      return Math.max(0, Math.floor(sum));
    })();
    const sortedTotal = Number(stats?.sorted_total_qty || 0);
    const diffQty = estimatedPendingProductTotal == null ? null : Math.floor(estimatedPendingProductTotal - sortedTotal);
    return (
      <div style={{ lineHeight: 1.7 }}>
        <Alert
          type={Number(stats?.location_retention_total_qty || 0) > 0 ? 'warning' : 'success'}
          showIcon
          style={{ marginBottom: 12 }}
          message={`分拣清点阶段快照 | ${demandModeText}`}
          description={renderSnapshotHeaderDescription()}
        />
        <Descriptions bordered size="small" column={4} style={{ marginBottom: 12 }}>
          <Descriptions.Item label="预估操作对象数量">{Number(stats?.operation_target_expected_qty_total || 0)}</Descriptions.Item>
          <Descriptions.Item label="预估分拣目标总数量">{estimatedPendingProductTotal == null ? '-' : estimatedPendingProductTotal}</Descriptions.Item>
          <Descriptions.Item label="已分拣目标总数量">{sortedTotal}</Descriptions.Item>
          <Descriptions.Item label="差额">
            <span style={{ background: '#ffeb3b', border: '1px solid #fadb14', color: '#cf1322', fontWeight: 800, padding: '1px 8px', borderRadius: 4 }}>
              {diffQty == null ? '-' : diffQty}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="分拣标识数量">{Number(stats?.by_mark_count || 0)}</Descriptions.Item>
          <Descriptions.Item label="分拣组数量">{sortGroupCount}</Descriptions.Item>
          <Descriptions.Item label="已装入容器" span={2}>{`${Number(stats?.generated_container_count || 0)}容器 ${Number(stats?.generated_container_total_qty || 0)}件`}</Descriptions.Item>
        </Descriptions>
        <Divider style={{ margin: '14px 0 16px' }} />
        <Tabs
          size="small"
          items={[
            {
              key: 'targets',
              label: `操作对象(${targetRows.length})`,
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, i?: number) => String(r?.id || r?.target_id || r?.inventory_unit_id || i || 0)}
                  pagination={{ size: 'small', pageSize: 10 }}
                  dataSource={targetRows}
                  columns={[
                    { title: '序号', dataIndex: '__idx', key: '__idx', width: 70, align: 'center' as const },
                    {
                      title: '库存单元系统编码',
                      key: 'inventory_unit',
                      width: 200,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const code = String(
                          row?.inventory_unit_system_barcode
                          || row?.snapshot?.system_barcode
                          || row?.snapshot?.system_code
                          || row?.inventory_unit_code
                          || row?.inventory_unit_id
                          || '-',
                        ).trim();
                        return code || '-';
                      },
                    },
                    {
                      title: '入库分拣组',
                      key: 'sorting_group',
                      width: 200,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const sgCode = String(
                          row?.sorting_group_info?.system_code
                          || row?.sorting_group_code
                          || row?.snapshot?.sorting_group_info?.system_code
                          || '',
                        ).trim();
                        const sgName = String(
                          row?.sorting_group_info?.name
                          || row?.sorting_group_name
                          || row?.snapshot?.sorting_group_info?.name
                          || '',
                        ).trim();
                        if (sgCode && sgName) return `${sgCode} (${sgName})`;
                        return sgCode || sgName || '-';
                      },
                    },
                    {
                      title: '库存单元内容项',
                      key: 'inventory_unit_items',
                      width: 420,
                      render: (_: any, row: any) => renderSortCountTargetContentItems(row),
                    },
                    {
                      title: '预估库存单元数量',
                      key: 'qty',
                      width: 150,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const qty = row?.expected_qty;
                        return qty ?? 0;
                      },
                    },
                    {
                      title: '预估分拣目标数量',
                      key: 'estimated_pending_product_qty',
                      width: 170,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const v = calcEstimatedSortCountTargetQtyForDisplay(row);
                        return v == null ? '-' : v;
                      },
                    },
                  ] as any}
                  locale={{ emptyText: '暂无操作对象快照' }}
                  scroll={{ x: 'max-content' as any }}
                  tableLayout="fixed"
                />
              ),
            },
            {
              key: 'preset',
              label: `预设分组列表(${presetRows.length})`,
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, i?: number) => String(r?.__idx ?? i ?? 0)}
                  pagination={buildSnapshotPagination(presetRows.length, presetPage, presetPageSize, setPresetPage, setPresetPageSize)}
                  dataSource={presetRows}
                  columns={(Array.isArray(sections?.preset_group_list?.columns) ? sections.preset_group_list.columns : []).map((c: any) => ({
                    ...c,
                    render: (v: any) => {
                      const titleText = String(c?.title || '').trim();
                      if (titleText.includes('分拣标识')) return renderSnapshotSortMarkBadge(v);
                      return String(v ?? '');
                    },
                  }))}
                  locale={{ emptyText: '暂无预设分组列表快照' }}
                  scroll={{ x: 'max-content' as any }}
                />
              ),
            },
            {
              key: 'sort-db',
              label: `分拣数据库与仓位表(${sortDatabaseRows.length})`,
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, i?: number) => String(r?.id || `${r?.location_no || ''}-${r?.sort_id || ''}-${i || 0}`)}
                  pagination={buildSnapshotPagination(sortDatabaseRows.length, sortDbPage, sortDbPageSize, setSortDbPage, setSortDbPageSize)}
                  dataSource={sortDatabaseRows}
                  columns={[
                    { title: '仓位', dataIndex: 'location_no', key: 'location_no', width: 90, align: 'center' as const, render: (v: any) => renderSnapshotLocationBadge(v) },
                    { title: '分拣组', dataIndex: 'group_code', key: 'group_code', width: 120, align: 'center' as const },
                    { title: '分拣ID', dataIndex: 'sort_id', key: 'sort_id', width: 90, align: 'center' as const },
                    { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', render: (v: any) => renderSnapshotSortMarkBadge(v) },
                    { title: '预设分组编号', dataIndex: 'preset_group_no', key: 'preset_group_no', width: 130, align: 'center' as const },
                  ] as any}
                  locale={{ emptyText: '暂无分拣数据库与仓位表快照' }}
                />
              ),
            },
            {
              key: 'operations',
              label: `操作记录(${operationRows.length})`,
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, i?: number) => String(r?.id || r?.seq || i || 0)}
                  pagination={buildSnapshotPagination(operationRows.length, operationPage, operationPageSize, setOperationPage, setOperationPageSize)}
                  dataSource={operationRows}
                  columns={[
                    { title: '序号', dataIndex: 'seq', key: 'seq', width: 70, align: 'center' as const },
                    { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', render: (v: any) => renderSnapshotSortMarkBadge(v) },
                    { title: '数量', dataIndex: 'qty', key: 'qty', width: 80, align: 'center' as const },
                    { title: '仓位', dataIndex: 'location_no', key: 'location_no', width: 90, align: 'center' as const, render: (v: any) => renderSnapshotLocationBadge(v) },
                    { title: '时间', dataIndex: 'time_text', key: 'time_text', width: 150 },
                  ] as any}
                  locale={{ emptyText: '暂无操作记录快照' }}
                />
              ),
            },
            {
              key: 'by-mark',
              label: `按分拣标识统计(${byMarkRows.length})`,
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, i?: number) => String(r?.sorting_mark || i || 0)}
                  pagination={buildSnapshotPagination(byMarkRows.length, byMarkPage, byMarkPageSize, setByMarkPage, setByMarkPageSize)}
                  dataSource={byMarkRows}
                  columns={[
                    { title: '序号', dataIndex: '__idx', key: '__idx', width: 70, align: 'center' as const },
                    { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', render: (v: any) => renderSnapshotSortMarkBadge(v) },
                    { title: '分拣ID', dataIndex: 'sort_id', key: 'sort_id', width: 90, align: 'center' as const },
                    { title: '累计数量', dataIndex: 'total_qty', key: 'total_qty', width: 100, align: 'center' as const },
                    { title: '仓位', dataIndex: 'location_no', key: 'location_no', width: 90, align: 'center' as const, render: (v: any) => renderSnapshotLocationBadge(v) },
                    { title: '最近时间', dataIndex: 'last_time', key: 'last_time', width: 150 },
                  ] as any}
                  locale={{ emptyText: '暂无按分拣标识统计快照' }}
                />
              ),
            },
            {
              key: 'containers',
              label: `按容器统计(${containerGroupKeys.length})`,
              children: (
                <div>
                  <Table
                    size="small"
                    rowKey={(r: any, i?: number) => String(r?.__rowKey || `${r?.system_code || r?.__containerGroupKey || ''}-${i || 0}`)}
                    pagination={false}
                    dataSource={containerDisplayRows}
                    onRow={(row: any) => ({
                      style: { background: row?.__containerBg || undefined },
                    })}
                    columns={[
                      {
                        title: '序号',
                        dataIndex: '__containerSeq',
                        key: '__containerSeq',
                        width: 70,
                        align: 'center' as const,
                        onCell: (row: any) => ({
                          rowSpan: Number(row?.__containerRowSpanForSnapshot || 0),
                          style: { background: row?.__containerBg || undefined },
                        }),
                      },
                      {
                        title: '容器系统编码',
                        dataIndex: 'system_code',
                        key: 'system_code',
                        width: 160,
                        onCell: (row: any) => ({
                          rowSpan: Number(row?.__containerRowSpanForSnapshot || 0),
                          style: { background: row?.__containerBg || undefined },
                        }),
                      },
                      {
                        title: '容器类型',
                        dataIndex: 'container_type',
                        key: 'container_type',
                        width: 100,
                        align: 'center' as const,
                        render: (v: any) => String(v || '-'),
                        onCell: (row: any) => ({
                          rowSpan: Number(row?.__containerRowSpanForSnapshot || 0),
                          style: { background: row?.__containerBg || undefined },
                        }),
                      },
                      { title: '库存单元', dataIndex: 'unit_code', key: 'unit_code', width: 160, onCell: (row: any) => ({ style: { background: row?.__containerBg || undefined } }) },
                      { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', render: (v: any) => renderSnapshotSortMarkBadge(v), onCell: (row: any) => ({ style: { background: row?.__containerBg || undefined } }) },
                      { title: '数量', dataIndex: 'qty', key: 'qty', width: 80, align: 'center' as const, onCell: (row: any) => ({ style: { background: row?.__containerBg || undefined } }) },
                      {
                        title: '总数量',
                        dataIndex: 'total_qty',
                        key: 'total_qty',
                        width: 90,
                        align: 'center' as const,
                        onCell: (row: any) => ({
                          rowSpan: Number(row?.__containerRowSpanForSnapshot || 0),
                          style: { background: row?.__containerBg || undefined },
                        }),
                      },
                    ] as any}
                    locale={{ emptyText: '暂无按容器统计快照' }}
                  />
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <Pagination
                      size="small"
                      current={containerPage}
                      pageSize={containerPageSize}
                      total={containerGroupsForSnapshot.length}
                      showSizeChanger
                      showQuickJumper
                      pageSizeOptions={['5', '10', '20', '50'] as any}
                      onChange={(page: number, pageSize: number) => {
                        setContainerPage(page);
                        setContainerPageSize(pageSize);
                      }}
                      showTotal={(total: number, range: [number, number]) => `第${range[0]}-${range[1]}个容器,共${total}个容器`}
                    />
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    );
  };

  // ========= 常规出库：快照渲染（提交前预览 & 提交后回看共用） =========
  const RegularOutboundSnapshotBlock: React.FC<{ snapshotEnvelope: any }> = ({ snapshotEnvelope }) => {
    const root = snapshotEnvelope || {};
    const snap = (root && root.snapshot) ? root.snapshot : root;
    const sections = (snap?.sections || {}) as any;
    const metrics = (snap?.metrics || {}) as any;
    const stats = (root?.stats || {}) as any;

    const process = sections?.process_result || {
      status: String(metrics?.process_result_status || '').trim().toLowerCase(),
      unable_reason: String(metrics?.process_unable_reason || '').trim(),
    };
    const waybillCarrier = String(metrics?.waybill_carrier || '').trim();
    const waybillTrackingNo = String(metrics?.waybill_tracking_no || '').trim();
    const waybillChargeAmountRaw = String(metrics?.waybill_charge_amount || '').trim();
    const waybillChargeCurrency = String(metrics?.waybill_charge_currency || '').trim().toUpperCase();
    const waybillChargeNote = String(metrics?.waybill_charge_note || '').trim();
    const waybillChargeDisplay = (() => {
      if (!waybillChargeAmountRaw) return '-';
      return waybillChargeCurrency ? `${waybillChargeAmountRaw} ${waybillChargeCurrency}` : waybillChargeAmountRaw;
    })();
    const waybillSourceRaw = String(
      metrics?.waybill_source
      || metrics?.waybillMode
      || metrics?.waybill_source_text
      || '',
    ).trim();
    const waybillSubmitModeRaw = String(
      metrics?.waybill_submit_mode
      || metrics?.waybillSubmitMode
      || '',
    ).trim();
    const waybillStatusForSnapshot = (() => {
      const src = waybillSourceRaw;
      const submitMode = waybillSubmitModeRaw;
      if (!src && !submitMode) return '-';
      if (src.includes('客户') || src.toLowerCase() === 'customer') return '有运单';
      if (src.includes('仓库') || src.toLowerCase() === 'warehouse') {
        if (submitMode.includes('先申请包装数据') || submitMode.toLowerCase() === 'before_pack') return '先申请包装数据';
        return '仓库提供运单';
      }
      return src || submitMode || '-';
    })();
    const showWarehouseWaybillLine = waybillStatusForSnapshot === '仓库提供运单';
    useEffect(() => {
      const isProd = typeof process !== 'undefined' && !!process?.env && process.env.NODE_ENV === 'production';
      if (isProd) return;
      try {
        // 调试快照预览/回看：确认仓库运单判定所需字段是否写入 snapshot.metrics
        console.debug('[RO-Snapshot][Waybill][ExecutePage]', {
          waybill_source: waybillSourceRaw || '',
          waybill_submit_mode: waybillSubmitModeRaw || '',
          waybill_status_for_snapshot: waybillStatusForSnapshot,
          show_line: showWarehouseWaybillLine,
          carrier: waybillCarrier || '',
          tracking_no: waybillTrackingNo || '',
          charge_amount: waybillChargeAmountRaw || '',
          charge_currency: waybillChargeCurrency || '',
        });
      } catch {}
    }, [
      waybillSourceRaw,
      waybillSubmitModeRaw,
      waybillStatusForSnapshot,
      showWarehouseWaybillLine,
      waybillCarrier,
      waybillTrackingNo,
      waybillChargeAmountRaw,
      waybillChargeCurrency,
    ]);
    const opRows = Array.isArray(sections?.operation_records?.rows)
      ? sections.operation_records.rows
      : (Array.isArray(metrics?.operation_records) ? metrics.operation_records : []);
    const boxes = Array.isArray(sections?.containers?.rows)
      ? sections.containers.rows
      : (Array.isArray(metrics?.boxes) ? metrics.boxes : []);
    const hierarchyLinks = Array.isArray(sections?.containers?.hierarchy_links)
      ? sections.containers.hierarchy_links
      : (Array.isArray(metrics?.hierarchy_links) ? metrics.hierarchy_links : []);
    const progressTabs = (sections?.progress_tabs || metrics?.progress_tabs_snapshot || {}) as any;
    const statusZh = (stRaw: any): string => {
      const st = String(stRaw || '').trim().toLowerCase();
      if (st === 'open' || st === 'active') return '进行中';
      if (st === 'closed') return '已结束';
      if (st === 'cancelled') return '已作废';
      if (st === 'consumed') return '已消耗';
      if (st === 'lost') return '丢失';
      return st || '-';
    };
    const isIocTarget = !!progressTabs?.has_ioc_target;
    const byTargetRows = useMemo(() => {
      if (Array.isArray(progressTabs?.by_target_rows)) return progressTabs.by_target_rows;
      const m = new Map<string, { target_type: string; target_code: string; qty: number }>();
      (Array.isArray(opRows) ? opRows : []).forEach((r: any) => {
        const targetCode = String(r?.target || r?.target_code || r?.code || '').trim().toUpperCase();
        if (!targetCode || targetCode === '-') return;
        const targetType = String(r?.target_type || '').trim() || 'unknown';
        const ev = String(r?.event || r?.action || '').trim();
        const qRaw = Number(r?.qty ?? r?.quantity ?? 0) || 0;
        const qAbs = Math.max(0, Math.floor(Math.abs(qRaw)));
        if (qAbs <= 0) return;
        const isOut = /取出|冲销|移除|移出/.test(ev);
        const delta = isOut ? -qAbs : qAbs;
        const key = `${targetType}:${targetCode}`;
        const cur = m.get(key) || { target_type: targetType, target_code: targetCode, qty: 0 };
        cur.qty += delta;
        m.set(key, cur);
      });
      return Array.from(m.values())
        .filter((x) => Number(x.qty || 0) > 0)
        .sort((a, b) => String(a.target_code).localeCompare(String(b.target_code), undefined, { numeric: true, sensitivity: 'base' }));
    }, [progressTabs, opRows]);
    const byContainerRows = useMemo(() => {
      if (Array.isArray(progressTabs?.by_container_rows)) return progressTabs.by_container_rows;
      return (Array.isArray(boxes) ? boxes : []).map((r: any) => ({
        container_code: String(r?.code || r?.container_code || '-'),
        status: String(r?.status || '-'),
        status_zh: statusZh(r?.status),
        is_parent: (r?.is_parent != null ? !!r.is_parent : !!r?.has_children),
        hierarchy_level: (r?.is_parent != null && !!r?.is_parent) ? '父' : '独立',
        qty: Math.max(0, Math.floor(Number(r?.estimated_qty ?? r?.content_qty ?? r?.qty ?? 0) || 0)),
        direct_qty: Math.max(0, Math.floor(Number(r?.direct_total_qty ?? r?.estimated_qty ?? r?.content_qty ?? r?.qty ?? 0) || 0)),
        aggregated_qty: Math.max(0, Math.floor(Number(r?.aggregated_total_qty ?? r?.estimated_qty ?? r?.content_qty ?? r?.qty ?? 0) || 0)),
        ops: 0,
        objectKinds: 0,
        weight_display: '-',
        dims_display: '-',
        last_ts: 0,
      }));
    }, [progressTabs, boxes]);
    const byHierarchyRows = useMemo(() => {
      if (Array.isArray(progressTabs?.by_hierarchy_rows)) return progressTabs.by_hierarchy_rows;
      const boxStatusMap = new Map<string, string>();
      (Array.isArray(byContainerRows) ? byContainerRows : []).forEach((r: any) => {
        const code = String(r?.container_code || r?.code || '').trim();
        if (code) boxStatusMap.set(code, String(r?.status || '').trim());
      });
      return (Array.isArray(hierarchyLinks) ? hierarchyLinks : []).map((ln: any, idx: number) => {
        const p = String(ln?.parent_code || '').trim();
        const c = String(ln?.child_code || '').trim();
        const pSt = boxStatusMap.get(p) || '';
        const cSt = boxStatusMap.get(c) || '';
        return {
          key: `${idx}-${p}-${c}`,
          parent_code: p,
          parent_status: pSt,
          parent_status_zh: statusZh(pSt),
          child_code: c,
          child_status: cSt,
          child_status_zh: statusZh(cSt),
          group_idx: idx,
          is_first_in_group: true,
        };
      });
    }, [progressTabs, hierarchyLinks, byContainerRows]);
    const activeContainerRows = useMemo(() => (
      (Array.isArray(byContainerRows) ? byContainerRows : []).filter((r: any) => String(r?.status || '').trim().toLowerCase() !== 'cancelled')
    ), [byContainerRows]);
    const cancelledOnlyRows = useMemo(() => (
      (Array.isArray(byContainerRows) ? byContainerRows : []).filter((r: any) => String(r?.status || '').trim().toLowerCase() === 'cancelled')
    ), [byContainerRows]);
    const boxCodes = useMemo(() => (
      Array.from(new Set((Array.isArray(byContainerRows) ? byContainerRows : []).map((b: any) => String(b?.container_code || b?.code || '').trim()).filter(Boolean)))
    ), [byContainerRows]);
    const parentCodeSet = useMemo(() => (
      new Set((Array.isArray(byHierarchyRows) ? byHierarchyRows : []).map((ln: any) => String(ln?.parent_code || '').trim()).filter(Boolean))
    ), [byHierarchyRows]);
    const childCodeSet = useMemo(() => (
      new Set((Array.isArray(byHierarchyRows) ? byHierarchyRows : []).map((ln: any) => String(ln?.child_code || '').trim()).filter(Boolean))
    ), [byHierarchyRows]);
    const parentContainerCount = useMemo(() => (
      boxCodes.filter((c: string) => parentCodeSet.has(c)).length
    ), [boxCodes, parentCodeSet]);
    const childContainerCount = useMemo(() => (
      boxCodes.filter((c: string) => childCodeSet.has(c)).length
    ), [boxCodes, childCodeSet]);
    const independentContainerCount = useMemo(() => (
      boxCodes.filter((c: string) => !parentCodeSet.has(c) && !childCodeSet.has(c)).length
    ), [boxCodes, parentCodeSet, childCodeSet]);
    const cancelledContainerCount = useMemo(() => (
      (Array.isArray(byContainerRows) ? byContainerRows : []).filter((b: any) => String(b?.status || '').trim().toLowerCase() === 'cancelled').length
    ), [byContainerRows]);
    const generatedContainerCount = boxCodes.length;

    const [opPage, setOpPage] = useState<number>(1);
    const [opPageSize, setOpPageSize] = useState<number>(10);
    const [snapByTargetPage, setSnapByTargetPage] = useState<number>(1);
    const [snapByTargetPageSize, setSnapByTargetPageSize] = useState<number>(10);
    const [snapByContainerActivePage, setSnapByContainerActivePage] = useState<number>(1);
    const [snapByContainerActivePageSize, setSnapByContainerActivePageSize] = useState<number>(10);
    const [snapByContainerCancelledPage, setSnapByContainerCancelledPage] = useState<number>(1);
    const [snapByContainerCancelledPageSize, setSnapByContainerCancelledPageSize] = useState<number>(10);
    const [snapByHierarchyPage, setSnapByHierarchyPage] = useState<number>(1);
    const [snapByHierarchyPageSize, setSnapByHierarchyPageSize] = useState<number>(10);
    const [targetContainerOpen, setTargetContainerOpen] = useState<boolean>(false);
    const [targetContainerTitle, setTargetContainerTitle] = useState<string>('装箱汇总');
    const [targetContainerRows, setTargetContainerRows] = useState<Array<{ key: string; container_code: string; qty: number; cnt: number; lastTs: number }>>([]);
    const [targetContainerPage, setTargetContainerPage] = useState<number>(1);
    const [targetContainerPageSize, setTargetContainerPageSize] = useState<number>(10);
    const [containerDetailOpen, setContainerDetailOpen] = useState<boolean>(false);
    const [containerDetailCode, setContainerDetailCode] = useState<string>('');
    const [containerDetailRows, setContainerDetailRows] = useState<any[]>([]);
    const [containerDetailPage, setContainerDetailPage] = useState<number>(1);
    const [containerDetailPageSize, setContainerDetailPageSize] = useState<number>(10);
    const [boxFinalOpen, setBoxFinalOpen] = useState<boolean>(false);
    const [boxFinalCode, setBoxFinalCode] = useState<string>('');
    const [boxFinalContentPage, setBoxFinalContentPage] = useState<number>(1);
    const [boxFinalContentPageSize, setBoxFinalContentPageSize] = useState<number>(10);

    useEffect(() => {
      setOpPage(1);
    }, [opRows.length, byContainerRows.length, byHierarchyRows.length, byTargetRows.length]);
    useEffect(() => {
      setSnapByTargetPage(1);
    }, [byTargetRows.length]);
    useEffect(() => {
      setSnapByContainerActivePage(1);
    }, [activeContainerRows.length]);
    useEffect(() => {
      setSnapByContainerCancelledPage(1);
    }, [cancelledOnlyRows.length]);
    useEffect(() => {
      setSnapByHierarchyPage(1);
    }, [byHierarchyRows.length]);

    const processRaw = String(process?.status || '').trim().toLowerCase();
    const processText = processRaw === 'completed' ? '已完成' : (processRaw === 'unable' ? '无法操作' : '未标注');
    const processBadgeStyle: React.CSSProperties = processRaw === 'completed'
      ? { background: '#52c41a', color: '#fff', border: '1px solid #52c41a' }
      : (processRaw === 'unable'
        ? { background: '#ff4d4f', color: '#fff', border: '1px solid #ff4d4f' }
        : { background: '#f5f5f5', color: '#595959', border: '1px solid #d9d9d9' });
    const overviewHeaderStats = useMemo(() => {
      try {
        const rows = Array.isArray(byContainerRows) ? byContainerRows : [];
        const boxMeta = (metrics?.box_meta && typeof metrics.box_meta === 'object') ? metrics.box_meta : {};
        const closedRows = rows.filter((r: any) => {
          const st = String(r?.status || '').trim().toLowerCase();
          return st && st !== 'open' && st !== 'cancelled';
        });
        const closedCount = closedRows.length;
        // 总体积/总重量口径：仅统计父容器和独立容器，不统计子容器
        const closedRowsForMassVolume = closedRows.filter((r: any) => {
          const code = String(r?.container_code || r?.code || '').trim();
          if (!code) return false;
          return !childCodeSet.has(code);
        });
        let totalVolM3 = 0;
        let totalKg = 0;
        closedRowsForMassVolume.forEach((r: any) => {
          const code = String(r?.container_code || '').trim();
          const meta: any = (boxMeta as any)?.[code] || {};
          const l = Math.floor(Number(meta?.length_cm || 0) || 0);
          const w = Math.floor(Number(meta?.width_cm || 0) || 0);
          const h = Math.floor(Number(meta?.height_cm || 0) || 0);
          if (l > 0 && w > 0 && h > 0) totalVolM3 += (l * w * h) / 1_000_000;
          const kg = (() => {
            const v = Number(meta?.weight_kg);
            if (Number.isFinite(v) && v > 0) return v;
            const g = Number(meta?.weight_g);
            if (Number.isFinite(g) && g > 0) return g / 1000;
            const lb = Number(meta?.weight_lb);
            if (Number.isFinite(lb) && lb > 0) return lb / 2.2046226218;
            return 0;
          })();
          if (kg > 0) totalKg += kg;
        });
        const targetQty = isIocTarget
          ? (Array.isArray(byTargetRows) ? byTargetRows.length : 0)
          : (Array.isArray(byTargetRows) ? byTargetRows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.target_qty || 0) || 0)), 0) : 0);
        const doneQty = isIocTarget
          ? (Array.isArray(byTargetRows) ? byTargetRows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.done_qty || 0) || 0)), 0) : 0)
          : (Array.isArray(byTargetRows) ? byTargetRows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.picked_qty || 0) || 0)), 0) : 0);
        const rateValue = targetQty > 0 ? (doneQty / targetQty) * 100 : null;
        const fmt1 = (n: number) => (!Number.isFinite(n) ? '-' : String(Number(n.toFixed(1))));
        const fmt2 = (n: number) => (!Number.isFinite(n) || n <= 0 ? '0' : String(Number(n.toFixed(2))));
        const fmt3 = (n: number) => (!Number.isFinite(n) || n <= 0 ? '0' : String(Number(n.toFixed(3))));
        return {
          closedCount,
          volM3Text: fmt3(totalVolM3),
          kgText: fmt2(totalKg),
          doneQty,
          targetQty,
          rateValue: (rateValue == null || !Number.isFinite(rateValue)) ? null : Number(rateValue),
          rateText: (rateValue == null || !Number.isFinite(rateValue)) ? '-' : `${fmt1(rateValue)}%`,
        };
      } catch {
        return { closedCount: 0, volM3Text: '0', kgText: '0', doneQty: 0, targetQty: 0, rateValue: null, rateText: '-' };
      }
    }, [byContainerRows, metrics, byTargetRows, isIocTarget, childCodeSet]);
    const formatDateTimeCell = useCallback((raw: any) => {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw || '-');
      const y = d.getFullYear();
      const mo = d.getMonth() + 1;
      const da = d.getDate();
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span>{`${y}/${mo}/${da}`}</span>
          <span>{`${hh}:${mm}:${ss}`}</span>
        </span>
      );
    }, []);
    const operationTimeStats = useMemo(() => {
      const tsList = (Array.isArray(opRows) ? opRows : [])
        .map((r: any) => {
          const raw = r?.raw_time ?? r?.time ?? r?.ts;
          const ts = Number(new Date(raw).getTime());
          return Number.isFinite(ts) && ts > 0 ? ts : null;
        })
        .filter((x: any) => x != null) as number[];
      if (tsList.length <= 0) {
        return { earliestTs: 0, latestTs: 0, durationText: '-' };
      }
      const earliestTs = Math.min(...tsList);
      const latestTs = Math.max(...tsList);
      const diffMs = Math.max(0, latestTs - earliestTs);
      const totalMin = Math.floor(diffMs / 60000);
      const day = Math.floor(totalMin / (24 * 60));
      const hour = Math.floor((totalMin % (24 * 60)) / 60);
      const minute = totalMin % 60;
      return {
        earliestTs,
        latestTs,
        durationText: `${day}天${hour}小时${minute}分钟`,
      };
    }, [opRows]);
    const formatDateTimeText = useCallback((raw: any): string => {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '-';
      const y = d.getFullYear();
      const mo = d.getMonth() + 1;
      const da = d.getDate();
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      return `${y}/${mo}/${da} ${hh}:${mm}:${ss}`;
    }, []);
    const resolveLedgerSignedQty = useCallback((r: any) => {
      const ev = String(r?.event || r?.action || '').trim();
      const qRaw = Number(r?.qty ?? r?.quantity ?? 0) || 0;
      const qAbs = Math.max(0, Math.floor(Math.abs(qRaw)));
      if (qAbs <= 0) return 0;
      const isOut = /取出|冲销|移除|移出/.test(ev);
      return isOut ? -qAbs : qAbs;
    }, []);
    const toTargetKey = useCallback((v: any): string => String(v || '').trim().toUpperCase(), []);
    const getTargetOperationRows = useCallback((targetCodeRaw: any) => {
      const tc = toTargetKey(targetCodeRaw);
      if (!tc) return [] as any[];
      return (Array.isArray(opRows) ? opRows : []).filter((r: any) => {
        const t = toTargetKey(r?.target || r?.target_code || r?.code);
        return t === tc;
      });
    }, [opRows, toTargetKey]);
    const targetContainerStats = useMemo(() => {
      const rows = Array.isArray(targetContainerRows) ? targetContainerRows : [];
      const boxCount = rows.length;
      const totalQty = rows.reduce((acc: number, r: any) => acc + Math.max(0, Math.floor(Number(r?.qty || 0) || 0)), 0);
      return { boxCount, totalQty };
    }, [targetContainerRows]);
    const openContainerDetail = useCallback((containerCodeRaw: any) => {
      const cc = String(containerCodeRaw || '').trim();
      if (!cc) return;
      const rows = (Array.isArray(opRows) ? opRows : [])
        .filter((r: any) => String(r?.container_code || '').trim() === cc)
        .map((r: any, idx: number) => ({
          key: `${idx}-${String(r?.raw_time ?? r?.time ?? r?.ts ?? '')}`,
          raw: r,
          event: String(r?.event || r?.action || '-'),
          container_code: String(r?.container_code || '-'),
          target: String(r?.target || r?.target_code || r?.code || '-'),
          qty: resolveLedgerSignedQty(r),
          time: (r?.raw_time ?? r?.time ?? r?.ts ?? ''),
        }))
        .sort((a: any, b: any) => {
          const ta = Number(new Date(a?.time).getTime()) || 0;
          const tb = Number(new Date(b?.time).getTime()) || 0;
          return tb - ta;
        });
      setContainerDetailCode(cc);
      setContainerDetailRows(rows);
      setContainerDetailPage(1);
      setContainerDetailOpen(true);
    }, [opRows, resolveLedgerSignedQty]);
    const openTargetContainerSummary = useCallback((row: any) => {
      const titleCode = String(row?.unit_code || row?.output_code || row?.target_code || row?.key || '-').trim();
      const keyCode = String(row?.unit_code || row?.output_code || row?.target_code || row?.key || '').trim();
      const list = getTargetOperationRows(keyCode);
      const byBox = new Map<string, { container_code: string; qty: number; cnt: number; lastTs: number }>();
      (Array.isArray(list) ? list : []).forEach((x: any) => {
        const cc = String(x?.container_code || '').trim();
        if (!cc || cc === '-') return;
        const delta = resolveLedgerSignedQty(x);
        if (!Number.isFinite(delta) || delta === 0) return;
        const ts = Number(new Date(x?.raw_time ?? x?.time ?? x?.ts).getTime()) || 0;
        const cur = byBox.get(cc) || { container_code: cc, qty: 0, cnt: 0, lastTs: 0 };
        cur.qty += delta;
        cur.cnt += 1;
        if (ts > cur.lastTs) cur.lastTs = ts;
        byBox.set(cc, cur);
      });
      const rows = Array.from(byBox.values())
        .filter((r: any) => Math.floor(Number(r?.qty || 0) || 0) > 0)
        .sort((a: any, b: any) => (Number(b?.lastTs || 0) - Number(a?.lastTs || 0)) || String(a?.container_code || '').localeCompare(String(b?.container_code || '')))
        .map((r: any, idx: number) => ({ key: `${idx}-${r.container_code}`, ...r }));
      setTargetContainerTitle(`操作对象 ${titleCode || '-'} · 装箱汇总`);
      setTargetContainerRows(rows);
      setTargetContainerPage(1);
      setTargetContainerOpen(true);
    }, [getTargetOperationRows, resolveLedgerSignedQty]);
    const containerDetailHeaderStats = useMemo(() => {
      const recordCount = Math.max(0, Math.floor(Number(containerDetailRows.length || 0) || 0));
      return { recordCount };
    }, [containerDetailRows]);
    const boxFinalData = useMemo(() => {
      if (!boxFinalCode) return null as any;
      const code = String(boxFinalCode || '').trim();
      const row = (Array.isArray(byContainerRows) ? byContainerRows : []).find((r: any) => String(r?.container_code || '').trim() === code);
      if (!row) return null as any;
      const outputDraftByCode = (metrics?.output_draft_by_code && typeof metrics.output_draft_by_code === 'object') ? metrics.output_draft_by_code : {};
      const boxMeta = (metrics?.box_meta && typeof metrics.box_meta === 'object') ? metrics.box_meta : {};
      const draftLike: any = (outputDraftByCode as any)?.[code] || (boxMeta as any)?.[code] || {};
      const lcm = Math.max(0, Math.floor(Number(draftLike?.length_cm || 0) || 0));
      const wcm = Math.max(0, Math.floor(Number(draftLike?.width_cm || 0) || 0));
      const hcm = Math.max(0, Math.floor(Number(draftLike?.height_cm || 0) || 0));
      const kgNum = (() => {
        const wg = Number(draftLike?.weight_g);
        if (Number.isFinite(wg) && wg > 0) return Number((wg / 1000).toFixed(2));
        const kk = Number(draftLike?.weight_kg);
        if (Number.isFinite(kk) && kk > 0) return Number(kk.toFixed(2));
        return 0;
      })();
      const lbNum = (() => {
        const v = Number(draftLike?.weight_lb);
        if (Number.isFinite(v) && v > 0) return Number(v.toFixed(2));
        return kgNum > 0 ? Number((kgNum * 2.2046226218).toFixed(2)) : 0;
      })();
      const li = Number(draftLike?.length_in) > 0 ? Math.floor(Number(draftLike?.length_in)) : (lcm > 0 ? Math.ceil(lcm / 2.54) : 0);
      const wi = Number(draftLike?.width_in) > 0 ? Math.floor(Number(draftLike?.width_in)) : (wcm > 0 ? Math.ceil(wcm / 2.54) : 0);
      const hi = Number(draftLike?.height_in) > 0 ? Math.floor(Number(draftLike?.height_in)) : (hcm > 0 ? Math.ceil(hcm / 2.54) : 0);
      const timeRows = (Array.isArray(opRows) ? opRows : []).filter((r: any) => String(r?.container_code || '').trim() === code);
      const detailMap = new Map<string, number>();
      const targetContentLinesMap = new Map<string, string[]>();
      const splitContentLines = (raw: any): string[] => String(raw || '')
        .split(/[；;]/)
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      (Array.isArray(byTargetRows) ? byTargetRows : []).forEach((r: any) => {
        const targetCode = String(r?.target_code || r?.unit_code || r?.output_code || r?.key || '').trim().toUpperCase();
        if (!targetCode || targetCode === '-') return;
        const kind = String(r?.output_content_kind || '').trim().toLowerCase();
        let lines: string[] = [];
        if (kind === 'exception') {
          lines = ['无法操作等异常产品'];
        } else if (String(r?.output_contents_detail || '').trim()) {
          lines = splitContentLines(r?.output_contents_detail);
        } else if (Array.isArray(r?.content_lines)) {
          lines = r.content_lines.map((x: any) => String(x || '').trim()).filter(Boolean);
        }
        targetContentLinesMap.set(targetCode, lines.length ? lines : ['-']);
      });
      let startTs = 0;
      let endTs = 0;
      let lastTs = 0;
      (Array.isArray(timeRows) ? timeRows : []).forEach((r: any) => {
        const ts = Number(new Date(r?.raw_time ?? r?.time ?? r?.ts).getTime());
        if (Number.isFinite(ts) && ts > 0) {
          if (!startTs || ts < startTs) startTs = ts;
          if (ts > lastTs) lastTs = ts;
        }
        const ev = String(r?.event || r?.action || '').trim();
        if (ev === '开始容器' && ts > 0) startTs = startTs > 0 ? Math.min(startTs, ts) : ts;
        if (ev === '结束容器' && ts > 0) endTs = Math.max(endTs, ts);
        const target = String(r?.target || r?.target_code || r?.code || '').trim().toUpperCase();
        if (!target || target === '-') return;
        const delta = resolveLedgerSignedQty(r);
        if (!Number.isFinite(delta) || delta === 0) return;
        detailMap.set(target, (detailMap.get(target) || 0) + delta);
      });
      if (!endTs) endTs = lastTs;
      const detailRows = Array.from(detailMap.entries())
        .map(([target, qty]) => ({
          key: `${target}`,
          target,
          qty: Math.max(0, Math.floor(Number(qty || 0) || 0)),
          content_lines: targetContentLinesMap.get(String(target || '').trim().toUpperCase()) || ['-'],
        }))
        .filter((x: any) => Number(x.qty || 0) > 0)
        .sort((a: any, b: any) => Number(b.qty || 0) - Number(a.qty || 0));
      const directQty = Math.max(0, Math.floor(Number(row?.direct_qty ?? row?.qty ?? 0) || 0));
      const aggregatedQty = Math.max(0, Math.floor(Number(row?.aggregated_qty ?? row?.qty ?? 0) || 0));
      const childLinks = (Array.isArray(byHierarchyRows) ? byHierarchyRows : []).filter((ln: any) => String(ln?.parent_code || '').trim() === code);
      const hasChildren = childLinks.length > 0;
      const durationHours = (startTs > 0 && endTs > 0 && endTs >= startTs) ? Number((((endTs - startTs) / 3600000)).toFixed(2)) : 0;
      return {
        code,
        containerTypeText: String(row?.container_type_display || '-'),
        statusText: String(row?.status_zh || statusZh(row?.status) || '-'),
        lcm,
        wcm,
        hcm,
        li,
        wi,
        hi,
        kgNum,
        lbNum,
        detailRows,
        totalQty: detailRows.reduce((acc: number, cur: any) => acc + Math.max(0, Math.floor(Number(cur?.qty || 0) || 0)), 0),
        directQty,
        aggregatedQty,
        hasChildren,
        objectKinds: detailRows.length,
        recordCount: timeRows.length,
        startTs,
        endTs,
        durationHours,
      };
    }, [boxFinalCode, byContainerRows, metrics, opRows, resolveLedgerSignedQty, byHierarchyRows, byTargetRows, statusZh]);
    const boxFinalLinkedRows = useMemo(() => {
      const code = String(boxFinalCode || '').trim();
      if (!code) return [] as any[];
      const children = (Array.isArray(byHierarchyRows) ? byHierarchyRows : []).filter((ln: any) => String(ln?.parent_code || '').trim() === code);
      const rowsByCode = new Map<string, any>();
      (Array.isArray(byContainerRows) ? byContainerRows : []).forEach((r: any) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc) return;
        rowsByCode.set(cc, r);
      });
      return children.map((ln: any, idx: number) => {
        const cc = String(ln?.child_code || '').trim();
        const hit = rowsByCode.get(cc) || {};
        return {
          key: `${idx}-${cc}`,
          child_code: cc || '-',
          child_status_zh: String(ln?.child_status_zh || hit?.status_zh || '-'),
          qty: Math.max(0, Math.floor(Number(hit?.direct_qty ?? hit?.qty ?? 0) || 0)),
        };
      });
    }, [boxFinalCode, byHierarchyRows, byContainerRows]);

    return (
      <div style={{ lineHeight: 1.7 }}>
        <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #91caff', borderRadius: 8, background: '#f0f5ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600, color: '#0958d9' }}>处理结果与提交摘要</div>
            <span style={{ ...processBadgeStyle, borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
              {processText}
            </span>
          </div>
          {processRaw === 'unable' ? (
            <div style={{ marginTop: 6 }}>
              无法操作原因：<span style={{ color: '#cf1322' }}>{String(process?.unable_reason || '-')}</span>
            </div>
          ) : null}
          <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span>{`操作记录：${Number(stats?.operation_records_count || opRows.length || 0)}条`}</span>
            <span style={{ color: '#d9d9d9' }}>|</span>
            <span>{`容器数：父容器${parentContainerCount} 子容器${childContainerCount} 独立容器${independentContainerCount}`}</span>
            <span style={{ color: '#d9d9d9' }}>|</span>
            <span>{`共生成容器编码${generatedContainerCount}(含作废容器编码${cancelledContainerCount})`}</span>
          </div>
          {showWarehouseWaybillLine ? (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontWeight: 600, color: '#595959', marginBottom: 4 }}>仓库提供运单信息</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <span>{`物流提供商：${waybillCarrier || '-'}`}</span>
                <span style={{ color: '#d9d9d9' }}>|</span>
                <span>{`跟踪号：${waybillTrackingNo || '-'}`}</span>
                <span style={{ color: '#d9d9d9' }}>|</span>
                <span>{`费用：${waybillChargeDisplay}`}</span>
                <span style={{ color: '#d9d9d9' }}>|</span>
                <span>{`费用备注：${waybillChargeNote || '-'}`}</span>
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: 8, background: '#f6ffed' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontWeight: 600, color: '#389e0d', marginBottom: 6 }}>
            <span>操作对象与包装进度（三表快照）</span>
            <span style={{ display: 'inline-block', padding: '1px 6px', border: (overviewHeaderStats.rateValue != null && overviewHeaderStats.rateValue >= 100) ? '1px solid #b7eb8f' : '1px solid #ffadd2', background: (overviewHeaderStats.rateValue != null && overviewHeaderStats.rateValue >= 100) ? '#f6ffed' : '#fff0f6', color: (overviewHeaderStats.rateValue != null && overviewHeaderStats.rateValue >= 100) ? '#389e0d' : '#c41d7f', borderRadius: 4, fontWeight: 700, fontSize: 12, lineHeight: '18px' }}>
              包装完成率 {overviewHeaderStats.rateText}
            </span>
            <span style={{ color: '#d9d9d9', fontWeight: 400 }}>|</span>
            <Tooltip title="口径：状态非进行中且非已作废的容器数量；允许直接装载件数为0的父容器计入。">
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>已完成容器 {overviewHeaderStats.closedCount}</span>
            </Tooltip>
            <Tooltip title="口径：仅统计已完成的父容器和独立容器；子容器不计入；按容器长×宽×高换算体积后汇总。">
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #87e8de', background: '#e6fffb', color: '#006d75', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>总体积 {overviewHeaderStats.volM3Text}m3</span>
            </Tooltip>
            <Tooltip title="口径：仅统计已完成的父容器和独立容器；子容器不计入；优先 weight_kg，再回退 weight_g / weight_lb 换算。">
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>总重量 {overviewHeaderStats.kgText}kg</span>
            </Tooltip>
            <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #d3adf7', background: '#f9f0ff', color: '#531dab', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>已装入容器 {overviewHeaderStats.doneQty}件</span>
            <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #ffe58f', background: '#fffbe6', color: '#ad8b00', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>目标 {overviewHeaderStats.targetQty}件</span>
          </div>
          <Tabs
            size="small"
            items={[
              {
                key: 'snap_by_target',
                label: String(progressTabs?.by_target_label || (isIocTarget ? '按操作对象统计(历史指令产出)' : '按操作对象统计(当前库存)')),
                children: (
                  <Table
                    size="small"
                    rowKey={(r: any, idx?: number) => String(r?.key || r?.output_code || r?.unit_code || `${idx || 0}`)}
                    dataSource={Array.isArray(byTargetRows) ? byTargetRows : []}
                    columns={(isIocTarget
                      ? [
                        {
                          title: '序号',
                          key: '__idx',
                          width: 72,
                          align: 'center' as const,
                          render: (_: any, __: any, idx: number) => ((Math.max(1, Number(snapByTargetPage) || 1) - 1) * (Math.max(1, Number(snapByTargetPageSize) || 10)) + idx + 1),
                        },
                        { title: '容器系统编码', dataIndex: 'output_code', key: 'output_code', width: 190, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 120 },
                        {
                          title: '内容类型',
                          dataIndex: 'output_content_kind_display',
                          key: 'output_content_kind_display',
                          width: 140,
                          render: (v: any, row: any) => {
                            const txt = String(v || '-');
                            const kind = String(row?.output_content_kind || '').trim().toLowerCase();
                            const isException = kind === 'exception' || txt.includes('异常');
                            return (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 4,
                                border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
                                background: isException ? '#fff7e6' : '#f6ffed',
                                color: isException ? '#fa8c16' : '#389e0d',
                                lineHeight: 1.5,
                              }}>
                                {txt}
                              </span>
                            );
                          },
                        },
                        { title: '容器内容统计', dataIndex: 'output_contents_summary', key: 'output_contents_summary', width: 140 },
                        {
                          title: '容器内容',
                          dataIndex: 'output_contents_detail',
                          key: 'output_contents_detail',
                          width: 220,
                          render: (v: any) => {
                            const full = String(v || '').trim();
                            if (!full) return '-';
                            const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
                            if (!lines.length) return '-';
                            const preview = lines.slice(0, 3).join('；');
                            return <span title={full}>{lines.length > 3 ? `${preview}...` : preview}</span>;
                          },
                        },
                        {
                          title: '容器尺寸',
                          key: 'dims',
                          width: 170,
                          render: (_: any, row: any) => (
                            row?.length_cm != null && row?.width_cm != null && row?.height_cm != null
                              ? `${row.length_cm}×${row.width_cm}×${row.height_cm} cm`
                              : '-'
                          ),
                        },
                        { title: '容器重量', dataIndex: 'weight_g', key: 'weight_g', width: 110, render: (v: any) => (v != null && String(v).trim() !== '' ? `${v}g` : '-') },
                        { title: '目标数量', dataIndex: 'target_qty', key: 'target_qty', width: 100, align: 'center' as const },
                        { title: '已装入容器', dataIndex: 'done_qty', key: 'done_qty', width: 140, align: 'center' as const },
                        {
                          title: '操作',
                          key: 'ops',
                          width: 120,
                          render: (_: any, row: any) => (
                            <Button
                              size="small"
                              style={{ background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }}
                              onClick={() => openTargetContainerSummary(row)}
                            >
                              查看容器
                            </Button>
                          ),
                        },
                      ]
                      : [
                        {
                          title: '序号',
                          key: '__idx',
                          width: 72,
                          align: 'center' as const,
                          render: (_: any, __: any, idx: number) => ((Math.max(1, Number(snapByTargetPage) || 1) - 1) * (Math.max(1, Number(snapByTargetPageSize) || 10)) + idx + 1),
                        },
                        { title: '库存单元类型', dataIndex: 'unit_type', key: 'unit_type', width: 150 },
                        { title: '库存单元系统编码', dataIndex: 'unit_code', key: 'unit_code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        {
                          title: '内容物',
                          dataIndex: 'content_lines',
                          key: 'content_lines',
                          width: 300,
                          render: (v: any) => {
                            const lines = Array.isArray(v) ? v : [];
                            if (!lines.length) return '-';
                            const preview = lines.slice(0, 3).join('；');
                            return <span title={lines.join('；')}>{lines.length > 3 ? `${preview}...` : preview}</span>;
                          },
                        },
                        { title: '操作全部库存', dataIndex: 'use_all_available', key: 'use_all_available', width: 130, align: 'center' as const, render: (v: any) => (v ? '是' : '否') },
                        { title: '目标数量', dataIndex: 'target_qty', key: 'target_qty', width: 100, align: 'center' as const },
                        { title: '已装入容器', dataIndex: 'picked_qty', key: 'picked_qty', width: 140, align: 'center' as const },
                        {
                          title: '操作',
                          key: 'ops',
                          width: 120,
                          render: (_: any, row: any) => (
                            <Button
                              size="small"
                              style={{ background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }}
                              onClick={() => openTargetContainerSummary(row)}
                            >
                              查看容器
                            </Button>
                          ),
                        },
                      ]) as any}
                    pagination={{
                      current: snapByTargetPage,
                      pageSize: snapByTargetPageSize,
                      total: (Array.isArray(byTargetRows) ? byTargetRows.length : 0),
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions: ['10', '20', '50'] as any,
                      showTotal: (t: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
                      onChange: (p: number, ps: number) => {
                        if (ps !== snapByTargetPageSize) {
                          setSnapByTargetPage(1);
                          setSnapByTargetPageSize(ps);
                          return;
                        }
                        setSnapByTargetPage(p);
                      },
                    }}
                    scroll={{ x: isIocTarget ? 1500 : 1100 }}
                    locale={{ emptyText: '暂无操作对象统计数据' }}
                  />
                ),
              },
              {
                key: 'snap_by_container',
                label: '按容器统计(含调整池)',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <style>{`
                      .regular-outbound-snapshot-by-container .regular-outbound-snapshot-cancelled-row > td {
                        background: #d9d9d9 !important;
                        color: #8c8c8c !important;
                      }
                      .regular-outbound-snapshot-by-container .regular-outbound-snapshot-cancelled-row:hover > td {
                        background: #d9d9d9 !important;
                      }
                    `}</style>
                    <div>
                      <div style={{ marginBottom: 6, color: '#595959', fontWeight: 600 }}>可用容器</div>
                      <Table
                        className="regular-outbound-snapshot-by-container"
                        size="small"
                        rowKey={(r: any, idx?: number) => String(r?.container_code || r?.code || idx || 0)}
                        dataSource={activeContainerRows}
                        rowClassName={(record: any) => String(record?.status || '').trim().toLowerCase() === 'cancelled' ? 'regular-outbound-snapshot-cancelled-row' : ''}
                        columns={[
                          {
                            title: '序号',
                            key: '__idx',
                            width: 72,
                            align: 'center' as const,
                            render: (_: any, __: any, idx: number) => ((Math.max(1, Number(snapByContainerActivePage) || 1) - 1) * (Math.max(1, Number(snapByContainerActivePageSize) || 10)) + idx + 1),
                          },
                          { title: '容器系统编码', dataIndex: 'container_code', key: 'container_code', width: 220, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                          { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 110, render: (v: any) => String(v || '-') },
                          { title: '状态', dataIndex: 'status_zh', key: 'status_zh', width: 100, render: (v: any, row: any) => <Tag color={String(row?.status || '').trim().toLowerCase() === 'cancelled' ? 'default' : (String(row?.status || '').trim().toLowerCase() === 'open' ? 'blue' : 'green')}>{String(v || statusZh(row?.status) || '-')}</Tag> },
                          {
                            title: '容器层级',
                            dataIndex: 'hierarchy_level',
                            key: 'hierarchy_level',
                            width: 110,
                            render: (v: any) => {
                              const lv = String(v || '').trim();
                              if (lv === '子') return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700 }}>子</Tag>;
                              if (lv === '父') return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}>父</Tag>;
                              if (lv === '独立') return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}>独立</Tag>;
                              return '-';
                            },
                          },
                          { title: '记录数', dataIndex: 'ops', key: 'ops', width: 90, align: 'center' as const },
                          { title: '操作对象种类', dataIndex: 'objectKinds', key: 'objectKinds', width: 120, align: 'center' as const },
                          {
                            title: '直接装载件数',
                            dataIndex: 'qty',
                            key: 'qty',
                            width: 90,
                            align: 'center' as const,
                            render: (v: any, row: any) => {
                              const direct = Number(row?.direct_qty ?? row?.qty ?? v ?? 0) || 0;
                              return Math.max(0, Math.floor(direct));
                            },
                          },
                          {
                            title: '含子容器汇总件数',
                            key: 'aggregated_qty',
                            width: 150,
                            align: 'center' as const,
                            render: (_: any, row: any) => {
                              const isParent = String(row?.hierarchy_level || '').trim() === '父';
                              if (!isParent) return '-';
                              const direct = Math.max(0, Math.floor(Number(row?.direct_qty ?? row?.qty ?? 0) || 0));
                              const aggregated = Math.max(0, Math.floor(Number(row?.aggregated_qty ?? row?.qty ?? 0) || 0));
                              if (aggregated <= direct) return aggregated;
                              return (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    minWidth: 26,
                                    textAlign: 'center',
                                    padding: '0 8px',
                                    borderRadius: 6,
                                    border: '1px solid #91caff',
                                    background: '#e6f4ff',
                                    color: '#0958d9',
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                  }}
                                  title={`直接装载:${direct}，含子容器汇总:${aggregated}`}
                                >
                                  {aggregated}
                                </span>
                              );
                            },
                          },
                          { title: '重量(kg | Lb)', dataIndex: 'weight_display', key: 'weight_display', width: 150 },
                          { title: '尺寸(cm | inch)', dataIndex: 'dims_display', key: 'dims_display', width: 220 },
                          {
                            title: '操作',
                            key: 'ops_view',
                            width: 140,
                            render: (_: any, row: any) => (
                              <Button
                                size="small"
                                style={{ background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }}
                                onClick={() => {
                                  const code = String(row?.container_code || '').trim();
                                  if (!code) return;
                                  setBoxFinalCode(code);
                                  setBoxFinalOpen(true);
                                }}
                              >
                                最终装箱数据
                              </Button>
                            ),
                          },
                        ] as any}
                        pagination={{
                          current: snapByContainerActivePage,
                          pageSize: snapByContainerActivePageSize,
                          total: activeContainerRows.length,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          pageSizeOptions: ['10', '20', '50'] as any,
                          showTotal: (t:number, range:[number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
                          onChange: (p: number, ps: number) => {
                            if (ps !== snapByContainerActivePageSize) {
                              setSnapByContainerActivePage(1);
                              setSnapByContainerActivePageSize(ps);
                              return;
                            }
                            setSnapByContainerActivePage(p);
                          },
                        }}
                        scroll={{ x: 'max-content' as any }}
                        locale={{ emptyText: '暂无可用容器数据' }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 6, color: '#8c8c8c', fontWeight: 600 }}>已作废容器（集中展示）</div>
                      <Table
                        className="regular-outbound-snapshot-by-container"
                        size="small"
                        rowKey={(r: any, idx?: number) => String(r?.container_code || r?.code || idx || 0)}
                        dataSource={cancelledOnlyRows}
                        rowClassName={() => 'regular-outbound-snapshot-cancelled-row'}
                        columns={[
                          {
                            title: '序号',
                            key: '__idx',
                            width: 72,
                            align: 'center' as const,
                              render: (_: any, __: any, idx: number) => ((Math.max(1, Number(snapByContainerCancelledPage) || 1) - 1) * (Math.max(1, Number(snapByContainerCancelledPageSize) || 10)) + idx + 1),
                          },
                          { title: '容器系统编码', dataIndex: 'container_code', key: 'container_code', width: 220, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                          { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 110, render: () => '-' },
                          { title: '状态', dataIndex: 'status_zh', key: 'status_zh', width: 100, render: () => <Tag>已作废</Tag> },
                          { title: '容器层级', dataIndex: 'hierarchy_level', key: 'hierarchy_level', width: 110, render: () => '-' },
                          { title: '记录数', dataIndex: 'ops', key: 'ops', width: 90, align: 'center' as const },
                          { title: '操作对象种类', dataIndex: 'objectKinds', key: 'objectKinds', width: 120, align: 'center' as const },
                          {
                            title: '直接装载件数',
                            dataIndex: 'qty',
                            key: 'qty',
                            width: 90,
                            align: 'center' as const,
                            render: (v: any, row: any) => {
                              const direct = Number(row?.direct_qty ?? row?.qty ?? v ?? 0) || 0;
                              return Math.max(0, Math.floor(direct));
                            },
                          },
                          {
                            title: '含子容器汇总件数',
                            key: 'aggregated_qty',
                            width: 150,
                            align: 'center' as const,
                            render: () => '-',
                          },
                          { title: '重量(kg | Lb)', dataIndex: 'weight_display', key: 'weight_display', width: 150, render: () => '-' },
                          { title: '尺寸(cm | inch)', dataIndex: 'dims_display', key: 'dims_display', width: 220, render: () => '-' },
                          {
                            title: '操作',
                            key: 'ops_view',
                            width: 140,
                            render: (_: any, row: any) => (
                              <Button
                                size="small"
                                style={{ background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }}
                                onClick={() => {
                                  const code = String(row?.container_code || '').trim();
                                  if (!code) return;
                                  setBoxFinalCode(code);
                                  setBoxFinalOpen(true);
                                }}
                              >
                                最终装箱数据
                              </Button>
                            ),
                          },
                        ] as any}
                        pagination={{
                          current: snapByContainerCancelledPage,
                          pageSize: snapByContainerCancelledPageSize,
                          total: cancelledOnlyRows.length,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          pageSizeOptions: ['10', '20', '50'] as any,
                          showTotal: (t:number, range:[number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
                          onChange: (p: number, ps: number) => {
                            if (ps !== snapByContainerCancelledPageSize) {
                              setSnapByContainerCancelledPage(1);
                              setSnapByContainerCancelledPageSize(ps);
                              return;
                            }
                            setSnapByContainerCancelledPage(p);
                          },
                        }}
                        scroll={{ x: 'max-content' as any }}
                        locale={{ emptyText: '暂无已作废容器' }}
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: 'snap_by_hierarchy',
                label: '容器层级展示',
                children: (
                  <Table
                    size="small"
                    rowKey={(r: any, idx?: number) => String(r?.key || idx || 0)}
                    dataSource={Array.isArray(byHierarchyRows) ? byHierarchyRows : []}
                    onRow={(record: any) => {
                      const g = Number(record?.group_idx || 0);
                      return { style: { background: g % 2 === 0 ? '#f6ffed' : '#e6f7ff' } } as any;
                    }}
                    columns={[
                      { title: '父容器编码', dataIndex: 'parent_code', key: 'parent_code', width: 220, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '父容器状态', dataIndex: 'parent_status_zh', key: 'parent_status_zh', width: 120 },
                      { title: '子容器编码', dataIndex: 'child_code', key: 'child_code', width: 220, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '子容器状态', dataIndex: 'child_status_zh', key: 'child_status_zh', width: 120 },
                    ] as any}
                    pagination={{
                      current: snapByHierarchyPage,
                      pageSize: snapByHierarchyPageSize,
                      total: (Array.isArray(byHierarchyRows) ? byHierarchyRows.length : 0),
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions: ['10', '20', '50'] as any,
                      showTotal: (t:number, range:[number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
                      onChange: (p: number, ps: number) => {
                        if (ps !== snapByHierarchyPageSize) {
                          setSnapByHierarchyPage(1);
                          setSnapByHierarchyPageSize(ps);
                          return;
                        }
                        setSnapByHierarchyPage(p);
                      },
                    }}
                    locale={{ emptyText: '暂无容器层级数据' }}
                  />
                ),
              },
            ]}
          />
        </div>

        <div style={{ padding: '10px 12px', border: '1px solid #ffd591', borderRadius: 8, background: '#fff7e6' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontWeight: 600, color: '#d46b08', marginBottom: 6 }}>
            <span>{`操作记录（共 ${Number(opRows.length || 0)} 条）`}</span>
            <span style={{ color: '#d9d9d9' }}>|</span>
            <span>{`最早：${operationTimeStats.earliestTs > 0 ? formatDateTimeText(operationTimeStats.earliestTs) : '-'}`}</span>
            <span>{`最晚：${operationTimeStats.latestTs > 0 ? formatDateTimeText(operationTimeStats.latestTs) : '-'}`}</span>
            <span>{`总耗时：${operationTimeStats.durationText}`}</span>
          </div>
          <Table
            size="small"
            rowKey={(r: any, idx?: number) => String(r?.id || r?.ts || idx || 0)}
            dataSource={(Array.isArray(opRows) ? opRows : []).map((r: any, idx: number) => ({
              key: `${idx}-${String(r?.ts || '')}`,
              raw_time: (r?.raw_time ?? r?.time ?? r?.ts ?? ''),
              action: String(r?.action || r?.event || '-'),
              container: String(r?.container_code || '-'),
              qty: Number(r?.qty ?? r?.quantity ?? 0) || 0,
              remark: String(r?.remark || '-'),
            }))}
            columns={[
              { title: '时间', dataIndex: 'raw_time', key: 'raw_time', width: 180, render: (v: any) => formatDateTimeCell(v) },
              { title: '操作', dataIndex: 'action', key: 'action', width: 160 },
              { title: '容器', dataIndex: 'container', key: 'container', width: 180, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
              { title: '数量', dataIndex: 'qty', key: 'qty', width: 100 },
              { title: '备注', dataIndex: 'remark', key: 'remark' },
            ] as any}
            pagination={{
              current: opPage,
              pageSize: opPageSize,
              total: opRows.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'] as any,
              onChange: (p:number, ps:number) => { setOpPage(p); setOpPageSize(ps); },
              showTotal: (t:number, range:[number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
            }}
            locale={{ emptyText: '暂无操作记录可预览' }}
          />
        </div>
        <Modal
          open={targetContainerOpen}
          title={(
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 36 }}>
              <span>{targetContainerTitle || '装箱汇总'}</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '18px', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
                  涉及容器 {targetContainerStats.boxCount}
                </span>
                <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', borderRadius: 4, fontWeight: 700 }}>
                  已装入容器 {targetContainerStats.totalQty}件
                </span>
              </div>
            </div>
          )}
          onCancel={() => { setTargetContainerOpen(false); setTargetContainerRows([]); setTargetContainerTitle(''); }}
          footer={<Button onClick={() => { setTargetContainerOpen(false); setTargetContainerRows([]); setTargetContainerTitle(''); }}>关闭</Button>}
          width={920}
          destroyOnClose
        >
          <Table
            size="small"
            rowKey="key"
            dataSource={targetContainerRows}
            pagination={{
              current: targetContainerPage,
              pageSize: targetContainerPageSize,
              total: targetContainerRows.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'] as any,
              showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
              onChange: (p: number, ps: number) => { setTargetContainerPage(p); setTargetContainerPageSize(ps); },
            }}
            columns={[
              { title: '序号', key: '__idx', width: 72, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(targetContainerPage) || 1) - 1) * (Math.max(1, Number(targetContainerPageSize) || 10)) + idx + 1) },
              { title: '容器系统编码', dataIndex: 'container_code', key: 'container_code', width: 260, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
              { title: '数量合计', dataIndex: 'qty', key: 'qty', width: 120, render: (v: any) => <span style={{ fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
              { title: '记录条数', dataIndex: 'cnt', key: 'cnt', width: 110, render: (v: any) => <span style={{ color: '#595959' }}>{Math.floor(Number(v || 0) || 0)}</span> },
              { title: '最近时间', dataIndex: 'lastTs', key: 'lastTs', width: 180, render: (v: any) => (v ? new Date(Number(v)).toLocaleString() : '-') },
              {
                title: '操作',
                key: 'ops',
                width: 120,
                render: (_: any, row: any) => (
                  <Button
                    size="small"
                    onClick={() => openContainerDetail(String(row?.container_code || '').trim())}
                  >
                    查看明细
                  </Button>
                ),
              },
            ] as any}
            scroll={{ x: 980 }}
            locale={{ emptyText: '暂无关联容器数据' }}
          />
        </Modal>
        <Modal
          open={containerDetailOpen}
          title={(
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 36 }}>
              <span>容器 {containerDetailCode || '-'} 记录明细</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '18px', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
                  记录数 {containerDetailHeaderStats.recordCount}
                </span>
              </div>
            </div>
          )}
          onCancel={() => { setContainerDetailOpen(false); setContainerDetailCode(''); setContainerDetailRows([]); }}
          footer={<Button onClick={() => { setContainerDetailOpen(false); setContainerDetailCode(''); setContainerDetailRows([]); }}>关闭</Button>}
          width={1040}
          destroyOnClose
        >
          <Table
            size="small"
            rowKey={(r: any) => String(r?.key || '')}
            dataSource={containerDetailRows}
            pagination={{
              current: containerDetailPage,
              pageSize: containerDetailPageSize,
              total: containerDetailRows.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'] as any,
              showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
              onChange: (p: number, ps: number) => { setContainerDetailPage(p); setContainerDetailPageSize(ps); },
            }}
            columns={[
              {
                title: '序号',
                width: 72,
                align: 'center' as const,
                render: (_: any, __: any, idx: number) => ((Math.max(1, Number(containerDetailPage) || 1) - 1) * (Math.max(1, Number(containerDetailPageSize) || 10)) + idx + 1),
              },
              { title: '事件', dataIndex: 'event', key: 'event', width: 160 },
              { title: '容器系统编码', dataIndex: 'container_code', key: 'container_code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
              { title: '操作对象', dataIndex: 'target', key: 'target', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
              { title: '数量', dataIndex: 'qty', key: 'qty', width: 100, align: 'center' as const },
              { title: '时间', dataIndex: 'time', key: 'time', width: 180, render: (v: any) => formatDateTimeText(v) },
            ] as any}
            scroll={{ x: 980 }}
            locale={{ emptyText: '暂无记录' }}
          />
        </Modal>
        <Modal
          open={boxFinalOpen}
          title={(
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, width: '100%', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>最终包装数据</span>
                {boxFinalData?.code ? (
                  <>
                    <span style={{ color: '#d9d9d9' }}>·</span>
                    <span style={{ color: '#fa541c', fontWeight: 800 }}>{boxFinalData.code}</span>
                  </>
                ) : null}
              </span>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6fffb', borderColor: '#87e8de', color: '#006d75' }}>
                <span>直接装载{Number(boxFinalData?.directQty || 0)}件</span>
              </Tag>
              {boxFinalData?.hasChildren ? (
                <Tag
                  style={{
                    marginInlineEnd: 0,
                    borderRadius: 999,
                    background: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#e6f4ff' : '#fafafa',
                    borderColor: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#91caff' : '#d9d9d9',
                    color: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#0958d9' : '#595959',
                  }}
                >
                  <span>含子容器汇总{Number(boxFinalData?.aggregatedQty || 0)}件</span>
                </Tag>
              ) : null}
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f9f0ff', borderColor: '#d3adf7', color: '#531dab' }}>
                <span>操作对象种类{Number(boxFinalData?.objectKinds || 0)}</span>
              </Tag>
            </div>
          )}
          onCancel={() => setBoxFinalOpen(false)}
          footer={<Button onClick={() => setBoxFinalOpen(false)}>关闭</Button>}
          width={980}
          destroyOnClose
        >
          {boxFinalData ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Descriptions size="small" bordered column={2}>
                <Descriptions.Item label="容器系统编码">
                  <span style={{ fontFamily: 'monospace' }}>{boxFinalData.code || '-'}</span>
                </Descriptions.Item>
                <Descriptions.Item label="容器类型">{boxFinalData.containerTypeText || '-'}</Descriptions.Item>
                <Descriptions.Item label="状态">{boxFinalData.statusText || '-'}</Descriptions.Item>
                <Descriptions.Item label="记录数">{Math.max(0, Math.floor(Number(boxFinalData.recordCount || 0) || 0))}</Descriptions.Item>
                <Descriptions.Item label="开始时间">{boxFinalData.startTs ? formatDateTimeText(boxFinalData.startTs) : '-'}</Descriptions.Item>
                <Descriptions.Item label="结束时间">{boxFinalData.endTs ? formatDateTimeText(boxFinalData.endTs) : '-'}</Descriptions.Item>
                <Descriptions.Item label="总耗时">
                  {Number(boxFinalData.durationHours || 0) > 0 ? `${Number(boxFinalData.durationHours).toFixed(1)}小时` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="统计">{`操作对象种类 ${boxFinalData.objectKinds} | 直接装载 ${boxFinalData.directQty}${boxFinalData.hasChildren ? ` | 含子容器汇总 ${boxFinalData.aggregatedQty}` : ''}`}</Descriptions.Item>
                <Descriptions.Item label="尺寸(cm | inch)">
                  {boxFinalData.lcm > 0 && boxFinalData.wcm > 0 && boxFinalData.hcm > 0
                    ? `${boxFinalData.lcm}×${boxFinalData.wcm}×${boxFinalData.hcm} cm | ${boxFinalData.li}×${boxFinalData.wi}×${boxFinalData.hi} inch`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="重量(kg | lb)">
                  {boxFinalData.kgNum > 0 ? `${boxFinalData.kgNum} kg | ${boxFinalData.lbNum} lb` : '-'}
                </Descriptions.Item>
              </Descriptions>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 420 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>容器内容物统计</div>
                  <Table
                    size="small"
                    rowKey={(r: any) => String(r?.key || r?.target || '')}
                    dataSource={boxFinalData.detailRows}
                    tableLayout="fixed"
                    pagination={{
                      current: boxFinalContentPage,
                      pageSize: boxFinalContentPageSize,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions: ['10', '20', '50'] as any,
                      onChange: (p: number, ps: number) => {
                        setBoxFinalContentPage(p);
                        setBoxFinalContentPageSize(ps);
                      },
                    }}
                    columns={[
                      {
                        title: '序号',
                        width: 72,
                        align: 'center' as const,
                        render: (_: any, __: any, idx: number) => ((Math.max(1, Number(boxFinalContentPage) || 1) - 1) * (Math.max(1, Number(boxFinalContentPageSize) || 10)) + idx + 1),
                      },
                      { title: '操作对象', dataIndex: 'target', width: 200, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      {
                        title: '内容物',
                        dataIndex: 'content_lines',
                        key: 'content_lines',
                        width: 300,
                        render: (v: any) => {
                          const lines = Array.isArray(v) ? v : [];
                          if (!lines.length) return '-';
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {lines.map((line: any, idx: number) => (
                                <span key={`${String(line || '')}-${idx}`}>{String(line || '-')}</span>
                              ))}
                            </div>
                          );
                        },
                      },
                      { title: '数量', dataIndex: 'qty', width: 100, align: 'center' as const, render: (v: any) => <span style={{ fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
                    ] as any}
                    scroll={{ x: 760 }}
                    locale={{ emptyText: '暂无内容明细' }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 360 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>{`关联子容器统计（${boxFinalLinkedRows.length}）`}</div>
                  <Table
                    size="small"
                    rowKey={(r: any) => String(r?.key || r?.child_code || '')}
                    dataSource={boxFinalLinkedRows}
                    pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true, pageSizeOptions: ['10', '20', '50'] as any }}
                    columns={[
                      { title: '子容器编码', dataIndex: 'child_code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '状态', dataIndex: 'child_status_zh', width: 110 },
                      { title: '直接装载件数', dataIndex: 'qty', width: 120, align: 'center' as const },
                    ] as any}
                    locale={{ emptyText: '暂无关联子容器' }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: '#8c8c8c' }}>暂无最终包装数据</div>
          )}
        </Modal>
      </div>
    );
  };

  const legacyByStatus = useMemo(() => {
    if (stages.length > 0) return {};
    const by: Record<string, any[]> = {};
    (workOrders || []).forEach((w:any) => {
      const s = String(w?.status || 'pending');
      if (!by[s]) by[s] = [];
      by[s].push(w);
    });
    return by;
  }, [stages, workOrders]);

  // 阶段跳转与校验规则
  const canGoNext = useMemo(() => {
    if (stages.length === 0) return false;
    const cur = stages[currentStageIdx];
    if (!cur) return false;
    // 规则：必做阶段 → 必须全部完成；全可选阶段 → 允许跳过但建议触发“跳过本阶段”
    return cur.optional || isStageCompleted(cur.steps);
  }, [stages, currentStageIdx]);

  const goPrev = () => {
    setCurrentStageIdx(idx => Math.max(0, idx - 1));
  };
  const goNext = () => {
    if (!canGoNext) {
      message.warning('请先完成本阶段必做步骤');
      return;
    }
    setCurrentStageIdx(idx => Math.min(stages.length - 1, idx + 1));
  };

  // 跳过本阶段（仅可选阶段）：将未完成步骤直接标记为 done + meta.skipped=true
  const skipCurrentStage = async () => {
    const cur = stages[currentStageIdx];
    if (!cur) return;
    if (!cur.optional) { message.warning('仅可选阶段可跳过'); return; }
    try {
      setLoading(true);
      const pendings = (cur.steps || []).filter((s:any)=> !isStepDone(s));
      await Promise.all(pendings.map(async (st:any) => {
        const nextMeta = { ...(st?.meta || {}), skipped: true };
        await request.patch(`/api/v1/instruction/exec/steps/${st.id}/`, { status: 'done', meta: nextMeta });
      }));
      message.success('本阶段已标记为跳过');
      await load();
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '跳过失败');
    } finally {
      setLoading(false);
    }
  };

  // 取消跳过：将已跳过阶段恢复为“待处理”（不做库存回滚）
  const cancelSkipCurrentStage = async () => {
    const cur = stages[currentStageIdx];
    if (!cur) return;
    Modal.confirm({
      title: '确认取消跳过？',
      content: '将把本阶段所有步骤状态重置为“待处理”，并清除跳过标记。',
      okText: '取消跳过',
      cancelText: '取消',
      onOk: async () => {
        try {
          setLoading(true);
          await Promise.all((cur.steps || []).map(async (st:any) => {
            const meta = { ...(st?.meta || {}) };
            if (meta.skipped) delete meta.skipped;
            await request.patch(`/api/v1/instruction/exec/steps/${st.id}/`, { status: 'pending', meta });
          }));
          message.success('已取消跳过，阶段已恢复为待处理');
          await load();
        } catch (e:any) {
          message.error(e?.response?.data?.detail || '取消跳过失败');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 完成本阶段：若当前阶段包含拣货，则先提交拣货记录快照与容器更新；再将未完成步骤提交为 done，并触发阶段完成日志
  const finishCurrentStage = async () => {
    const cur = stages[currentStageIdx];
    if (!cur) return;
    // 库存非标准操作指令：客户还没回复本轮结果时，结束执行阶段前再确认一次（其他指令不受影响；查询失败不阻断）
    if (
      String(instr?.template_snapshot?.meta?.engine || '') === 'nonstandard_ops'
      && (cur.steps || []).some((s: any) => String(s?.step_type || '').toUpperCase() === 'NONSTD_RECORD')
    ) {
      try {
        const summaryResp = await request.get(`/api/v1/nonstandard-ops/instructions/${instrId}/summary/`);
        if (summaryResp?.data?.waiting_customer) {
          const proceed = await new Promise<boolean>((resolve) => {
            Modal.confirm({
              title: '客户还没有回复本轮结果，确定要结束执行阶段吗？',
              content: '结束后进入收尾阶段，之后不再回到执行记录。如果后面还有需要客户确认的事项，请先不要结束。',
              okText: '仍然结束执行阶段',
              cancelText: '再看看',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!proceed) return;
        }
        // 已建但从未打印的容器标签：结束前提醒一次，防止漏打（之后只能重打，不能再新建）
        const unprintedLabels = (Array.isArray(summaryResp?.data?.labels) ? summaryResp.data.labels : [])
          // 已消耗的标签（容器已不存在）不能再打印，不算漏打
          .filter((l: any) => !l?.consumed && Number(l?.print_count || 0) === 0).length;
        if (unprintedLabels > 0) {
          const proceedLabels = await new Promise<boolean>((resolve) => {
            Modal.confirm({
              title: `还有 ${unprintedLabels} 张标签已保存但还没有打印，确定要结束执行阶段吗？`,
              content: '结束后不能再新建或修改标签，只能在清单里重打已有的标签。',
              okText: '仍然结束执行阶段',
              cancelText: '回去打印',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!proceedLabels) return;
        }
        // 执行中有直接销毁要记录件数和净重：还没有销毁记录时提醒一次（这里只提醒，"确认完成"提交前才必须回答）
        const destroyInfo = summaryResp?.data?.destroy;
        if (destroyInfo && Number(destroyInfo?.totals?.count || 0) === 0) {
          const proceedDestroy = await new Promise<boolean>((resolve) => {
            Modal.confirm({
              title: '本阶段是否有直接销毁？',
              content: '有直接销毁的产品，请先在“销毁记录”里记录件数和净重；没有就继续。下一阶段“确认完成”还可以补记。',
              okText: '没有，继续',
              cancelText: '有，回去填写',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!proceedDestroy) return;
        }
      } catch {
        // 查询失败时不阻断完成阶段
      }
    }
    // 库存非标准操作 · 确认完成：容器没有都贴好标签并记录位置时不能提交（后端提交步骤时还会再校验一次）
    if (
      String(instr?.template_snapshot?.meta?.engine || '') === 'nonstandard_ops'
      && (cur.steps || []).some((s: any) => String(s?.step_type || '').toUpperCase() === 'NONSTD_SUBMIT')
    ) {
      try {
        const confirmResp = await request.get(`/api/v1/nonstandard-ops/instructions/${instrId}/summary/`);
        const pending = Number(confirmResp?.data?.confirm?.containers?.pending || 0);
        if (pending > 0) {
          message.warning(`还有 ${pending} 个容器没有处理完：请逐个标记“已贴标签”并记录放置位置（标签打错、实际没有的容器请标记为“未使用”并写明原因），处理完才能提交本阶段。`, 6);
          return;
        }
        if (confirmResp?.data?.destroy && !confirmResp.data.destroy.answered) {
          message.warning('还没有确认销毁情况：有直接销毁请添加销毁记录，没有请勾选“本指令没有发生销毁”，才能提交本阶段。', 6);
          return;
        }
      } catch {
        // 查询失败时交给后端校验
      }
    }
    const isLastStage = currentStageIdx >= (stages.length - 1);
    const wrapUpGate = getWrapUpPutAwayGate(cur);
    if (wrapUpGate.hasWrapUp && wrapUpGate.blocked) {
      message.warning('所有指令产出容器和仓储容器必须放入仓位,才能启用此按钮.');
      return;
    }
    // 最小修复：常规出库“处理结果必填”校验前置到 loading 之前，避免校验失败触发页面重挂载清空本地运单信息。
    {
      const isRelabelTypePre = (t: any) => String(t || '').trim().toUpperCase() === 'RELABEL';
      const isPackTypePre = (t: any) => {
        const k = String(t || '').trim().toUpperCase();
        return k === 'BOX/BAG_PACK' || k === 'BOX_PACK' || k === 'BAG_PACK' || k === 'BOX' || k === 'BAG';
      };
      const tplNameStagePre = String(
        instr?.template_detail?.name
        || instr?.template_snapshot?.name
        || '',
      ).trim().toLowerCase();
      const tplCodeStagePre = String(
        instr?.template_detail?.system_code
        || instr?.template_snapshot?.system_code
        || instr?.template_detail?.code
        || instr?.template_snapshot?.code
        || '',
      ).trim().toLowerCase();
      const isRegularOutboundTemplateForStagePre = (
        tplNameStagePre.includes('常规出库')
        || (tplCodeStagePre.includes('regular') && tplCodeStagePre.includes('outbound'))
      );
      const hasRegularOutboundPackPre = (() => {
        try {
          const steps = Array.isArray(cur?.steps) ? cur.steps : [];
          const hasRelabel = steps.some((s: any) => isRelabelTypePre(s?.step_type || s?.renderer_key));
          const hasPack = steps.some((s: any) => isPackTypePre(s?.step_type || s?.renderer_key));
          return hasPack && !hasRelabel && isRegularOutboundTemplateForStagePre;
        } catch {
          return false;
        }
      })();
      if (hasRegularOutboundPackPre) {
        if (!taskId) { message.error('提交失败：缺少任务ID'); return; }
        const stageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx;
        const packStep = (cur.steps || []).find((s: any) => isPackTypePre(s?.step_type || s?.renderer_key));
        const sid = String(packStep?.id ?? '');
        const mdDraft = (sid && metricsDraft[sid] && typeof metricsDraft[sid] === 'object') ? (metricsDraft[sid] || {}) : {};
        const mdCollected = (packStep?.collected_metrics && typeof packStep.collected_metrics === 'object') ? (packStep.collected_metrics || {}) : {};
        let mdServerDraft: any = {};
        try {
          const stepId = Number(packStep?.id || 0);
          if (stepId > 0) {
            const dResp = await request.get(`/api/v1/instruction/exec/steps/${stepId}/draft/`, { params: { t: Date.now() } as any });
            const d = dResp?.data?.draft;
            if (d && String(d?.kind || '').trim() === 'regular_outbound_pack' && d?.state && typeof d.state === 'object') {
              mdServerDraft = d.state || {};
            }
          }
        } catch {
          mdServerDraft = {};
        }
        let mdLocal: any = {};
        const draftIdentityCode = String((instr?.system_code || 'UNKNOWN')).trim().replace(/[^\w-]+/g, '_');
        const regularDraftKey = `REGULAR_OUTBOUND_PACK_${Number(instrId || taskId || 0) || 0}_${stageNo}_${draftIdentityCode || 'UNKNOWN'}`;
        try {
          const raw = localStorage.getItem(regularDraftKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') {
              const mapped: any = { ...parsed };
              if (!Array.isArray(mapped?.operation_records) && Array.isArray(parsed?.local_operation_records)) {
                mapped.operation_records = parsed.local_operation_records;
              }
              if (!String(mapped?.process_result_status || '').trim() && String(parsed?.process_result_status || '').trim()) {
                mapped.process_result_status = String(parsed.process_result_status || '').trim();
              }
              if (!String(mapped?.process_unable_reason || '').trim() && String(parsed?.process_unable_reason || '').trim()) {
                mapped.process_unable_reason = String(parsed.process_unable_reason || '').trim();
              }
              if (!Array.isArray(mapped?.boxes) && Array.isArray(parsed?.boxes)) {
                mapped.boxes = parsed.boxes;
              }
              if (!Array.isArray(mapped?.hierarchy_links) && Array.isArray(parsed?.hierarchy_links)) {
                mapped.hierarchy_links = parsed.hierarchy_links;
              }
              mdLocal = mapped;
            }
          }
        } catch {
          mdLocal = {};
        }
        const md = { ...mdCollected, ...mdLocal, ...mdServerDraft, ...mdDraft };
        const processResultRaw = String(md?.process_result_status || '').trim().toLowerCase();
        if (!(processResultRaw === 'completed' || processResultRaw === 'unable')) {
          Modal.warning({
            title: '提交前校验未通过',
            content: '请先标记本指令的处理结果（已完成或无法操作）后再提交。',
            okText: '我知道了',
          });
          return;
        }
      }
    }
    let pendingSortCountSubmission: any = null;
    {
      const sortCountStep = (Array.isArray(cur?.steps) ? cur.steps : []).find((s: any) => {
        const key = String(s?.renderer_key || s?.step_type || '').trim().toUpperCase();
        return key === 'SORT_AND_COUNT' || key === 'SORT_COUNT';
      });
      if (sortCountStep) {
        if (!taskId) { message.error('提交失败：缺少任务ID'); return; }
        const sid = String(sortCountStep?.id ?? '');
        const mdDraft = (sid && metricsDraft[sid] && typeof metricsDraft[sid] === 'object') ? (metricsDraft[sid] || {}) : {};
        const mdCollected = (sortCountStep?.collected_metrics && typeof sortCountStep.collected_metrics === 'object') ? sortCountStep.collected_metrics : {};
        const md = { ...mdCollected, ...mdDraft };
        const retentionTotalQty = Math.max(0, Number(md?.sort_count_location_retention_total_qty || 0));
        const retentionLocationCount = Math.max(0, Number(md?.sort_count_location_retention_location_count || 0));
        if (retentionTotalQty > 0 || retentionLocationCount > 0 || !!md?.sort_count_has_location_retention) {
          Modal.warning({
            title: '提交前校验未通过',
            width: 760,
            okText: '我知道了',
            content: `仓位当前留存中仍有已分拣商品未放入仓储容器（${retentionLocationCount}个仓位 ${retentionTotalQty}件），全部放入容器后才能提交完成本阶段。`,
          });
          return;
        }
        const snapshotDraft = (md?.sort_count_snapshot_draft && typeof md.sort_count_snapshot_draft === 'object')
          ? md.sort_count_snapshot_draft
          : null;
        if (!snapshotDraft) {
          message.error('提交失败：缺少分拣清点阶段快照草稿，请刷新页面后重试');
          return;
        }
        const stageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx;
        const submissionEnvelope = {
          schema_version: 1,
          kind: 'sort_count',
          stats: snapshotDraft?.stats || {},
          snapshot: snapshotDraft,
        };
        const calcSortCountPendingDiffMetrics = (() => {
          const extractTargetItems = (row: any): Array<{ qty: number }> => {
            try {
              const snap = row?.target?.snapshot || row?.snapshot || {};
              const fromInventoryContentItems = Array.isArray(row?.inventory_content_items) ? row.inventory_content_items : [];
              if (fromInventoryContentItems.length > 0) {
                return fromInventoryContentItems.map((it: any) => ({ qty: Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0 }));
              }
              const candidates: any[] = [
                snap?.content?.items,
                row?.content?.items,
                row?.target?.content?.items,
                row?.items,
              ].filter(Boolean);
              for (const cand of candidates) {
                const arr = Array.isArray(cand) ? cand : [];
                if (arr.length > 0) {
                  return arr.map((it: any) => ({ qty: Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0 }));
                }
              }
            } catch {}
            return [];
          };
          const getTargetUnitType = (row: any): string => {
            const snap = row?.target?.snapshot || row?.snapshot || {};
            const direct = String(
              row?.unit_type
              || row?.unit_type_display
              || snap?.unit_type
              || snap?.unit_type_display
              || '',
            ).trim().toUpperCase();
            if (direct === 'BIU' || direct.includes('箱库存单元')) return 'BIU';
            if (direct === 'LIU' || direct.includes('散货库存单元')) return 'LIU';
            if (direct === 'PIU' || direct.includes('托盘库存单元')) return 'PIU';
            const iuCode = String(
              row?.inventory_unit_system_barcode
              || row?.inventory_unit_code
              || row?.target_code
              || snap?.system_barcode
              || snap?.system_code
              || '',
            ).trim().toUpperCase();
            if (iuCode.startsWith('BIU')) return 'BIU';
            if (iuCode.startsWith('LIU')) return 'LIU';
            if (iuCode.startsWith('PIU')) return 'PIU';
            return '';
          };
          const calcEstimatedTargetQty = (row: any): number | null => {
            if (String(snapshotDraft?.instruction_context?.demand_mode || '').trim() === 'need1' && getTargetUnitType(row) === 'BIU') {
              const v = Number(row?.expected_qty ?? row?.target_qty ?? 0);
              if (!Number.isFinite(v)) return 0;
              return Math.max(0, Math.floor(v));
            }
            const unitQty = Math.max(0, Number(row?.expected_qty ?? row?.target_qty ?? 0) || 0);
            const items = extractTargetItems(row);
            if (!Array.isArray(items) || items.length <= 0) return null;
            const perUnitQty = items.reduce((acc: number, it: any) => {
              const q = Number(it?.qty ?? 0);
              if (!Number.isFinite(q) || q <= 0) return acc;
              return acc + q;
            }, 0);
            return Math.max(0, Math.floor(perUnitQty * unitQty));
          };
          const sections = (snapshotDraft?.sections || {}) as any;
          const targets = Array.isArray(sections?.operation_targets?.rows) ? sections.operation_targets.rows : [];
          let estimated = 0;
          let missing = false;
          (Array.isArray(targets) ? targets : []).forEach((row: any) => {
            const qty = calcEstimatedTargetQty(row);
            if (qty == null) {
              missing = true;
              return;
            }
            estimated += qty;
          });
          if (missing) return null;
          const estimatedTotal = Math.max(0, Math.floor(estimated));
          if (!(estimatedTotal > 0)) return null;
          const sortedTotal = Math.max(0, Number(submissionEnvelope?.stats?.sorted_total_qty || 0) || 0);
          const diff = Math.floor(estimatedTotal - sortedTotal);
          const absDiff = Math.abs(diff);
          const ratio = absDiff / estimatedTotal;
          return {
            estimatedTotal,
            sortedTotal,
            diff,
            absDiff,
            ratio,
            ratioPctText: (ratio * 100).toFixed(1),
          };
        })();
        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: (
              <span>
                确认提交分拣清点记录并完成阶段 - 指令：
                <span style={{ color: '#1677ff' }}>{String((instr?.system_code || instrId))}</span>
              </span>
            ),
            width: 1120,
            icon: null,
            okText: '提交并完成',
            cancelText: '取消',
            content: (
              <div style={{ lineHeight: 1.7 }}>
                <div style={{ color: '#595959', marginBottom: 8 }}>
                  将先固化本阶段快照（用于跨设备回看/审计），再提交并完成当前阶段。
                </div>
                <SortCountSnapshotBlock snapshotEnvelope={submissionEnvelope} />
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
        if (calcSortCountPendingDiffMetrics && calcSortCountPendingDiffMetrics.ratio >= 0.05) {
          const m = calcSortCountPendingDiffMetrics;
          const confirmedLargeDiff = await new Promise<boolean>((resolve) => {
            Modal.confirm({
              title: '差额较大，确认继续提交？',
              okText: '确认继续提交',
              cancelText: '返回检查',
              content: (
                <div style={{ lineHeight: 1.8 }}>
                  <div>{`预估分拣目标总数量：${m.estimatedTotal}`}</div>
                  <div>{`已分拣目标总数量：${m.sortedTotal}`}</div>
                  <div>{`差额数量：${m.diff}`}</div>
                  <div>{`差额百分比：${m.ratioPctText}%`}</div>
                </div>
              ),
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!confirmedLargeDiff) return;
        }
        pendingSortCountSubmission = {
          stageNo,
          payload: {
            stage_idx: stageNo,
            idempotency_key: `task_${taskId}_stage_${stageNo}_sort_count`,
            hash: '',
            stats: submissionEnvelope.stats,
            snapshot: submissionEnvelope.snapshot,
          },
        };
      }
    }
    try {
      setLoading(true);
      if (pendingSortCountSubmission) {
        await request.post(`/api/v1/instruction/exec/tasks/${taskId}/sort-count/submissions/`, pendingSortCountSubmission.payload);
      }
      // ===== 换标+装箱：阶段快照（在“提交记录并完成”时生成）=====
      // 说明：不依赖渲染器内部 state（其草稿可能在 submitBoth 后被清空）。
      // 优先读取步骤草稿（跨设备）；草稿不存在则回退到已提交的 collected_metrics；并尽量补齐容器草稿（output-containers drafts）。
      const isRelabelType = (t: any) => String(t || '').trim().toUpperCase() === 'RELABEL';
      const isPackType = (t: any) => {
        const k = String(t || '').trim().toUpperCase();
        return k === 'BOX/BAG_PACK' || k === 'BOX_PACK' || k === 'BAG_PACK' || k === 'BOX' || k === 'BAG';
      };
      const tplNameStage = String(
        instr?.template_detail?.name
        || instr?.template_snapshot?.name
        || '',
      ).trim().toLowerCase();
      const tplCodeStage = String(
        instr?.template_detail?.system_code
        || instr?.template_snapshot?.system_code
        || instr?.template_detail?.code
        || instr?.template_snapshot?.code
        || '',
      ).trim().toLowerCase();
      const isRegularOutboundTemplateForStage = (
        tplNameStage.includes('常规出库')
        || (tplCodeStage.includes('regular') && tplCodeStage.includes('outbound'))
      );
      const relabelPackGroups = (() => {
        const map: Record<string, { group_key: string; relabelStep?: any; packStep?: any }> = {};
        (cur.steps || []).forEach((st: any) => {
          const gk = String(st?.group_key || '').trim() || '__NO_GROUP__';
          if (!map[gk]) map[gk] = { group_key: gk };
          const stType = String(st?.step_type || '').toUpperCase();
          if (isRelabelType(stType)) map[gk].relabelStep = st;
          if (isPackType(stType)) map[gk].packStep = st;
        });
        return Object.values(map).filter(x => !!x.relabelStep && !!x.packStep);
      })();

      const hasRelabelPack = relabelPackGroups.length > 0;
      if (hasRelabelPack) {
        if (!taskId) { message.error('提交失败：缺少任务ID'); setLoading(false); return; }
        const stageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx;

        // 系统生成标签（归档副本）：用于快照展示“新条码标签PDF”
        let genLabelMap: Record<string, any> = {};
        try {
          const resp = await request.get(`/api/v1/instruction/instructions/${instrId}/generated-labels/`, { params: { t: Date.now() } as any });
          const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
          const next: Record<string, any> = {};
          (Array.isArray(rows) ? rows : []).forEach((x: any) => {
            const code = String(x?.new_code || '').trim().toUpperCase();
            if (!code) return;
            next[code] = x;
          });
          genLabelMap = next;
        } catch {
          genLabelMap = {};
        }

        // 1) 尝试拉取容器草稿（用于“最终确认后的快照回看”），失败不阻断
        let outputDrafts: any[] = [];
        try {
          const resp = await request.get('/api/v1/storage/output-containers/drafts/', { params: { instruction_id: instrId } as any });
          const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
          outputDrafts = Array.isArray(list) ? list : [];
        } catch {
          outputDrafts = [];
        }

        // 2) 每个 group 读取步骤草稿（跨设备）；草稿不存在则 fallback 到 collected_metrics
        const groupSnapshots: any[] = [];
        for (const g of relabelPackGroups) {
          const gk = String(g.group_key || '');
          const relabelStep = g.relabelStep;
          const packStep = g.packStep;
          const draftStepId = (() => {
            const p = Number(packStep?.id);
            if (Number.isFinite(p) && p > 0) return p;
            const r = Number(relabelStep?.id);
            if (Number.isFinite(r) && r > 0) return r;
            return null;
          })();
          let draftState: any = null;
          let draftUpdatedAt: any = null;
          if (draftStepId) {
            try {
              const dResp = await request.get(`/api/v1/instruction/exec/steps/${draftStepId}/draft/`, { params: { t: Date.now() } as any });
              const d = dResp?.data?.draft;
              if (d && String(d?.kind || '') === 'combined_relabel_pack') {
                draftState = d?.state || null;
                draftUpdatedAt = d?.updated_at;
              }
            } catch {}
          }
          const labelCount = Number(
            (draftState && draftState.label_count != null) ? draftState.label_count :
              (relabelStep?.collected_metrics?.label_count ?? 0)
          ) || 0;
          const boxes = Array.isArray(draftState?.boxes)
            ? draftState.boxes
            : (Array.isArray(packStep?.collected_metrics?.boxes) ? packStep.collected_metrics.boxes : []);
          const records = Array.isArray(draftState?.records) ? draftState.records : [];
          const recordsPreview = Array.isArray(records) ? records.slice(0, 3000) : [];
          const closedCodes = (boxes || [])
            .filter((b:any) => String(b?.status || '') === 'closed')
            .map((b:any) => String(b?.code || '').trim())
            .filter(Boolean);
          const draftsForGroup = outputDrafts.length > 0
            ? outputDrafts.filter((d:any) => closedCodes.includes(String(d?.code || '').trim()))
            : [];

          // 3) 优先读取“总进度表快照”（由 CombinedRelabelBoxRenderer 实时暴露）
          const progressKey = `COMBINED_RELABEL_PACK_${taskId}_${stageNo}_${gk || 'G'}`;
          const progress = (() => {
            try {
              const all = (window as any).__RELABEL_PACK_PROGRESS__ || {};
              return all[progressKey] || null;
            } catch { return null; }
          })();

          groupSnapshots.push({
            group_key: gk,
            draft_step_id: draftStepId || null,
            draft_state: draftState && typeof draftState === 'object' ? { ...draftState } : null,
            label_count: labelCount,
            boxes,
            records_count: records.length,
            // 快照保留操作记录（只读回看用；防止完成阶段后主页面不再展示）
            // 注意：为了避免快照过大，做截断（与步骤草稿同口径）
            records_preview: recordsPreview,
            closed_container_codes: closedCodes,
            output_container_drafts: draftsForGroup,
            draft_updated_at: draftUpdatedAt,
            // 总进度表快照（用于阶段快照回看）
            progress_snapshot: progress,
            source: progress ? 'progress_table' : (draftState ? 'step_draft' : 'step_metrics+drafts')
          });
        }

        // 提交前校验：禁止存在“未结束(open)”容器
        const openBoxCodes = Array.from(new Set(
          groupSnapshots
            .flatMap((g: any) => (Array.isArray(g?.boxes) ? g.boxes : []))
            .map((b: any) => ({
              code: String(b?.code || '').trim(),
              status: String(b?.status || '').trim().toLowerCase(),
            }))
            .filter((x: any) => !!x.code && x.status === 'open')
            .map((x: any) => x.code),
        ));
        if (openBoxCodes.length > 0) {
          Modal.warning({
            title: '提交前校验未通过',
            width: 640,
            okText: '我知道了',
            content: (
              <div style={{ lineHeight: 1.8 }}>
                <div style={{ marginBottom: 8, color: '#595959' }}>
                  以下容器仍处于“未结束(open)”状态，请先在“换标+装入容器”标签页点击“结束新容器”关闭容器后再提交：
                </div>
                <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '6px 8px', background: '#fff' }}>
                  {openBoxCodes.map((code: string, idx: number) => (
                    <div key={`${code}-${idx}`} style={{ fontFamily: 'monospace' }}>
                      {idx + 1}. {code}
                    </div>
                  ))}
                </div>
              </div>
            ),
          });
          setLoading(false);
          return;
        }

        // 提交前校验：禁止存在“未作废空容器”（与常规出库提交流程对齐）
        const contentEvOkForRelabelPack = (ev: any) => {
          const e = String(ev || '').trim();
          return e === '换标'
            || e === '调整-取出' || e === '调整-放入'
            || e === '重扫-冲销' || e === '重扫-加回';
        };
        const collectInvalidEmptyCodes = (groups: any[]) => {
          const qtyByContainer = new Map<string, number>();
          (Array.isArray(groups) ? groups : []).forEach((g: any) => {
            const recs = Array.isArray(g?.records_preview) ? g.records_preview : [];
            const aggByBox = new Map<string, Map<string, number>>();
            (Array.isArray(recs) ? recs : []).forEach((r: any) => {
              if (!contentEvOkForRelabelPack(r?.event)) return;
              const cc = String(r?.container_code || '').trim();
              if (!cc) return;
              const newc = String(r?.new_code || '').trim().toUpperCase();
              if (!newc || newc === '-') return;
              const q = Math.trunc(Number(r?.qty || 0) || 0);
              if (!Number.isFinite(q) || q === 0) return;
              const mm = aggByBox.get(cc) || new Map<string, number>();
              mm.set(newc, (mm.get(newc) || 0) + q);
              aggByBox.set(cc, mm);
            });
            aggByBox.forEach((mm: Map<string, number>, cc: string) => {
              let qty = 0;
              mm.forEach((v: number) => {
                const n = Math.floor(Number(v || 0) || 0);
                if (Number.isFinite(n) && n > 0) qty += n;
              });
              qtyByContainer.set(cc, qty);
            });
          });
          return Array.from(new Set(
            (Array.isArray(groups) ? groups : [])
              .flatMap((g: any) => (Array.isArray(g?.boxes) ? g.boxes : []))
              .map((b: any) => ({
                code: String(b?.code || '').trim(),
                status: String(b?.status || '').trim().toLowerCase(),
              }))
              .filter((x: any) => !!x.code && x.status !== 'cancelled' && x.status !== 'open')
              .filter((x: any) => Math.floor(Number(qtyByContainer.get(x.code) || 0) || 0) <= 0)
              .map((x: any) => x.code),
          ));
        };
        let invalidEmptyCodes = collectInvalidEmptyCodes(groupSnapshots);
        while (invalidEmptyCodes.length > 0) {
          const action = await new Promise<'void' | 'cancel'>((resolve) => {
            Modal.confirm({
              title: '提交前检测到空容器',
              width: 840,
              okText: '作废空容器并继续提交',
              cancelText: '取消提交',
              content: (
                <div style={{ lineHeight: 1.8 }}>
                  <div style={{ marginBottom: 8, color: '#595959' }}>
                    以下容器当前总件数为0，且尚未作废。可批量作废后继续提交。
                  </div>
                  <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '6px 8px', background: '#fff' }}>
                    {invalidEmptyCodes.map((code: string, idx: number) => (
                      <div key={`${code}-${idx}`} style={{ fontFamily: 'monospace' }}>
                        {idx + 1}. {code}
                      </div>
                    ))}
                  </div>
                </div>
              ),
              onOk: () => resolve('void'),
              onCancel: () => resolve('cancel'),
            });
          });
          if (action !== 'void') { setLoading(false); return; }
          try {
            const resp = await request.post('/api/v1/storage/output-containers/void/', {
              instruction_id: instrId,
              codes: invalidEmptyCodes,
              defer_inventory_settlement: true,
            });
            const voidedCodes = Array.isArray(resp?.data?.voided_codes) ? resp.data.voided_codes : [];
            const blockedRows = Array.isArray(resp?.data?.blocked_codes) ? resp.data.blocked_codes : [];
            const voidedSet = new Set<string>(
              (voidedCodes || []).map((x: any) => String(x || '').trim()).filter((x: string) => !!x),
            );
            if (voidedSet.size <= 0) {
              message.warning('未作废任何空容器，已中断提交');
              setLoading(false);
              return;
            }
            groupSnapshots.forEach((g: any) => {
              if (Array.isArray(g?.boxes)) {
                g.boxes = g.boxes.map((b: any) => {
                  const code = String(b?.code || '').trim();
                  return voidedSet.has(code) ? ({ ...b, status: 'cancelled' }) : b;
                });
              }
              if (Array.isArray(g?.closed_container_codes)) {
                g.closed_container_codes = g.closed_container_codes
                  .map((x: any) => String(x || '').trim())
                  .filter((x: string) => !!x && !voidedSet.has(x));
              }
              const p = g?.progress_snapshot;
              if (p && Array.isArray(p?.boxes_rows)) {
                p.boxes_rows = p.boxes_rows.map((r: any) => {
                  const code = String(r?.container_code || '').trim();
                  return voidedSet.has(code) ? ({ ...r, status: 'cancelled', status_zh: '已作废' }) : r;
                });
              }
            });
            // 关键同步：回写步骤 draft（否则再次点击提交会从旧 draft 读回“未作废”状态）
            try {
              for (const g of (Array.isArray(groupSnapshots) ? groupSnapshots : [])) {
                const sid = Number(g?.draft_step_id || 0);
                if (!Number.isFinite(sid) || sid <= 0) continue;
                const prevState = (g?.draft_state && typeof g.draft_state === 'object') ? g.draft_state : {};
                const nextState = {
                  ...(prevState || {}),
                  boxes: Array.isArray(g?.boxes) ? g.boxes : [],
                };
                const nextDraft = {
                  kind: 'combined_relabel_pack',
                  version: 1,
                  updated_at: Date.now(),
                  state: nextState,
                };
                await request.post(`/api/v1/instruction/exec/steps/${sid}/draft/`, { draft: nextDraft });
                g.draft_state = nextState;
              }
            } catch {
              // 同步失败不阻断当前循环，下一次可重试
            }
            if (blockedRows.length > 0) {
              const sample = blockedRows.slice(0, 3).map((x: any) => `${String(x?.code || '-')}:${String(x?.reason || '-')}`).join('；');
              message.warning(`部分容器未作废：${sample}${blockedRows.length > 3 ? ` 等${blockedRows.length}条` : ''}`);
            }
          } catch (e: any) {
            message.error(e?.response?.data?.detail || '批量作废空容器失败，已中断提交');
            setLoading(false);
            return;
          }
          invalidEmptyCodes = collectInvalidEmptyCodes(groupSnapshots);
        }

        const stats = (() => {
          const allBoxes = groupSnapshots.flatMap(g => (Array.isArray(g?.boxes) ? g.boxes : []));
          const allClosed = new Set<string>();
          groupSnapshots.forEach(g => (g.closed_container_codes || []).forEach((c:string)=> allClosed.add(c)));
          const recordsCount = groupSnapshots.reduce((s, g) => s + Number(g?.records_count || 0), 0);
          const labelCount = groupSnapshots.reduce((s, g) => s + Number(g?.label_count || 0), 0);
          return {
            label_count: labelCount,
            records_count: recordsCount,
            containers_total: allBoxes.length,
            containers_closed: allClosed.size
          };
        })();

        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: `确认提交换标+装箱记录并完成阶段 - 指令：${String((instr?.system_code || instrId))}`,
            width: 1240,
            icon: null,
            okText: '提交并完成',
            cancelText: '取消',
            content: (
              <div style={{ lineHeight: 1.7 }}>
                <div style={{ color:'#595959', marginBottom: 8 }}>
                  本阶段包含“换标 + 装箱/装袋”。快照将用于后续回看与审计（内容/样式按阶段单独定义）。
                </div>
                {/* 三大块：1 总进度 2 条码映射 3 容器+调整池 */}
                {(() => {
                  const ps = groupSnapshots.map((g:any) => g?.progress_snapshot).filter(Boolean);
                  const overall = ps.find((x:any) => x?.overall)?.overall || null;
                  const mappingRows: any[] = ps.flatMap((x:any) => (Array.isArray(x?.mapping_rows) ? x.mapping_rows : []));
                  const boxRows: any[] = ps.flatMap((x:any) => (Array.isArray(x?.boxes_rows) ? x.boxes_rows : []));
                  const pool = ps.find((x:any) => x?.pool_summary)?.pool_summary || null;
                  const recordRows: any[] = groupSnapshots.flatMap((g:any) => (Array.isArray(g?.records_preview) ? g.records_preview : []));
                  const hasProgress = ps.length > 0;
                  return (
                    <>
                      <RelabelPackSnapshotBlock
                        overall={overall}
                        mappingRows={mappingRows}
                        boxRows={boxRows}
                        pool={pool}
                        records={recordRows}
                        genLabelMap={genLabelMap}
                        emptyHint={hasProgress ? '（未采集到总进度表快照）' : '未采集到总进度表，已降级（请确认前端已更新并在该阶段页面内提交）'}
                      />
                      <RelabelPackRecordsSnapshotBlock
                        records={recordRows}
                        mappingRows={mappingRows}
                        emptyHint="本次预览未采集到操作记录快照（可能该组 draft 为空或未保存）"
                      />
                    </>
                  );
                })()}
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!ok) { setLoading(false); return; }

        // ===== 正式落库：按 records 口径写入 IOC + IOC Content（并锁定 Draft）=====
        // 目标：与前端“总进度→按容器统计”的口径一致（records 净额），同时 IOC 物理数据以 Draft 为准。
        try {
          const poolCode = `POOL-${Number(instrId)}`;
          const contentEvOk = (ev:any) => {
            const e = String(ev || '').trim();
            return e === '换标'
              || e === '调整-取出' || e === '调整-放入'
              || e === '重扫-冲销' || e === '重扫-加回';
          };
          // 全部已结束容器 codes（去重）
          const closedCodes = Array.from(new Set(
            (groupSnapshots || []).flatMap((g:any) => (Array.isArray(g?.closed_container_codes) ? g.closed_container_codes : []))
              .map((x:any) => String(x || '').trim())
              .filter(Boolean)
          ));

          // 将 DraftItem（低频缓存）写回：每个容器按 IU 聚合 records（replace_items=true）
          // 注意：这不是最终口径（最终口径仍然 finalize-from-records），这里只是让 admin inline 能看到草稿内容物。
          const recordByGroup: any[] = [];
          // 从步骤草稿里没有直接保留 records 在 groupSnapshots；这里通过 progress_snapshot 取不到 IU，必须用 step draft。
          // 因此我们再次拉取各组 draftState，聚合 records（仅一次提交时触发，开销可接受）。
          for (const g of relabelPackGroups) {
            const gk = String(g.group_key || '');
            const relabelStep = (g as any).relabelStep;
            const packStep = (g as any).packStep;
            const draftStepId = (() => {
              const p = Number(packStep?.id);
              if (Number.isFinite(p) && p > 0) return p;
              const r = Number(relabelStep?.id);
              if (Number.isFinite(r) && r > 0) return r;
              return null;
            })();
            if (!draftStepId) continue;
            try {
              const dResp = await request.get(`/api/v1/instruction/exec/steps/${draftStepId}/draft/`, { params: { t: Date.now() } as any });
              const d = dResp?.data?.draft;
              if (d && String(d?.kind || '') === 'combined_relabel_pack') {
                const st = d?.state || {};
                const recs = Array.isArray(st?.records) ? st.records : [];
                recordByGroup.push(...recs);
              }
            } catch {}
          }
          const allRecords = recordByGroup;

          // 每个容器写入 draft items（IU+qty），以 records 净额为准
          const byContainerIu = new Map<string, Map<number, number>>();
          (allRecords || []).forEach((r:any) => {
            const ev = String(r?.event || '').trim();
            if (!contentEvOk(ev)) return;
            const cc = String(r?.container_code || '').trim();
            if (!cc) return;
            if (cc === poolCode) return;
            if (!closedCodes.includes(cc)) return;
            const iu = Math.floor(Number(r?.op_iu_id || 0));
            if (!Number.isFinite(iu) || iu <= 0) return;
            const q = Math.trunc(Number(r?.qty || 0) || 0);
            if (!Number.isFinite(q) || q === 0) return;
            const inner = byContainerIu.get(cc) || new Map<number, number>();
            inner.set(iu, (inner.get(iu) || 0) + q);
            byContainerIu.set(cc, inner);
          });

          // 写 DraftItem（replace_items=true），并尽量携带 Draft 物理数据（来自 outputDrafts）
          for (const cc of closedCodes) {
            const inner = byContainerIu.get(cc) || new Map<number, number>();
            const items = Array.from(inner.entries())
              .map(([inventory_unit_id, quantity]) => ({ inventory_unit_id, quantity: Math.floor(Number(quantity || 0) || 0) }))
              .filter((x:any) => Number.isFinite(x.inventory_unit_id) && x.inventory_unit_id > 0 && Number.isFinite(x.quantity) && x.quantity > 0);
            // 若该容器无正向内容，则不写 items（避免覆盖为全空）
            if (!items.length) continue;
            const d = (outputDrafts || []).find((x:any) => String(x?.code || '').trim() === cc) || null;
            await request.post('/api/v1/storage/output-containers/drafts/upsert/', {
              instruction_id: instrId,
              code: cc,
              container_type: String(d?.container_type || 'box'),
              status: 'closed_pending_final',
              length_cm: d?.length_cm,
              width_cm: d?.width_cm,
              height_cm: d?.height_cm,
              weight_g: d?.weight_g,
              length_in: d?.length_in,
              width_in: d?.width_in,
              height_in: d?.height_in,
              weight_lb: d?.weight_lb,
              replace_items: true,
              items,
            });
          }

          // 最终确认：records -> IOC Content（并锁定 Draft；物理数据从 Draft 同步）
          await request.post('/api/v1/storage/output-containers/finalize-from-records/', {
            instruction_id: instrId,
            codes: closedCodes,
            pool_code: poolCode,
            records: allRecords,
            defer_inventory_settlement: true,
          });
        } catch (e:any) {
          message.error(e?.response?.data?.detail || e?.message || '正式落库失败（产出容器/内容物），已阻断提交');
          setLoading(false);
          return;
        }

        const payload = {
          stage_idx: stageNo,
          // 若一个阶段可能出现多个 group_key，这里使用“阶段级幂等键”即可（后端按 hash 再兜底）
          idempotency_key: `task_${taskId}_stage_${stageNo}_relabel_pack`,
          hash: '',
          stats,
          // 快照内容：以“总进度表”信息为准（每组包含 overall/mapping/boxes/pool），并保留兜底字段
          snapshot: { groups: groupSnapshots, generated_labels: genLabelMap },
        };
        await request.post(`/api/v1/instruction/exec/tasks/${taskId}/relabel-pack/submissions/`, payload);
      }

      // ===== 单指令代发“配货|装箱|贴单”：阶段快照 =====
      const hasSingleDropshipPack = (() => {
        try {
          const steps = Array.isArray(cur?.steps) ? cur.steps : [];
          const hasRelabel = steps.some((s: any) => isRelabelType(s?.step_type || s?.renderer_key));
          const hasPack = steps.some((s: any) => isPackType(s?.step_type || s?.renderer_key));
          return hasPack && !hasRelabel && !isRegularOutboundTemplateForStage;
        } catch {
          return false;
        }
      })();
      const hasRegularOutboundPack = (() => {
        try {
          const steps = Array.isArray(cur?.steps) ? cur.steps : [];
          const hasRelabel = steps.some((s: any) => isRelabelType(s?.step_type || s?.renderer_key));
          const hasPack = steps.some((s: any) => isPackType(s?.step_type || s?.renderer_key));
          return hasPack && !hasRelabel && isRegularOutboundTemplateForStage;
        } catch {
          return false;
        }
      })();
      if (hasRegularOutboundPack) {
        if (!taskId) { message.error('提交失败：缺少任务ID'); setLoading(false); return; }
        const stageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx;
        const packStep = (cur.steps || []).find((s: any) => isPackType(s?.step_type || s?.renderer_key));
        const sid = String(packStep?.id ?? '');
        const mdDraft = (sid && metricsDraft[sid] && typeof metricsDraft[sid] === 'object') ? (metricsDraft[sid] || {}) : {};
        const mdCollected = (packStep?.collected_metrics && typeof packStep.collected_metrics === 'object') ? (packStep.collected_metrics || {}) : {};
        let mdServerDraft: any = {};
        try {
          const stepId = Number(packStep?.id || 0);
          if (stepId > 0) {
            const dResp = await request.get(`/api/v1/instruction/exec/steps/${stepId}/draft/`, { params: { t: Date.now() } as any });
            const d = dResp?.data?.draft;
            if (d && String(d?.kind || '').trim() === 'regular_outbound_pack' && d?.state && typeof d.state === 'object') {
              mdServerDraft = d.state || {};
            }
          }
        } catch {
          mdServerDraft = {};
        }
        let mdLocal: any = {};
        const draftIdentityCode = String((instr?.system_code || 'UNKNOWN')).trim().replace(/[^\w-]+/g, '_');
        const regularDraftKey = `REGULAR_OUTBOUND_PACK_${Number(instrId || taskId || 0) || 0}_${stageNo}_${draftIdentityCode || 'UNKNOWN'}`;
        try {
          const raw = localStorage.getItem(regularDraftKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') {
              const mapped: any = { ...parsed };
              if (!Array.isArray(mapped?.operation_records) && Array.isArray(parsed?.local_operation_records)) {
                mapped.operation_records = parsed.local_operation_records;
              }
              if (!String(mapped?.process_result_status || '').trim() && String(parsed?.process_result_status || '').trim()) {
                mapped.process_result_status = String(parsed.process_result_status || '').trim();
              }
              if (!String(mapped?.process_unable_reason || '').trim() && String(parsed?.process_unable_reason || '').trim()) {
                mapped.process_unable_reason = String(parsed.process_unable_reason || '').trim();
              }
              if (!Array.isArray(mapped?.boxes) && Array.isArray(parsed?.boxes)) {
                mapped.boxes = parsed.boxes;
              }
              if (!Array.isArray(mapped?.hierarchy_links) && Array.isArray(parsed?.hierarchy_links)) {
                mapped.hierarchy_links = parsed.hierarchy_links;
              }
              mdLocal = mapped;
            }
          }
        } catch {
          mdLocal = {};
        }
        // 兜底合并：已提交值(collected) < 本地草稿(localStorage) < 步骤草稿(step_draft) < 当前会话草稿(metricsDraft)
        const md = { ...mdCollected, ...mdLocal, ...mdServerDraft, ...mdDraft };
        const processResultRaw = String(md?.process_result_status || '').trim().toLowerCase();
        const processUnableReason = String(md?.process_unable_reason || '').trim();
        const operationRecords = Array.isArray(md?.operation_records) ? md.operation_records : [];
        const boxes = Array.isArray(md?.boxes) ? md.boxes : [];
        const hierarchyLinks = Array.isArray(md?.hierarchy_links) ? md.hierarchy_links : [];
        // 校验1：必须标记处理结果
        if (!(processResultRaw === 'completed' || processResultRaw === 'unable')) {
          Modal.warning({
            title: '提交前校验未通过',
            content: '请先标记本指令的处理结果（已完成或无法操作）后再提交。',
            okText: '我知道了',
          });
          setLoading(false);
          return;
        }
        // 校验2：禁止存在“空容器且非父容器”
        const hasIocTargetForRegular = (() => {
          try {
            const td = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
            return td.some((t: any) => String(t?.target_type || '').trim() === 'instruction_output');
          } catch {
            return false;
          }
        })();
        const parentCodes = new Set<string>();
        (hierarchyLinks || []).forEach((ln: any) => {
          const p = String(ln?.parent_code || '').trim();
          if (p) parentCodes.add(p);
        });
        const qtyByContainer = new Map<string, number>();
        (operationRecords || []).forEach((r: any) => {
          const cc = String(r?.container_code || '').trim();
          if (!cc || cc === '-') return;
          const ev = String(r?.event || r?.action || '').trim();
          if (!ev || ev === '开始容器' || ev === '结束容器' || ev === '新增到已有容器' || ev === '结束新增') return;
          const isOut = /取出|冲销|移除|移出/.test(ev);
          const isIn = /放入|加入|加回/.test(ev);
          if (!isOut && !isIn) return;
          const qtyRaw = Number(r?.qty ?? r?.quantity ?? r?.amount ?? 0) || 0;
          const qtyBase = hasIocTargetForRegular ? 1 : Math.max(0, Math.floor(Math.abs(qtyRaw)));
          if (!Number.isFinite(qtyBase) || qtyBase <= 0) return;
          const delta = isOut ? -qtyBase : qtyBase;
          qtyByContainer.set(cc, (qtyByContainer.get(cc) || 0) + delta);
        });
        const collectInvalidEmptyNonParentCodes = (boxRows: any[]) => (
          (Array.isArray(boxRows) ? boxRows : [])
            .map((b: any) => ({
              code: String(b?.code || '').trim(),
              status: String(b?.status || '').trim().toLowerCase(),
            }))
            .filter((b: any) => b.code && b.status !== 'cancelled')
            .filter((b: any) => !parentCodes.has(b.code))
            .filter((b: any) => Math.floor(Number(qtyByContainer.get(b.code) || 0) || 0) <= 0)
            .map((b: any) => b.code)
        );
        let workingBoxes = Array.isArray(boxes) ? boxes.map((b: any) => ({ ...(b || {}) })) : [];
        // 校验3：禁止存在“进行中”容器（必须先结束容器或作废后才能提交）
        const activeContainerCodes = (Array.isArray(workingBoxes) ? workingBoxes : [])
          .map((b: any) => ({
            code: String(b?.code || '').trim(),
            status: String(b?.status || '').trim().toLowerCase(),
          }))
          .filter((b: any) => !!b.code && (b.status === 'open' || b.status === 'active'))
          .map((b: any) => b.code);
        if (activeContainerCodes.length > 0) {
          Modal.warning({
            title: '提交前校验未通过',
            width: 760,
            okText: '我知道了',
            content: (
              <div style={{ lineHeight: 1.8 }}>
                <div style={{ marginBottom: 8, color: '#595959' }}>
                  检测到存在“进行中”容器，必须先结束容器或作废后，才能继续提交并查看阶段快照预览。
                </div>
                <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '6px 8px', background: '#fff' }}>
                  {activeContainerCodes.map((code: string, idx: number) => (
                    <div key={`${code}-${idx}`} style={{ fontFamily: 'monospace' }}>
                      {idx + 1}. {code}
                    </div>
                  ))}
                </div>
              </div>
            ),
          });
          setLoading(false);
          return;
        }
        let invalidEmptyNonParentContainers = collectInvalidEmptyNonParentCodes(workingBoxes);
        while (invalidEmptyNonParentContainers.length > 0) {
          const action = await new Promise<'delete' | 'cancel'>((resolve) => {
            Modal.confirm({
              title: '提交前检测到空容器（且非父容器）',
              width: 840,
              okText: '作废空容器并继续提交',
              cancelText: '取消提交',
              content: (
                <div style={{ lineHeight: 1.8 }}>
                  <div style={{ marginBottom: 8, color: '#595959' }}>
                    以下容器内容物数量为0，且不是父容器。可直接删除（作废）后继续提交。
                  </div>
                  <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '6px 8px', background: '#fff' }}>
                    {invalidEmptyNonParentContainers.map((code: string, idx: number) => (
                      <div key={`${code}-${idx}`} style={{ fontFamily: 'monospace' }}>
                        {idx + 1}. {code}
                      </div>
                    ))}
                  </div>
                </div>
              ),
              onOk: () => resolve('delete'),
              onCancel: () => resolve('cancel'),
            });
          });
          if (action !== 'delete') {
            setLoading(false);
            return;
          }
          try {
            const resp = await request.post('/api/v1/storage/output-containers/void/', {
              instruction_id: instrId,
              codes: invalidEmptyNonParentContainers,
              defer_inventory_settlement: true,
            });
            const voidedCodes = Array.isArray(resp?.data?.voided_codes) ? resp.data.voided_codes : [];
            const blockedRows = Array.isArray(resp?.data?.blocked_codes) ? resp.data.blocked_codes : [];
            const voidedSet = new Set(
              (voidedCodes || []).map((x: any) => String(x || '').trim()).filter(Boolean)
            );
            if (voidedSet.size <= 0) {
              message.warning('未删除任何空容器，已中断提交');
              setLoading(false);
              return;
            }
            workingBoxes = (workingBoxes || []).map((b: any) => {
              const code = String(b?.code || '').trim();
              return voidedSet.has(code) ? ({ ...b, status: 'cancelled' }) : b;
            });
            // 同步回当前页面草稿，避免用户再次点击提交时仍按旧状态重复命中“空容器”校验。
            try {
              if (sid) {
                const nextBoxes = Array.isArray(workingBoxes) ? workingBoxes : [];
                setMetricsDraft((prev: any) => ({
                  ...(prev || {}),
                  [sid]: {
                    ...((prev || {})[sid] || {}),
                    boxes: nextBoxes,
                  },
                }));
              }
            } catch {}
            // 同步本地草稿缓存（跨刷新保持一致）
            try {
              const rawDraft = localStorage.getItem(regularDraftKey);
              const parsedDraft = rawDraft ? JSON.parse(rawDraft) : {};
              const nextDraft = {
                ...(parsedDraft && typeof parsedDraft === 'object' ? parsedDraft : {}),
                boxes: Array.isArray(workingBoxes) ? workingBoxes : [],
                updated_at: Date.now(),
              };
              localStorage.setItem(regularDraftKey, JSON.stringify(nextDraft));
            } catch {}
            if (blockedRows.length > 0) {
              const sample = blockedRows.slice(0, 3).map((x: any) => `${String(x?.code || '-')}:${String(x?.reason || '-')}`).join('；');
              message.warning(`部分容器未删除：${sample}${blockedRows.length > 3 ? ` 等${blockedRows.length}条` : ''}`);
            }
          } catch (e: any) {
            message.error(e?.response?.data?.detail || '删除空容器失败，已中断提交');
            setLoading(false);
            return;
          }
          invalidEmptyNonParentContainers = collectInvalidEmptyNonParentCodes(workingBoxes);
        }
        const mdForSubmit = {
          ...md,
          boxes: workingBoxes,
          // 兜底：避免前端状态时序导致 waybill_source / waybill_submit_mode 未及时写入 metricsDraft
          // 这里在提交快照前强制补齐，确保快照回看判定口径稳定。
          waybill_source: String(
            md?.waybill_source
            || instr?.waybill_source
            || instr?.customer_answers?.waybill_source
            || '',
          ).trim(),
          waybill_submit_mode: String(
            md?.waybill_submit_mode
            || instr?.waybill_submit_mode
            || instr?.customer_answers?.waybill_submit_mode
            || '',
          ).trim(),
        };
        const isProd = typeof process !== 'undefined' && !!process?.env && process.env.NODE_ENV === 'production';
        if (!isProd) {
          try {
            // 调试提交前快照固化：确认 metrics 中是否携带 waybill_source / waybill_submit_mode 及运单事实
            console.debug('[RO-Submit][Waybill][BeforeSnapshot]', {
              step_id: packStep?.id,
              waybill_source: String(mdForSubmit?.waybill_source || '').trim(),
              waybill_submit_mode: String(mdForSubmit?.waybill_submit_mode || '').trim(),
              waybill_carrier: String(mdForSubmit?.waybill_carrier || '').trim(),
              waybill_tracking_no: String(mdForSubmit?.waybill_tracking_no || '').trim(),
              waybill_charge_amount: String(mdForSubmit?.waybill_charge_amount || '').trim(),
              waybill_charge_currency: String(mdForSubmit?.waybill_charge_currency || '').trim(),
              attachments_count: Array.isArray(mdForSubmit?.attachments_detail) ? mdForSubmit.attachments_detail.length : 0,
            });
          } catch {}
        }
        const progressTabsSnapshot = (
          mdForSubmit?.progress_tabs_snapshot
          && typeof mdForSubmit.progress_tabs_snapshot === 'object'
        ) ? mdForSubmit.progress_tabs_snapshot : null;
        const toStatusZh = (stRaw: any): string => {
          const st = String(stRaw || '').trim().toLowerCase();
          if (st === 'open' || st === 'active') return '进行中';
          if (st === 'closed') return '已结束';
          if (st === 'cancelled') return '已作废';
          if (st === 'consumed') return '已消耗';
          if (st === 'lost') return '丢失';
          return st || '-';
        };
        const containerRows = (workingBoxes || []).map((b: any) => {
          const code = String(b?.code || '').trim();
          return {
            code,
            status: String(b?.status || '').trim(),
            is_parent: parentCodes.has(code),
            estimated_qty: Math.floor(Number(qtyByContainer.get(code) || 0) || 0),
          };
        });
        const progressByTargetRows = Array.isArray(progressTabsSnapshot?.by_target_rows)
          ? progressTabsSnapshot.by_target_rows
          : (() => {
          const m = new Map<string, { target_type: string; target_code: string; qty: number }>();
          (operationRecords || []).forEach((r: any) => {
            const targetCode = String(r?.target || r?.target_code || r?.code || '').trim().toUpperCase();
            if (!targetCode || targetCode === '-') return;
            const targetType = String(r?.target_type || '').trim() || 'unknown';
            const ev = String(r?.event || r?.action || '').trim();
            const qRaw = Number(r?.qty ?? r?.quantity ?? 0) || 0;
            const qAbs = Math.max(0, Math.floor(Math.abs(qRaw)));
            if (qAbs <= 0) return;
            const isOut = /取出|冲销|移除|移出/.test(ev);
            const delta = isOut ? -qAbs : qAbs;
            const key = `${targetType}:${targetCode}`;
            const cur = m.get(key) || { target_type: targetType, target_code: targetCode, qty: 0 };
            cur.qty += delta;
            m.set(key, cur);
          });
          return Array.from(m.values())
            .filter((x) => Number(x.qty || 0) > 0)
            .sort((a, b) => String(a.target_code).localeCompare(String(b.target_code), undefined, { numeric: true, sensitivity: 'base' }));
        })();
        const progressByContainerRows = Array.isArray(progressTabsSnapshot?.by_container_rows)
          ? progressTabsSnapshot.by_container_rows
          : (containerRows || []).map((r: any) => ({
            container_code: String(r?.code || '').trim(),
            status: String(r?.status || '').trim(),
            status_zh: toStatusZh(r?.status),
            hierarchy_level: !!r?.is_parent ? '父' : '独立',
            qty: Math.floor(Number(r?.estimated_qty || 0) || 0),
            ops: 0,
            objectKinds: 0,
            weight_display: '-',
            dims_display: '-',
            last_ts: 0,
          }));
        // 同步“本次提交流程中最新容器状态”（例如刚在弹窗中作废的空容器），
        // 避免预览弹窗仍显示旧快照状态，导致作废容器短暂出现在“可用容器”列表。
        const latestStatusMap = new Map<string, string>();
        (Array.isArray(workingBoxes) ? workingBoxes : []).forEach((b: any) => {
          const code = String(b?.code || '').trim();
          if (!code) return;
          latestStatusMap.set(code, String(b?.status || '').trim().toLowerCase());
        });
        const toStatusZhLatest = (stRaw: any): string => {
          const st = String(stRaw || '').trim().toLowerCase();
          if (st === 'open' || st === 'active') return '进行中';
          if (st === 'closed') return '已结束';
          if (st === 'cancelled') return '已作废';
          if (st === 'consumed') return '已消耗';
          if (st === 'lost') return '丢失';
          return st || '-';
        };
        const progressByContainerRowsSynced = (() => {
          const rows = Array.isArray(progressByContainerRows) ? progressByContainerRows.map((r: any) => ({ ...(r || {}) })) : [];
          const seen = new Set<string>();
          rows.forEach((r: any) => {
            const code = String(r?.container_code || r?.code || '').trim();
            if (!code) return;
            seen.add(code);
            if (latestStatusMap.has(code)) {
              const st = String(latestStatusMap.get(code) || '').trim().toLowerCase();
              r.status = st;
              r.status_zh = toStatusZhLatest(st);
            }
          });
          latestStatusMap.forEach((st: string, code: string) => {
            if (seen.has(code)) return;
            rows.push({
              container_code: code,
              status: st,
              status_zh: toStatusZhLatest(st),
              hierarchy_level: '-',
              qty: 0,
              ops: 0,
              objectKinds: 0,
              weight_display: '-',
              dims_display: '-',
              last_ts: 0,
            });
          });
          return rows;
        })();
        const containerStatusMap = new Map<string, string>();
        progressByContainerRowsSynced.forEach((r: any) => {
          const code = String(r?.container_code || r?.code || '').trim();
          if (code) containerStatusMap.set(code, String(r?.status || '').trim());
        });
        const progressByHierarchyRows = Array.isArray(progressTabsSnapshot?.by_hierarchy_rows)
          ? progressTabsSnapshot.by_hierarchy_rows
          : (Array.isArray(hierarchyLinks) ? hierarchyLinks : []).map((ln: any, idx: number) => {
            const p = String(ln?.parent_code || '').trim();
            const c = String(ln?.child_code || '').trim();
            return {
              key: `${idx}-${p}-${c}`,
              parent_code: p,
              parent_status: containerStatusMap.get(p) || '',
              parent_status_zh: toStatusZh(containerStatusMap.get(p) || ''),
              child_code: c,
              child_status: containerStatusMap.get(c) || '',
              child_status_zh: toStatusZh(containerStatusMap.get(c) || ''),
              group_idx: idx,
              is_first_in_group: true,
            };
          });
        // 先固化 IOC 最终内容（与“换标+装箱”一致：落库失败则阻断阶段提交，避免半成功）
        try {
          const closedCodes = Array.from(new Set(
            (Array.isArray(workingBoxes) ? workingBoxes : [])
              .map((b: any) => ({
                code: String(b?.code || '').trim(),
                status: String(b?.status || '').trim().toLowerCase(),
              }))
              .filter((x: any) => !!x.code && x.status !== 'open' && x.status !== 'cancelled')
              .map((x: any) => x.code),
          ));
          const poolCode = (() => {
            const iid = Number(instrId || 0);
            return Number.isFinite(iid) && iid > 0 ? `ADJ-POOL-${iid}` : 'ADJ-POOL';
          })();
          await request.post('/api/v1/storage/output-containers/finalize-from-records/', {
            instruction_id: instrId,
            codes: closedCodes,
            pool_code: poolCode,
            records: Array.isArray(operationRecords) ? operationRecords : [],
            hierarchy_links: Array.isArray(hierarchyLinks) ? hierarchyLinks : [],
            execution_session_id: String(taskExecutionSessionId || '').trim() || undefined,
            reference_number: `RO-FINALIZE-T${String(taskId || '')}-S${String(stageNo || '')}`,
            defer_inventory_settlement: true,
          });
        } catch (e: any) {
          message.error(e?.response?.data?.detail || e?.message || '正式落库失败（产出容器/内容物），已阻断提交');
          setLoading(false);
          return;
        }
        const stats = {
          operation_records_count: operationRecords.length,
          container_count: (workingBoxes || []).filter((b: any) => String(b?.status || '').trim().toLowerCase() !== 'cancelled').length,
          hierarchy_links_count: hierarchyLinks.length,
          empty_non_parent_container_count: 0,
          process_result_status: String(md?.process_result_status || '').trim(),
        };
        const snapshot = {
          schema_version: 1,
          kind: 'regular_outbound_pack',
          step_id: packStep?.id,
          step_title: String(packStep?.title || '').trim(),
          step_type: String(packStep?.step_type || packStep?.renderer_key || '').trim().toUpperCase(),
          metrics: mdForSubmit,
          stage_ui_data: sid ? (stageUIData[sid] || {}) : {},
          metrics_sources: {
            has_collected_metrics: Object.keys(mdCollected || {}).length > 0,
            has_local_storage_draft: Object.keys(mdLocal || {}).length > 0,
            has_step_draft: Object.keys(mdServerDraft || {}).length > 0,
            has_metrics_draft: Object.keys(mdDraft || {}).length > 0,
          },
          instruction: {
            id: instrId,
            system_code: instr?.system_code || '',
          },
          sections: {
            process_result: {
              status: processResultRaw,
              unable_reason: processUnableReason,
            },
            containers: {
              rows: containerRows,
              hierarchy_links: hierarchyLinks,
            },
            progress_tabs: {
              has_ioc_target: !!progressTabsSnapshot?.has_ioc_target,
              by_target_label: String(progressTabsSnapshot?.by_target_label || ''),
              by_target_rows: progressByTargetRows,
              by_container_rows: progressByContainerRowsSynced,
              by_hierarchy_rows: progressByHierarchyRows,
            },
            operation_records: {
              rows: operationRecords,
            },
            billing_metrics: {
              pallet_direct_operation_object_counts: (
                mdForSubmit?.pallet_direct_operation_object_counts
                && typeof mdForSubmit.pallet_direct_operation_object_counts === 'object'
              ) ? mdForSubmit.pallet_direct_operation_object_counts : {},
              pallet_direct_operation_object_breakdown: Array.isArray(mdForSubmit?.pallet_direct_operation_object_breakdown)
                ? mdForSubmit.pallet_direct_operation_object_breakdown
                : [],
            },
          },
        };
        // 冻结“提交前预览”和“最终提交”使用的同一份快照对象，保证一致性。
        const submissionEnvelope = {
          schema_version: 1,
          kind: 'regular_outbound_pack',
          stats,
          snapshot,
        };
        // A: 预览弹窗期间不占用全页 loading，避免组件重挂载导致本地运单显示状态被重置。
        setLoading(false);
        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: (
              <span>
                确认提交常规出库配货|装箱|贴单记录并完成阶段 - 指令：
                <span style={{ color: '#1677ff' }}>{String((instr?.system_code || instrId))}</span>
              </span>
            ),
            width: 1120,
            icon: null,
            okText: '提交并完成',
            cancelText: '取消',
            content: (
              <div style={{ lineHeight: 1.7 }}>
                <div style={{ color: '#595959', marginBottom: 8 }}>
                  将先固化本阶段快照（用于跨设备回看/审计），再提交并完成当前阶段。
                </div>
                <RegularOutboundSnapshotBlock snapshotEnvelope={submissionEnvelope} />
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) { setLoading(false); return; }
        setLoading(true);
        if (processResultRaw === 'unable') {
          if (!processUnableReason) {
            message.error('提交失败：请先填写“无法操作原因”，该原因将写入驳回理由');
            setLoading(false);
            return;
          }
          try {
            await request.post(`/api/v1/instruction/instructions/${instrId}/review/`, {
              decision: 'reject',
              reason: processUnableReason,
            });
          } catch (e: any) {
            message.error(e?.response?.data?.detail || '提交失败：更新指令为“已驳回”失败');
            setLoading(false);
            return;
          }
        }
        await request.post(`/api/v1/instruction/exec/tasks/${taskId}/regular-outbound-pack/submissions/`, {
          stage_idx: stageNo,
          idempotency_key: `task_${taskId}_stage_${stageNo}_regular_outbound_pack`,
          hash: '',
          stats: submissionEnvelope.stats,
          snapshot: submissionEnvelope.snapshot,
        });
        try { localStorage.removeItem(regularDraftKey); } catch {}
        try {
          const stepId = Number(packStep?.id || 0);
          if (stepId > 0) {
            await request.post(`/api/v1/instruction/exec/steps/${stepId}/draft/`, { draft: null });
          }
        } catch {}
      }
      if (hasSingleDropshipPack) {
        if (!taskId) { message.error('提交失败：缺少任务ID'); setLoading(false); return; }
        const stageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx;
        const packStep = (cur.steps || []).find((s: any) => isPackType(s?.step_type || s?.renderer_key));
        const sid = String(packStep?.id ?? '');
        const mdDraft = (sid && metricsDraft[sid] && typeof metricsDraft[sid] === 'object') ? (metricsDraft[sid] || {}) : {};
        const mdCollected = (packStep?.collected_metrics && typeof packStep.collected_metrics === 'object') ? (packStep.collected_metrics || {}) : {};
        let mdLocal: any = {};
        try {
          const t = Number(taskId || 0);
          const taskPart = Number.isFinite(t) && t > 0 ? String(t) : 'NO_TASK';
          const sessionPart = String(taskExecutionSessionId || '').trim() || 'NO_SESSION';
          const draftKey = `SINGLE_DROPSHIP_PACK_DRAFT_${instrId}_${taskPart}_${sessionPart}`;
          const raw = localStorage.getItem(draftKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') {
              // 本地草稿字段与 metrics 字段存在命名差异，这里做一次归一映射
              const mapped: any = { ...parsed };
              const saved = parsed?.savedPackData;
              if (saved && typeof saved === 'object') {
                const l = Number(saved?.lengthCm);
                const w = Number(saved?.widthCm);
                const h = Number(saved?.heightCm);
                const g = Number(saved?.weightG);
                if (Number.isFinite(l) && l > 0) mapped.actual_pack_length_cm = Math.round(l);
                if (Number.isFinite(w) && w > 0) mapped.actual_pack_width_cm = Math.round(w);
                if (Number.isFinite(h) && h > 0) mapped.actual_pack_height_cm = Math.round(h);
                if (Number.isFinite(g) && g > 0) mapped.actual_gross_weight_g = Math.round(g);
              }
              if (!Array.isArray(mapped?.operation_records) && Array.isArray(parsed?.localOperationRecords)) {
                mapped.operation_records = parsed.localOperationRecords;
              }
              if (!String(mapped?.created_ioc_code || '').trim() && String(parsed?.createdIocCode || '').trim()) {
                mapped.created_ioc_code = String(parsed.createdIocCode || '').trim();
              }
              if (!String(mapped?.process_result_status || '').trim() && String(parsed?.processResultStatus || '').trim()) {
                mapped.process_result_status = String(parsed.processResultStatus || '').trim();
              }
              if (!String(mapped?.process_unable_reason || '').trim() && String(parsed?.processUnableReason || '').trim()) {
                mapped.process_unable_reason = String(parsed.processUnableReason || '').trim();
              }
              if (!String(mapped?.waybill_carrier || '').trim() && String(parsed?.localWaybillCarrier || '').trim()) {
                mapped.waybill_carrier = String(parsed.localWaybillCarrier || '').trim();
              }
              if (!String(mapped?.waybill_tracking_no || '').trim() && String(parsed?.localWaybillTrackingNo || '').trim()) {
                mapped.waybill_tracking_no = String(parsed.localWaybillTrackingNo || '').trim();
              }
              if (!String(mapped?.waybill_charge_amount || '').trim() && String(parsed?.localWaybillChargeAmount || '').trim()) {
                mapped.waybill_charge_amount = String(parsed.localWaybillChargeAmount || '').trim();
              }
              if (!String(mapped?.waybill_charge_currency || '').trim() && String(parsed?.localWaybillChargeCurrency || '').trim()) {
                mapped.waybill_charge_currency = String(parsed.localWaybillChargeCurrency || '').trim();
              }
              if (!String(mapped?.waybill_charge_note || '').trim() && String(parsed?.localWaybillChargeNote || '').trim()) {
                mapped.waybill_charge_note = String(parsed.localWaybillChargeNote || '').trim();
              }
              mdLocal = mapped;
            }
          }
        } catch {
          mdLocal = {};
        }
        // 兜底合并：已提交值(collected) < 本地草稿(localStorage) < 当前会话草稿(metricsDraft)
        const md = { ...mdCollected, ...mdLocal, ...mdDraft };
        const operationRecords = Array.isArray(md?.operation_records) ? md.operation_records : [];
        const createdIocCode = String(md?.created_ioc_code || '').trim();
        const waybillCarrier = String(md?.waybill_carrier || '').trim();
        const waybillTrackingNo = String(md?.waybill_tracking_no || '').trim();
        const snapshot = {
          step_id: packStep?.id,
          step_title: String(packStep?.title || '').trim(),
          step_type: String(packStep?.step_type || packStep?.renderer_key || '').trim().toUpperCase(),
          metrics: md,
          stage_ui_data: sid ? (stageUIData[sid] || {}) : {},
          metrics_sources: {
            has_collected_metrics: Object.keys(mdCollected || {}).length > 0,
            has_local_storage_draft: Object.keys(mdLocal || {}).length > 0,
            has_metrics_draft: Object.keys(mdDraft || {}).length > 0,
          },
          instruction: {
            id: instrId,
            system_code: instr?.system_code || '',
          },
        };
        const stats = {
          operation_records_count: operationRecords.length,
          has_ioc: !!createdIocCode,
          ioc_code: createdIocCode,
          has_waybill: !!(waybillCarrier && waybillTrackingNo),
          process_result_status: String(md?.process_result_status || '').trim(),
        };
        const mdWaybillChargeAmount = String(md?.waybill_charge_amount ?? md?.buy_waybill_info?.charge_info?.amount ?? '').trim();
        const mdWaybillChargeCurrency = String(md?.waybill_charge_currency ?? md?.buy_waybill_info?.charge_info?.currency ?? '').trim();
        const mdWaybillChargeNote = String(md?.waybill_charge_note ?? md?.buy_waybill_info?.charge_info?.note ?? '').trim();
        const processResultRaw = String(stats.process_result_status || '').trim().toLowerCase();
        const processResultText = processResultRaw === 'completed'
          ? '已完成'
          : (processResultRaw === 'unable' ? '无法操作' : '未标注');
        const processResultBadgeStyle: React.CSSProperties = processResultRaw === 'completed'
          ? { background: '#52c41a', color: '#fff', border: '1px solid #52c41a' }
          : (processResultRaw === 'unable'
            ? { background: '#ff4d4f', color: '#fff', border: '1px solid #ff4d4f' }
            : { background: '#f5f5f5', color: '#595959', border: '1px solid #d9d9d9' });
        const previewRows = operationRecords.slice(0, 100).map((r: any, idx: number) => ({
          key: `${idx}-${String(r?.time || '')}`,
          time: String(r?.time || '-'),
          action: String(r?.action || '-'),
          remark: String(r?.remark || '-'),
        }));
        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 24 }}>
                <span>{`确认提交配货|装箱|贴单记录并完成阶段 - 指令：${String((instr?.system_code || instrId))}`}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                  <span>处理结果：</span>
                  <span style={{ ...processResultBadgeStyle, borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                    {processResultText}
                  </span>
                </span>
              </div>
            ),
            width: 980,
            icon: null,
            okText: '提交并完成',
            cancelText: '取消',
            content: (
              <div style={{ lineHeight: 1.7 }}>
                <div style={{ color: '#595959', marginBottom: 8 }}>
                  将先固化本阶段快照（用于回看/审计），再提交并完成当前阶段。
                </div>
                <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #91caff', borderRadius: 8, background: '#f0f5ff' }}>
                  <div style={{ fontWeight: 600, color: '#0958d9', marginBottom: 6 }}>运单信息</div>
                  <div>
                    <span style={{ marginRight: 20 }}>运单：{stats.has_waybill ? '已填写' : '未填写'}</span>
                    <span style={{ marginRight: 20 }}>承运商：{String(md?.waybill_carrier || '-')}</span>
                    <span>物流跟踪号：{String(md?.waybill_tracking_no || '-')}</span>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <span style={{ marginRight: 20 }}>
                      运单金额：{mdWaybillChargeAmount
                        ? `${mdWaybillChargeAmount} ${mdWaybillChargeCurrency || 'USD'}`
                        : '-'}
                    </span>
                    <span>费用备注：{mdWaybillChargeNote || '-'}</span>
                  </div>
                </div>
                <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: 8, background: '#f6ffed' }}>
                  <div style={{ fontWeight: 600, color: '#389e0d', marginBottom: 6 }}>包装数据与指令产出容器信息</div>
                  <div style={{ marginBottom: 4 }}>
                    包装数据：{
                      (md?.actual_pack_length_cm && md?.actual_pack_width_cm && md?.actual_pack_height_cm && md?.actual_gross_weight_g)
                        ? `${md.actual_pack_length_cm}×${md.actual_pack_width_cm}×${md.actual_pack_height_cm} cm, ${md.actual_gross_weight_g} g`
                        : '-'
                    }
                  </div>
                  <div>指令产出容器：{String(stats.ioc_code || '-')}</div>
                </div>
                <div style={{ padding: '10px 12px', border: '1px solid #ffd591', borderRadius: 8, background: '#fff7e6' }}>
                  <div style={{ fontWeight: 600, color: '#d46b08', marginBottom: 6 }}>
                    操作记录（共 {Number(stats.operation_records_count || 0)} 条）
                  </div>
                  <Table
                    size="small"
                    rowKey="key"
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['5', '10', '20'] as any }}
                    locale={{ emptyText: '暂无操作记录可预览' }}
                    dataSource={previewRows}
                    columns={[
                      { title: '时间', dataIndex: 'time', key: 'time', width: 170 },
                      { title: '操作', dataIndex: 'action', key: 'action', width: 170 },
                      { title: '备注', dataIndex: 'remark', key: 'remark' },
                    ] as any}
                  />
                </div>
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) { setLoading(false); return; }
        // 代发单指令在本阶段标注“无法操作”时，提交前走审核驳回动作（与预审驳回流程对齐）
        if (processResultRaw === 'unable') {
          const rejectReason = String(md?.process_unable_reason || '').trim();
          if (!rejectReason) {
            message.error('提交失败：请先填写“无法操作原因”，该原因将写入驳回理由');
            setLoading(false);
            return;
          }
          try {
            await request.post(`/api/v1/instruction/instructions/${instrId}/review/`, {
              decision: 'reject',
              reason: rejectReason,
            });
          } catch (e: any) {
            message.error(e?.response?.data?.detail || '提交失败：更新指令为“已驳回”失败');
            setLoading(false);
            return;
          }
        }
        await request.post(`/api/v1/instruction/exec/tasks/${taskId}/single-dropship-pack/submissions/`, {
          stage_idx: stageNo,
          idempotency_key: `task_${taskId}_stage_${stageNo}_single_dropship_pack`,
          hash: '',
          stats,
          snapshot,
        });
      }

      // ===== 收尾：阶段快照（在“提交记录并完成本阶段”时生成）=====
      const hasWrapUp = Array.isArray(cur?.steps) && (cur.steps || []).some((s:any) => {
        const t = String(s?.step_type || s?.renderer_key || '').trim().toUpperCase();
        return t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP';
      });
      if (hasWrapUp) {
        if (!taskId) { message.error('提交失败：缺少任务ID'); setLoading(false); return; }
        const stageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx;
        const wrapStep = (cur.steps || []).find((s:any) => {
          const t = String(s?.step_type || s?.renderer_key || '').trim().toUpperCase();
          return t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP';
        });
        const stepId = Number.isFinite(Number(wrapStep?.id)) ? Number(wrapStep.id) : null;
        const sid = stepId != null ? String(stepId) : '';
        const md = (sid && metricsDraft[sid]) ? (metricsDraft[sid] || {}) : {};

        // 本地草稿（包含 exception_containers / over_pick_groups 等）
        let localDraft: any = null;
        if (stepId != null) {
          const sessionPart = String(taskExecutionSessionId || '').trim() || 'NO_SESSION';
          const draftKey = `WRAPUP_DRAFT_${taskId}_${stageNo}_${stepId}_${sessionPart}`;
          try {
            const raw = localStorage.getItem(draftKey);
            if (raw) localDraft = JSON.parse(raw);
          } catch { localDraft = null; }
        }

        const pickArr = (v:any) => (Array.isArray(v) ? v : []);
        const putAwayOps = pickArr(md?.put_away_ops).length > 0 ? pickArr(md?.put_away_ops) : pickArr(localDraft?.put_away_ops);
        const unoperableOps = pickArr(md?.unoperable_ops).length > 0 ? pickArr(md?.unoperable_ops) : pickArr(localDraft?.unoperable_ops);
        const overPickOps = pickArr(md?.over_pick_ops).length > 0 ? pickArr(md?.over_pick_ops) : pickArr(localDraft?.over_pick_ops);
        const exceptionContainers = pickArr(md?.exception_containers).length > 0
          ? pickArr(md?.exception_containers)
          : pickArr(localDraft?.exception_containers);
        const overPickGroups = pickArr(localDraft?.over_pick_groups);
        const need2ExceptionTotalWeightG = Number(
          (md?.need2_exception_total_weight_g != null
            ? md.need2_exception_total_weight_g
            : localDraft?.need2_exception_total_weight_g) || 0,
        ) || 0;
        const remarks = String((md?.remarks != null ? md.remarks : (localDraft?.remarks != null ? localDraft.remarks : '')) || '');

        // 附件：从后端读取（跨设备权威），用于快照固化
        let attachments: any[] = [];
        if (stepId != null) {
          try {
            // 注意：后端分页默认 page_size=10（CustomPageNumberPagination.page_size=10）
            // 这里必须拉全量，否则快照里只会固化前 10 条附件。
            const pageSize = 1000;
            let page = 1;
            let all: any[] = [];
            for (let guard = 0; guard < 50; guard += 1) {
              const aResp = await request.get('/api/v1/instruction/exec/step-attachments/', {
                params: { step: stepId, status: 'active', page, page_size: pageSize, t: Date.now() } as any
              });
              const list = Array.isArray(aResp?.data?.results) ? aResp.data.results : (Array.isArray(aResp?.data) ? aResp.data : []);
              const rows = Array.isArray(list) ? list : [];
              all = all.concat(rows);
              // 有 next 就继续；否则结束
              const hasNext = !!(aResp?.data && (aResp.data as any).next);
              if (!hasNext || rows.length === 0) break;
              page += 1;
            }
            attachments = all;
          } catch {
            attachments = [];
          }
        }

        const isSortCountWrapUp = (Array.isArray(execSteps) ? execSteps : []).some((s: any) => {
          const t = String(s?.step_type || s?.renderer_key || '').trim().toUpperCase();
          return t === 'SORT_COUNT' || t === 'SORT_AND_COUNT';
        });
        // 指令产出容器（IOC，正常内容）：从 DB 权威读取并固化到快照，保证审计回看不依赖本机草稿
        let iocNormalCodes: string[] = [];
        let iocCancelledCodes: string[] = [];
        try {
          const resp = await request.get('/api/v1/storage/output-containers/', { params: { instruction_id: instrId, t: Date.now() } as any });
          const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
          const rows = Array.isArray(list) ? list : [];
          const cancelledCodes = rows
            .filter((x: any) => String(x?.status || '').trim().toLowerCase() === 'cancelled')
            .map((x: any) => String(x?.code ?? '').trim().toUpperCase())
            .filter(Boolean);
          iocCancelledCodes = Array.from(new Set(cancelledCodes));
          iocCancelledCodes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
          const codes = rows
            .filter((x:any) => String(x?.content_kind || x?.contentKind || '').toLowerCase() !== 'exception')
            .filter((x:any) => String(x?.status || '').trim().toLowerCase() !== 'cancelled')
            .map((x:any) => String(x?.code ?? '').trim().toUpperCase())
            .filter(Boolean);
          iocNormalCodes = Array.from(new Set(codes));
          iocNormalCodes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        } catch {
          iocNormalCodes = [];
          iocCancelledCodes = [];
        }
        // 操作过程生成容器：确定性口径
        // - 分拣清点指令：仅显示分拣清点阶段生成的 SC
        // - 其他指令：仅显示 IOC normal
        let operationGeneratedCodes: string[] = [];
        const operationGeneratedKind: 'storage' | 'output' = isSortCountWrapUp ? 'storage' : 'output';
        if (isSortCountWrapUp) {
          try {
            const resp = await request.get('/api/v1/storage/containers/sort-count/drafts/', {
              params: { instruction_id: instrId, t: Date.now() } as any,
            });
            const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
            const codes = (Array.isArray(list) ? list : [])
              .map((x:any) => String(x?.system_code || '').trim().toUpperCase())
              .filter(Boolean);
            operationGeneratedCodes = Array.from(new Set(codes));
            operationGeneratedCodes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
          } catch {
            operationGeneratedCodes = [];
          }
        } else {
          operationGeneratedCodes = [...iocNormalCodes];
        }

        // 仅修正“快照显示口径”：异常IOC不应同时出现在“操作过程生成容器”列表
        // 不影响任何 IOC 创建/作废/落库逻辑，仅影响本次提交快照结构。
        const exceptionCodeSetForSnapshot = new Set<string>(
          (Array.isArray(exceptionContainers) ? exceptionContainers : [])
            .map((x: any) => String(x?.code || '').trim().toUpperCase())
            .filter(Boolean),
        );
        if (exceptionCodeSetForSnapshot.size > 0) {
          iocNormalCodes = (Array.isArray(iocNormalCodes) ? iocNormalCodes : [])
            .map((x: any) => String(x || '').trim().toUpperCase())
            .filter((c: string) => !!c && !exceptionCodeSetForSnapshot.has(c));
          iocNormalCodes = Array.from(new Set(iocNormalCodes));
          iocNormalCodes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

          operationGeneratedCodes = (Array.isArray(operationGeneratedCodes) ? operationGeneratedCodes : [])
            .map((x: any) => String(x || '').trim().toUpperCase())
            .filter((c: string) => !!c && !exceptionCodeSetForSnapshot.has(c));
          operationGeneratedCodes = Array.from(new Set(operationGeneratedCodes));
          operationGeneratedCodes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        }

        // 四板块快照：无论数据是否为空，都保留板块结构（空则数组为空/字符串为空）
        const snap = {
          schema_version: 1,
          kind: 'wrap_up',
          sections: {
            step1_unoperable: {
              unoperable_ops: unoperableOps,
              exception_containers: exceptionContainers,
              need2_exception_total_weight_g: need2ExceptionTotalWeightG,
            },
            step2_overpick: {
              over_pick_ops: overPickOps,
              over_pick_groups: overPickGroups,
            },
            step3_put_away: {
              put_away_ops: putAwayOps,
              operation_generated_kind: operationGeneratedKind,
              operation_generated_codes: operationGeneratedCodes,
              ioc_normal_codes: iocNormalCodes,
              ioc_cancelled_codes: iocCancelledCodes,
            },
            step4_remarks_attachments: {
              remarks,
              attachments,
            }
          }
        };

        const stats = {
          put_away_ops_count: putAwayOps.length,
          exception_containers_count: exceptionContainers.length,
          over_pick_groups_count: overPickGroups.length,
          attachments_count: attachments.length,
        };

        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: `确认提交收尾记录并完成本阶段 - 指令：${String((instr?.system_code || instrId))}`,
            width: 980,
            icon: null,
            okText: '提交并完成',
            cancelText: '取消',
            content: (
              <div style={{ lineHeight: 1.7 }}>
                <div style={{ color:'#595959', marginBottom: 8 }}>
                  收尾阶段快照将用于后续回看与审计（按页面四板块展示；板块即使为空也会保留）。
                </div>
                <WrapUpSnapshotBlock snapshot={snap} />
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) { setLoading(false); return; }

        await request.post(`/api/v1/instruction/exec/tasks/${taskId}/wrap-up/submissions/`, {
          stage_idx: stageNo,
          idempotency_key: `task_${taskId}_stage_${stageNo}_wrap_up`,
          hash: '',
          stats,
          snapshot: snap,
        });
      }

      // 如果包含拣货步骤，弹出汇总确认并提交后端
      const hasPick = Array.isArray(cur?.steps) && (cur.steps || []).some((s:any) => String(s?.step_type || '').toUpperCase() === 'PICK');
      if (hasPick) {
        // 从全局取拣货记录（由拣货渲染器实时写入）
        const pickLogs = (window as any)?.__PICK_LOGS__ || [];
        const pickModeForStage = (() => {
          try {
            const pickStep = (cur?.steps || []).find((s:any) => String(s?.step_type || '').toUpperCase() === 'PICK');
            if (!pickStep) return 'inventory';
            const sid = String(pickStep?.id ?? '');
            const mode = String((stageUIData[sid] || {})?.pick_mode || '').trim().toLowerCase();
            return mode === 'ioc' ? 'ioc' : 'inventory';
          } catch {
            return 'inventory';
          }
        })();
        // 先进行一次“即时草稿保存”，避免用户在随后取消时丢失进度
        try {
          if (taskId) {
            const stageIdxDraft = (Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx);
            const draftTotal = (pickLogs || []).reduce((s:number, r:any)=> s + (Number(r?.qty||0) || 0), 0);
            const draftKinds = Array.from(new Set((pickLogs || []).map((r:any)=> String(r?.target_code || '').trim()).filter(Boolean))).length;
            const draftContainers = Array.from(new Set((pickLogs || []).map((r:any)=> String(r?.container_code || '').trim()).filter(Boolean))).length;
            await request.post(`/api/v1/instruction/exec/tasks/${taskId}/picking-draft/`, {
              stage_idx: stageIdxDraft,
              pick_logs: Array.isArray(pickLogs) ? pickLogs : [],
              stats: { total_qty: draftTotal, kinds: draftKinds, containers: draftContainers }
            });
          }
        } catch {}
        // 1) 容器维度汇总（用于“涉及拣货记录汇总列表”）
        const summary = (() => {
          const byCont: Record<string, { units: Record<string, number> }> = {};
          const offShelves = new Set<string>();
          (pickLogs || []).forEach((r:any) => {
            const c = String(r?.container_code || '').trim();
            const u = String(r?.target_code || '').trim();
            const q = Number(r?.qty || 0);
            if (!c || !u || !(q > 0)) return;
            if (!byCont[c]) byCont[c] = { units: {} };
            byCont[c].units[u] = (byCont[c].units[u] || 0) + q;
            if (r?.off_shelf) offShelves.add(c);
          });
          const totalQty = (pickLogs || []).reduce((s:number, r:any)=> s + (Number(r?.qty||0) || 0), 0);
          const kinds = new Set((pickLogs || []).map((r:any)=> String(r?.target_code || '').trim()).filter(Boolean)).size;
          const containers = new Set((pickLogs || []).map((r:any)=> String(r?.container_code || '').trim()).filter(Boolean)).size;
          return { byCont, offShelves, stats: { total_qty: totalQty, kinds, containers } };
        })();
        // 2) 目标物维度汇总（来自 stageUIData 的 pick_rows 作为“目标拣货数量”，与 pickLogs 对比）
        const targetSummary = (() => {
          const targetNeed: Record<string, { target: string; need: number; mark?: string }> = {};
          const actualPick: Record<string, number> = {};
          // 汇总目标需求（遍历当前阶段内的 PICK 步骤）
          (cur?.steps || []).forEach((st:any) => {
            if (String(st?.step_type || '').toUpperCase() !== 'PICK') return;
            const sid = String(st?.id ?? '');
            if (!sid) return;
            const data = stageUIData[sid] || {};
            const rows = Array.isArray(data?.pick_rows) ? data.pick_rows : [];
            rows.forEach((row:any) => {
              const t = String(row?.target_label || '').trim();
              if (!t) return;
              const need = Number(row?.target_qty || 0);
              const mark = (row?.product_sorting_code || row?.sorting_code || '') as string;
              if (!targetNeed[t]) targetNeed[t] = { target: t, need: 0, mark };
              targetNeed[t].need += (Number.isFinite(need) ? need : 0);
              if (mark && !targetNeed[t].mark) targetNeed[t].mark = mark;
            });
          });
          // 汇总实际拣货
          (pickLogs || []).forEach((r:any) => {
            const t = String(r?.target_code || '').trim();
            const q = Number(r?.qty || 0);
            if (!t || !(q > 0)) return;
            actualPick[t] = (actualPick[t] || 0) + q;
          });
          // 组合明细
          const rows = Object.keys(targetNeed).map((k) => {
            const need = Number(targetNeed[k].need || 0);
            const got = Number(actualPick[k] || 0);
            const diff = need - got;
            return {
              target: k,
              mark: targetNeed[k].mark || '-',
              need,
              got,
              diff,
              done: diff <= 0
            };
          });
          // 统计：数量合计/完成条数；差额合计=needSum - gotSum
          const totals = rows.reduce((acc, r) => {
            acc.need += r.need; acc.got += r.got;
            if (r.done) acc.doneCount += 1;
            return acc;
          }, { need: 0, got: 0, diff: 0, doneCount: 0, total: rows.length });
          totals.diff = totals.need - totals.got;
          return { rows, totals };
        })();
          const iocPreview = await (async () => {
          const iocRowsRaw: any[] = [];
          (cur?.steps || []).forEach((st:any) => {
            if (String(st?.step_type || '').toUpperCase() !== 'PICK') return;
            const sid = String(st?.id ?? '');
            if (!sid) return;
            const data = stageUIData[sid] || {};
            const arr = Array.isArray(data?.pick_rows) ? data.pick_rows : [];
            iocRowsRaw.push(...arr);
          });
          const pickedSet = new Set(
            (Array.isArray(pickLogs) ? pickLogs : [])
              .map((r:any) => String(r?.container_code || '').trim())
              .filter(Boolean)
          );
          const byCode = new Map<string, any>();
          (iocRowsRaw || []).forEach((r:any) => {
            const code = String(r?.output_code || r?.display_code || r?.container_code || r?.target_label || '').trim();
            if (!code) return;
            if (!byCode.has(code)) byCode.set(code, r);
          });
          const rows = Array.from(byCode.values()).map((r:any) => {
            const code = String(r?.output_code || r?.display_code || r?.container_code || r?.target_label || '').trim();
            return {
              code,
              location: String(r?.location_code || '-'),
              instructionCode: String(r?.instruction_code || (r?.instruction_id ? `#${r.instruction_id}` : '') || '').trim() || '-',
              containerType: String(r?.container_type_display || '-'),
              contentType: String(r?.output_content_kind_display || '-'),
              contentSummary: String(r?.output_contents_summary || '-'),
              contentDetail: String(r?.output_contents_detail || '-'),
              picked: pickedSet.has(code),
            };
          });
            // 对齐“操作阶段页面”显示口径：补齐容器类型/内容类型/内容统计/内容等字段
            const rowsWithDetails = await (async () => {
              try {
                const byCode2 = new Map<string, any>();
                const uniqCodes = Array.from(new Set((rows || []).map((x: any) => String(x?.code || '').trim()).filter(Boolean)));
                await Promise.all(uniqCodes.map(async (code: string) => {
                  try {
                    const resp = await request.get('/api/v1/storage/containers/unified/', {
                      params: {
                        container_kind: 'output',
                        system_code: code,
                        exclude_active_reservations: false,
                        page: 1,
                        page_size: 5,
                      },
                    });
                    const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                    const norm = String(code || '').trim().toUpperCase();
                    const hit = list.find((x: any) => String(x?.system_code || x?.display_code || '').trim().toUpperCase() === norm) || list[0];
                    if (hit) byCode2.set(norm, hit);
                  } catch {}
                }));
                return (rows || []).map((r: any) => {
                  const norm = String(r?.code || '').trim().toUpperCase();
                  const hit = byCode2.get(norm);
                  if (!hit) return r;
                  return {
                    ...r,
                    location: String(hit?.current_location_display || r?.location || '-'),
                    instructionCode: String(hit?.instruction_code || r?.instructionCode || '-'),
                    containerType: String(hit?.container_type_display || r?.containerType || '-'),
                    contentType: String(hit?.output_content_kind_display || r?.contentType || '-'),
                    contentSummary: String(hit?.output_contents_summary || r?.contentSummary || '-'),
                    contentDetail: String(hit?.output_contents_detail || r?.contentDetail || '-'),
                  };
                });
              } catch {
                return rows;
              }
            })();
            const picked = rowsWithDetails.filter((r:any) => r.picked).length;
            const total = rowsWithDetails.length;
            return { rows: rowsWithDetails, stats: { total, picked, todo: Math.max(total - picked, 0) } };
          })();
        // 显示确认弹窗（简化版）
        const ok = await new Promise<boolean>((resolve) => {
          // 计算整体结果标识：优先级 超额 > 不足 > 正好（IOC 使用 todo 判断）
          const hasOver = pickModeForStage === 'ioc'
            ? false
            : targetSummary.rows.some((r:any) => r.diff < 0);
          const hasUnder = pickModeForStage === 'ioc'
            ? (iocPreview.stats.todo > 0)
            : targetSummary.rows.some((r:any) => r.diff > 0);
          const allZero = pickModeForStage === 'ioc'
            ? (iocPreview.stats.total > 0 && iocPreview.stats.todo === 0)
            : (targetSummary.rows.length > 0 && targetSummary.rows.every((r:any) => r.diff === 0));
          let chipText = '拣货目标正好完成';
          let chipColor: any = 'success';
          if (hasOver) { chipText = '存在超额拣货'; chipColor = 'error'; }
          else if (hasUnder) { chipText = '存在拣货不足'; chipColor = 'warning'; }
          else if (allZero) { chipText = '拣货目标正好完成'; chipColor = 'success'; }
          else { chipText = '拣货结果'; chipColor = 'default'; }
          // 结果徽标样式（与标题风格协调的“标签芯片”）
          const chipStyle: React.CSSProperties = (() => {
            if (hasOver) {
              return { marginLeft: 12, fontWeight: 600, color:'#a8071a', background:'#fff1f0', border:'1px solid #ffa39e', padding:'2px 8px', borderRadius: 4 };
            }
            if (hasUnder) {
              return { marginLeft: 12, fontWeight: 600, color:'#0958d9', background:'#e6f4ff', border:'1px solid #91caff', padding:'2px 8px', borderRadius: 4 };
            }
            if (allZero) {
              return { marginLeft: 12, fontWeight: 600, color:'#135200', background:'#f6ffed', border:'1px solid #b7eb8f', padding:'2px 8px', borderRadius: 4 };
            }
            return { marginLeft: 12, fontWeight: 600, color:'#595959', background:'#fafafa', border:'1px solid #d9d9d9', padding:'2px 8px', borderRadius: 4 };
          })();

          Modal.confirm({
            title: (
              <div style={{ display:'flex', alignItems:'center' }}>
                {/* 状态图标：正好=绿对勾；不足=蓝叹号；超额=红叉 */}
                {hasOver ? (
                  <CloseCircleFilled style={{ color:'#ff4d4f', marginRight: 8, fontSize: 18 }} />
                ) : hasUnder ? (
                  <InfoCircleFilled style={{ color:'#1677ff', marginRight: 8, fontSize: 18 }} />
                ) : (
                  <CheckCircleFilled style={{ color:'#52c41a', marginRight: 8, fontSize: 18 }} />
                )}
                <span style={{ fontWeight: 600 }}>{`确认提交拣货记录并完成阶段 - 指令：${String((instr?.system_code || instrId))}`}</span>
                <span style={chipStyle}>{chipText}</span>
              </div>
            ),
            width: 1160,
            icon: null,
            content: (
              pickModeForStage === 'ioc'
                ? (
                  <IocPickSnapshotContent
                    rows={iocPreview.rows}
                    stats={iocPreview.stats}
                    headerRecordCount={(pickLogs || []).length}
                    detailPickLogs={(Array.isArray(pickLogs) ? pickLogs : [])}
                  />
                )
                : (
                  <PickSnapshotPagedContent
                    targetSummary={targetSummary}
                    containerSummary={summary}
                    headerRecordCount={(pickLogs || []).length}
                    detailPickLogs={(Array.isArray(pickLogs) ? pickLogs : [])}
                  />
                )
            ),
            okText: '提交并完成',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) { setLoading(false); return; }
        // 生成最小快照并提交后端
        if (!taskId) { message.error('提交失败：缺少任务ID'); setLoading(false); return; }
        const payload = {
          stage_idx: cur?.stage ?? currentStageIdx,
          idempotency_key: `task_${taskId}_stage_${cur?.stage ?? currentStageIdx}`,
          hash: '', // 可选：前端计算 hash 后传入
          stats: summary.stats,
          ui_context: {
            pick_mode: pickModeForStage,
            inventory_settlement_facts: {
              target_scope: pickModeForStage === 'ioc' ? 'instruction_output_container' : 'inventory_unit',
              expected_total: pickModeForStage === 'ioc'
                ? Number(iocPreview?.stats?.total || 0)
                : Number(targetSummary?.totals?.need || 0),
              picked_total: pickModeForStage === 'ioc'
                ? Number(iocPreview?.stats?.picked || 0)
                : Number(targetSummary?.totals?.got || 0),
              execution_session_id: String(taskExecutionSessionId || '').trim() || undefined,
            },
            ...(pickModeForStage === 'ioc' ? {
              ioc_snapshot_preview: {
                total: iocPreview.stats.total,
                picked: iocPreview.stats.picked,
                todo: iocPreview.stats.todo,
                rows: iocPreview.rows,
              }
            } : {}),
          }, // 由后端按模式执行不同提交流程，并保存 IOC 阶段快照
          pick_logs: Array.isArray(pickLogs) ? pickLogs : [],
        };
        await request.post(`/api/v1/instruction/exec/tasks/${taskId}/picking/submissions/`, payload);
      }

      const targets = (cur.steps || []).filter((s:any)=> !isStepDone(s));
      await Promise.all(targets.map(async (st:any) => {
        const sid = String(st?.id ?? '');
        const metrics = (sid && (metricsDraft as any)?.[sid] && typeof (metricsDraft as any)[sid] === 'object')
          ? (metricsDraft as any)[sid]
          : {};
        await request.post(`/api/v1/instruction/exec/steps/${st.id}/submit/`, { metrics });
      }));
      // 触发后端阶段完成日志（若已提供）
      try {
        await request.post(`/api/v1/instruction/instructions/${instrId}/complete-current-stage/`, {});
      } catch {}
      message.success('本阶段已完成');
      // 若为最后阶段（通常为“收尾”），完成后回到“执行待办-待领取”页，便于继续领取新任务
      if (isLastStage) {
        try {
          const base = location.pathname.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/staff';
          navigate(`${base}/execution`, { replace: true });
          return;
        } catch {}
      }
      await load();
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '完成失败');
    } finally {
      setLoading(false);
    }
  };

  //（已移除重做功能）

  return (
    <div style={{ padding: 12 }}>
      <Card loading={loading} title={(() => {
        if (!instr) return '指令执行';
        const sc = instr.system_code || instr.id;
        const iname = (instr.name || '').toString().trim();
        const tplName = (instr?.template_detail?.name || instr?.template_snapshot?.name || '').toString().trim();
        const tplCode = (instr?.template_detail?.system_code || instr?.template_snapshot?.system_code || '').toString().trim();
        const toDetail = () => { try { navigate(`/dashboard/instructions/${instrId}`); } catch {} };
        const left = (
          <span>
            指令执行 -{' '}
            <a onClick={toDetail} style={{ color: '#1677ff' }}>
              {sc}{iname ? ` · ${iname}` : ''}
            </a>
          </span>
        ) as any;
        const projectNode = (() => {
          try {
            const arr = Array.isArray(instr?.projects_detail) ? instr.projects_detail : [];
            if (arr.length > 0) {
              const first = arr[0];
              const code = first?.project_code || first?.code || first?.id;
              const name = first?.name || '';
              if (name || code) {
                return (
                  <span>
                    <span>{name || '-'}</span>
                    <span>{'  '}</span>
                    <span style={{ color: '#1677ff' }}>{code || ''}</span>
                  </span>
                );
              }
            }
          } catch {}
          try {
            const pd = instr?.project_detail || instr?.project;
            if (pd && (pd.project_code || pd.name)) {
              const code = pd.project_code || pd.code || pd.id;
              const name = pd.name || '';
              return (
                <span>
                  <span>{name || '-'}</span>
                  <span>{'  '}</span>
                  <span style={{ color: '#1677ff' }}>{code || ''}</span>
                </span>
              );
            }
          } catch {}
          const code = instr?.project_code || instr?.project_id;
          const name = instr?.project_name;
          if (name || code) {
            return (
              <span>
                <span>{name || '-'}</span>
                <span>{'  '}</span>
                <span style={{ color: '#1677ff' }}>{code || ''}</span>
              </span>
            );
          }
          return <span style={{ color: '#999' }}>-</span>;
        })();
        const middle = (
          <Space size={16} align="center" style={{ fontWeight: 'normal' }}>
            <span style={{ fontWeight: 'normal' }}>项目：{projectNode}</span>
            <span style={{ fontWeight: 'normal' }}>优先级：<Tag color={instr.priority==='super'?'red':(instr.priority==='high'?'orange':'default')}>{instr.priority || 'normal'}</Tag></span>
          </Space>
        );
        const right = (tplName || tplCode) ? `${tplName || ''}${tplName && tplCode ? ' · ' : ''}${tplCode || ''}` : '';
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{left}</span>
            <span>{middle}</span>
            {right ? <span style={{ color: '#ff7875' }}>{right}</span> : null}
          </div>
        );
      })()}>
        {instr ? (
          <div>
            {(() => {
              try {
                const tips = (instr?.execution_tips || '').toString().trim();
                if (!tips) return null;
                return (
                  <Alert
                    style={{ marginBottom: 12, background: '#ffff00', border: '1px solid #ffff00', color: '#ff0000' }}
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleOutlined style={{ color: '#ff0000' }} />}
                    message={(
                      <span style={{ fontWeight: 700, color: '#ff0000' }}>
                        执行提醒
                        <span
                          onClick={() => setTipsCollapsed(v => !v)}
                          style={
                            tipsCollapsed
                              ? { cursor:'pointer', marginLeft: 12, fontSize: 12, fontWeight: 700, color:'#ff0000', border:'1px solid #ff0000', background:'transparent', padding:'1px 6px', borderRadius: 4 }
                              : { cursor:'pointer', marginLeft: 12, fontSize: 12, fontWeight: 700, color:'#ffff00', border:'1px solid #ff0000', background:'#ff0000', padding:'1px 6px', borderRadius: 4 }
                          }
                        >
                          {tipsCollapsed ? '展开' : '收起'}
                        </span>
                      </span>
                    )}
                    description={tipsCollapsed ? null : <div style={{ whiteSpace: 'pre-wrap', color: '#ff0000', fontWeight: 700 }}>{tips}</div>}
                  />
                );
              } catch {}
              return null;
            })()}
            {/* 头部信息（项目与优先级）已移动到标题中部；状态不再展示 */}
            {/* 阶段导航（左→右） */}
            {stages.length > 0 && (
              <div style={{ margin: '12px 0' }}>
                <Steps
                  current={currentStageIdx}
                  onChange={(idx) => {
                    // 仅允许返回或前往已完成阶段；向前跳跃需要完成当前与中间的必做阶段
                    if (idx <= currentStageIdx) { setCurrentStageIdx(idx); return; }
                    // 验证中间阶段
                    for (let i = currentStageIdx; i < idx; i++) {
                      const g = stages[i];
                      if (!g) continue;
                      if (!(g.optional || isStageCompleted(g.steps))) {
                        message.warning(`请先完成第 ${g.stage ?? (i+1)} 阶段的必做步骤`);
                        return;
                      }
                    }
                    setCurrentStageIdx(idx);
                  }}
                  items={stages.map((g, i) => {
                    const nm = getStageDisplayName(g.steps || [], 2);
                    const title = `阶段${g.stage ?? i}${nm ? `-${nm}` : ''}`;
                    const isCurrent = i === currentStageIdx;
                    const titleNode = (
                      <span
                        style={
                          isCurrent
                            ? { background: '#1677ff', color: '#ffffff', fontWeight: 700, padding: '2px 6px', borderRadius: 4 }
                            : undefined
                        }
                      >
                        {title}
                      </span>
                    );
                    return {
                      title: titleNode,
                      status: isStageCompleted(g.steps) ? 'finish' : (i === currentStageIdx ? 'process' : 'wait'),
                    } as any;
                  })}
                />
              </div>
            )}
            <Divider />
            <div>
              {/* 下半部分：当前操作阶段卡片 */}
              {Array.isArray(execSteps) && execSteps.length > 0 ? (
                (() => {
                  const cur = stages[currentStageIdx];
                  const nm = getStageDisplayName(cur?.steps || [], 3);
                  const statusText = (() => {
                    try {
                      const steps = cur?.steps || [];
                      const allDoneOrSkipped = isStageCompleted(steps);
                      if (!allDoneOrSkipped) return '未完成';
                      const allSkipped = steps.length > 0 && steps.every((s:any) => !!(s?.meta && s.meta.skipped));
                      return allSkipped ? '已跳过' : '已完成';
                    } catch { return '未完成'; }
                  })();
                  // 当前阶段是否包含拣货步骤（用于决定是否展示“拣货快照”入口）
                  const stageHasPick = Array.isArray(cur?.steps) && (cur.steps || []).some(
                    (s:any) => String(s?.step_type || s?.renderer_key || '').toUpperCase() === 'PICK'
                  );
                  // 当前阶段是否包含“换标+装箱/装袋”组合（用于展示快照入口）
                  const stageHasRelabelPack = (() => {
                    try {
                      const steps = Array.isArray(cur?.steps) ? cur.steps : [];
                      const types = steps.map((s:any) => String(s?.step_type || '').trim().toUpperCase());
                      const hasRelabel = types.includes('RELABEL') || steps.some((s:any) => String(s?.title || '').includes('换标'));
                      const hasPack = types.some((t:string) => (
                        t === 'BOX/BAG_PACK' || t === 'BOX_PACK' || t === 'BAG_PACK' || t === 'BOX' || t === 'BAG'
                      )) || steps.some((s:any) => String(s?.title || '').includes('装箱') || String(s?.title || '').includes('装袋'));
                      return hasRelabel && hasPack;
                    } catch { return false; }
                  })();
                  const stageHasSingleDropshipPack = (() => {
                    try {
                      const steps = Array.isArray(cur?.steps) ? cur.steps : [];
                      const types = steps.map((s:any) => String(s?.step_type || s?.renderer_key || '').trim().toUpperCase());
                      const hasRelabel = types.includes('RELABEL') || steps.some((s:any) => String(s?.title || '').includes('换标'));
                      const hasPack = types.some((t:string) => (
                        t === 'BOX/BAG_PACK' || t === 'BOX_PACK' || t === 'BAG_PACK' || t === 'BOX' || t === 'BAG'
                      )) || steps.some((s:any) => String(s?.title || '').includes('装箱') || String(s?.title || '').includes('装袋'));
                      const tplName = String(instr?.template_detail?.name || instr?.template_snapshot?.name || '').trim().toLowerCase();
                      const tplCode = String(
                        instr?.template_detail?.system_code
                        || instr?.template_snapshot?.system_code
                        || instr?.template_detail?.code
                        || instr?.template_snapshot?.code
                        || ''
                      ).trim().toLowerCase();
                      const isRegularOutboundTemplate = (
                        tplName.includes('常规出库')
                        || (tplCode.includes('regular') && tplCode.includes('outbound'))
                      );
                      return hasPack && !hasRelabel && !isRegularOutboundTemplate;
                    } catch { return false; }
                  })();
                  const stageHasRegularOutboundPack = (() => {
                    try {
                      const steps = Array.isArray(cur?.steps) ? cur.steps : [];
                      const types = steps.map((s:any) => String(s?.step_type || s?.renderer_key || '').trim().toUpperCase());
                      const hasRelabel = types.includes('RELABEL') || steps.some((s:any) => String(s?.title || '').includes('换标'));
                      const hasPack = types.some((t:string) => (
                        t === 'BOX/BAG_PACK' || t === 'BOX_PACK' || t === 'BAG_PACK' || t === 'BOX' || t === 'BAG'
                      )) || steps.some((s:any) => String(s?.title || '').includes('装箱') || String(s?.title || '').includes('装袋'));
                      const tplName = String(instr?.template_detail?.name || instr?.template_snapshot?.name || '').trim().toLowerCase();
                      const tplCode = String(
                        instr?.template_detail?.system_code
                        || instr?.template_snapshot?.system_code
                        || instr?.template_detail?.code
                        || instr?.template_snapshot?.code
                        || ''
                      ).trim().toLowerCase();
                      const isRegularOutboundTemplate = (
                        tplName.includes('常规出库')
                        || (tplCode.includes('regular') && tplCode.includes('outbound'))
                      );
                      return hasPack && !hasRelabel && isRegularOutboundTemplate;
                    } catch { return false; }
                  })();
                  // 库存非标准操作：当前阶段已提交的步骤上有快照时，展示"查看快照"入口
                  const nonstdSnapshotStep = (() => {
                    try {
                      const steps = Array.isArray(cur?.steps) ? cur.steps : [];
                      return steps.find((s: any) => {
                        const t = String(s?.step_type || '').trim().toUpperCase();
                        return (t === 'NONSTD_RECORD' || t === 'NONSTD_SUBMIT') && !!s?.collected_metrics?.nonstd_snapshot;
                      }) || null;
                    } catch { return null; }
                  })();
                  // 当前阶段是否包含“收尾”步骤（用于展示快照入口）
                  const stageHasWrapUp = (() => {
                    try {
                      const steps = Array.isArray(cur?.steps) ? cur.steps : [];
                      return steps.some((s:any) => {
                        const t = String(s?.step_type || s?.renderer_key || '').trim().toUpperCase();
                        return t === 'WRAP_UP' || t === 'SUBMIT_AND_WRAPUP';
                      });
                    } catch { return false; }
                  })();
                  const stageHasSortCount = (() => {
                    try {
                      const steps = Array.isArray(cur?.steps) ? cur.steps : [];
                      return steps.some((s:any) => {
                        const t = String(s?.step_type || s?.renderer_key || '').trim().toUpperCase();
                        return t === 'SORT_AND_COUNT' || t === 'SORT_COUNT';
                      });
                    } catch { return false; }
                  })();
                  // 当前阶段号（用于快照归属校验）
                  const currentStageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx;
                  const stageHasRequired = Array.isArray(cur?.steps) && (cur.steps || []).some((s:any) => !!s?.required);
                  const titleLeft = (
                    <span style={{ fontWeight: 600 }}>
                      当前操作阶段：{' '}
                      <span style={{ fontWeight: 600 }}>{`阶段${cur?.stage ?? currentStageIdx}${nm ? `-${nm}` : ''}`}</span>
                      {stageHasRequired ? <Tag color="red" style={{ marginLeft: 8 }}>必做</Tag> : null}
                      {(() => {
                        const styleBase: React.CSSProperties = { marginLeft: 12, fontWeight: 600, borderWidth: 1, borderStyle: 'solid' };
                        let style: React.CSSProperties = styleBase;
                        if (statusText === '未完成') {
                          style = { ...styleBase, background: '#fff7e6', borderColor: '#faad14', color: '#ad6800' };
                        } else if (statusText === '已完成') {
                          style = { ...styleBase, background: '#f6ffed', borderColor: '#b7eb8f', color: '#135200' };
                        } else if (statusText === '已跳过') {
                          style = { ...styleBase, background: '#f0f0f0', borderColor: '#d9d9d9', color: '#595959' };
                        }
                        return (
                          <>
                            <span style={{ ...style, padding: '2px 8px', borderRadius: 4 }}>{statusText}</span>
                            {showStageHelp ? (
                              <Tooltip title="查看当前操作阶段的使用说明（记录生成/落库规则/打印口径）">
                                <Button
                                  size="small"
                                  type="link"
                                  icon={<InfoCircleFilled />}
                                  style={{ marginLeft: 8 }}
                                  onClick={() => setStageHelpOpen(true)}
                                >
                                  使用说明
                                </Button>
                              </Tooltip>
                            ) : null}
                            {(statusText === '已完成' && stageHasSortCount) ? (
                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                loading={scSnapshotLoading}
                                onClick={async () => {
                                  if (!taskId) { message.warning('缺少执行任务ID'); return; }
                                  try {
                                    setScSnapshotLoading(true);
                                    const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/sort-count-submissions/`, { params: { latest: true, t: Date.now() } as any });
                                    const result = resp?.data?.result;
                                    if (!result) { message.info('暂无分拣清点快照'); return; }
                                    const resultStageNo = Number.isFinite(Number(result?.stage_idx)) ? Number(result.stage_idx) : null;
                                    if (resultStageNo != null && Number.isFinite(Number(currentStageNo)) && Number(resultStageNo) !== Number(currentStageNo)) {
                                      message.info('本阶段暂无分拣清点快照');
                                      return;
                                    }
                                    setScSnapshotData(result);
                                    setTimeout(() => setScSnapshotVisible(true), 0);
                                  } catch (e:any) {
                                    message.error(e?.response?.data?.detail || '加载分拣清点快照失败');
                                  } finally {
                                    setScSnapshotLoading(false);
                                  }
                                }}
                              >
                                查看分拣清点快照
                              </Button>
                            ) : null}
                            {/* 仅当“当前阶段包含拣货步骤”且“本阶段已完成”时，才展示“查看拣货快照”（避免跨阶段误显示） */}
                            {(statusText === '已完成' && stageHasPick) ? (
                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                onClick={async () => {
                                  if (!taskId) { message.warning('缺少执行任务ID'); return; }
                                  try {
                                    setSnapshotLoading(true);
                                    // 调试：按钮点击开始
                                    try { console.debug('[PickSnap][BTN] start taskId=', taskId); } catch {}
                                    // 让出同一事件循环，避免与可能的阶段重渲染竞争
                                    await Promise.resolve();
                                    // 使用专用接口读取最新提交（避免读到旧详情）
                                    const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/picking-submissions/`, { params: { latest: true, t: Date.now() } });
                                    const result = resp?.data?.result;
                                    try { console.debug('[PickSnap][RESP] ok=', resp?.data?.ok, 'has result=', !!result); } catch {}
                                    if (!result) { message.info('暂无拣货快照'); return; }
                                    // 关键：校验快照归属阶段，避免在其它阶段页面误展示“上一阶段拣货快照”
                                    const resultStageNo = Number.isFinite(Number(result?.stage_idx))
                                      ? Number(result.stage_idx)
                                      : Number.isFinite(Number((result as any)?.stage))
                                        ? Number((result as any).stage)
                                        : null;
                                    if (resultStageNo != null && Number.isFinite(Number(currentStageNo)) && Number(resultStageNo) !== Number(currentStageNo)) {
                                      message.info('本阶段暂无拣货快照');
                                      return;
                                    }
                                    const headStats = {
                                      total_qty: Number(result?.total_qty || 0),
                                      kinds: Number(result?.kinds || 0),
                                      containers: Number(result?.containers || 0)
                                    };
                                    const snapRaw = (result?.snapshot || {}) as any;
                                    // 若快照里缺少 pick_logs，则用 items 重建最小 pick_logs
                                    let pickLogsFromSnap = Array.isArray(snapRaw?.pick_logs) ? snapRaw.pick_logs : [];
                                    if ((!pickLogsFromSnap || pickLogsFromSnap.length === 0) && Array.isArray(result?.items)) {
                                      pickLogsFromSnap = (result.items as any[]).map(it => ({
                                        container_code: String(it?.container_code || ''),
                                        target_code: String(it?.unit_code || ''),
                                        sorting_mark: String(it?.sorting_mark || ''),
                                        qty: Number(it?.qty || 0),
                                        off_shelf: !!it?.off_shelf,
                                      }));
                                    }
                                    const snap = {
                                      ...(snapRaw || {}),
                                      stats: Object.keys(snapRaw?.stats || {}).length ? snapRaw.stats : headStats,
                                      pick_logs: pickLogsFromSnap,
                                      submitted_at: snapRaw?.submitted_at || result?.submitted_at,
                                      submitted_by: snapRaw?.submitted_by || result?.submitted_by_id,
                                    };
                                    setSnapshotData(snap as any);
                                    // 读取该次提交的阶段 pick_rows（用于还原目标需求）
                                    let pickRowsForStage: any[] = [];
                                    try {
                                      const stageNo = Number(currentStageNo ?? 0);
                                      if (Number.isFinite(stageNo)) {
                                        const uiResp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/stage-ui/`, { params: { stage: stageNo } });
                                        const payload = uiResp?.data || {};
                                        const list = Array.isArray(payload?.steps) ? payload.steps : [];
                                        list.forEach((s:any) => {
                                          if (String(s?.step_type || '').toUpperCase() !== 'PICK') return;
                                          const rows = Array.isArray(s?.data?.pick_rows) ? s.data.pick_rows : [];
                                          pickRowsForStage.push(...rows);
                                        });
                                      }
                                    } catch {}
                                    const tSummary = buildTargetSummary(pickRowsForStage, pickLogsFromSnap);
                                    const cSummary = buildContainerSummary(pickLogsFromSnap);
                                    const isIocSnap = String((snap as any)?.pick_mode || (snap as any)?.ui_context?.pick_mode || '').trim().toLowerCase() === 'ioc';
                                    let flags = computeFlags(tSummary);
                                    if (isIocSnap) {
                                      const pickedSet = new Set(
                                        (Array.isArray(pickLogsFromSnap) ? pickLogsFromSnap : [])
                                          .map((r:any) => String(r?.container_code || '').trim())
                                          .filter(Boolean)
                                      );
                                      const previewInSnap = (snap as any)?.ui_context?.ioc_snapshot_preview || {};
                                      let iocRows: any[] = Array.isArray(previewInSnap?.rows) ? previewInSnap.rows : [];
                                      if (!iocRows.length) {
                                        const byCode = new Map<string, any>();
                                        (Array.isArray(pickRowsForStage) ? pickRowsForStage : []).forEach((r:any) => {
                                          const code = String(r?.output_code || r?.display_code || r?.container_code || r?.target_label || '').trim();
                                          if (!code || byCode.has(code)) return;
                                          byCode.set(code, r);
                                        });
                                        iocRows = Array.from(byCode.values()).map((r:any) => {
                                          const code = String(r?.output_code || r?.display_code || r?.container_code || r?.target_label || '').trim();
                                          return {
                                            code,
                                            location: String(r?.location_code || '-'),
                                            instructionCode: String(r?.instruction_code || (r?.instruction_id ? `#${r.instruction_id}` : '') || '').trim() || '-',
                                            containerType: String(r?.container_type_display || '-'),
                                            contentType: String(r?.output_content_kind_display || '-'),
                                            contentSummary: String(r?.output_contents_summary || '-'),
                                            contentDetail: String(r?.output_contents_detail || '-'),
                                            picked: pickedSet.has(code),
                                          };
                                        });
                                      } else {
                                        iocRows = iocRows.map((r:any) => {
                                          const code = String(r?.code || '').trim();
                                          return {
                                            code,
                                            location: String(r?.location || '-'),
                                            instructionCode: String(r?.instructionCode || '-'),
                                            containerType: String(r?.containerType || '-'),
                                            contentType: String(r?.contentType || '-'),
                                            contentSummary: String(r?.contentSummary || '-'),
                                            contentDetail: String(r?.contentDetail || '-'),
                                            picked: pickedSet.has(code) || !!r?.picked,
                                          };
                                        });
                                      }
                                      const iocTotal = Number.isFinite(Number(previewInSnap?.total)) ? Number(previewInSnap.total) : iocRows.length;
                                      const iocPicked = Number.isFinite(Number(previewInSnap?.picked))
                                        ? Number(previewInSnap.picked)
                                        : iocRows.filter((r:any) => !!r?.picked).length;
                                      const iocTodo = Number.isFinite(Number(previewInSnap?.todo))
                                        ? Number(previewInSnap.todo)
                                        : Math.max(iocTotal - iocPicked, 0);
                                      setSnapshotIocPreview({
                                        rows: iocRows,
                                        stats: { total: iocTotal, picked: iocPicked, todo: iocTodo },
                                        headerRecordCount: (Array.isArray(pickLogsFromSnap) ? pickLogsFromSnap.length : 0),
                                        detailPickLogs: Array.isArray(pickLogsFromSnap) ? pickLogsFromSnap : [],
                                      });
                                      flags = {
                                        hasOver: false,
                                        hasUnder: iocTodo > 0,
                                        allZero: iocTotal > 0 && iocTodo === 0,
                                      };
                                    } else {
                                      setSnapshotIocPreview(null);
                                    }
                                    setSnapshotSummary({ targetSummary: tSummary, containerSummary: cSummary, flags });
                                    // 微延迟后再打开，避免与可能的阶段重渲染竞态
                                    setTimeout(() => {
                                      try { console.debug('[PickSnap][OPEN]'); } catch {}
                                      setSnapshotVisible(true);
                                    }, 0);
                                  } catch (e:any) {
                                    message.error(e?.response?.data?.detail || '加载拣货快照失败');
                                  } finally {
                                    setSnapshotLoading(false);
                                  }
                                }}
                                loading={snapshotLoading}
                              >
                                查看拣货快照
                              </Button>
                            ) : null}
                            {(statusText === '已完成' && stageHasRelabelPack) ? (
                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                loading={rpSnapshotLoading}
                                onClick={async () => {
                                  if (!taskId) { message.warning('缺少执行任务ID'); return; }
                                  try {
                                    setRpSnapshotLoading(true);
                                    const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/relabel-pack-submissions/`, { params: { latest: true, t: Date.now() } as any });
                                    const result = resp?.data?.result;
                                    if (!result) { message.info('暂无换标+装箱快照'); return; }
                                    const resultStageNo = Number.isFinite(Number(result?.stage_idx)) ? Number(result.stage_idx) : null;
                                    const currentStageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur.stage) : currentStageIdx;
                                    if (resultStageNo != null && Number.isFinite(Number(currentStageNo)) && Number(resultStageNo) !== Number(currentStageNo)) {
                                      message.info('本阶段暂无换标+装箱快照');
                                      return;
                                    }
                                    setRpSnapshotData(result);
                                    setTimeout(() => setRpSnapshotVisible(true), 0);
                                  } catch (e:any) {
                                    message.error(e?.response?.data?.detail || '加载换标+装箱快照失败');
                                  } finally {
                                    setRpSnapshotLoading(false);
                                  }
                                }}
                              >
                                查看换标+装箱快照
                              </Button>
                            ) : null}
                            {(statusText === '已完成' && stageHasSingleDropshipPack) ? (
                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                loading={sdpSnapshotLoading}
                                onClick={async () => {
                                  if (!taskId) { message.warning('缺少执行任务ID'); return; }
                                  try {
                                    setSdpSnapshotLoading(true);
                                    const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/single-dropship-pack-submissions/`, { params: { latest: true, t: Date.now() } as any });
                                    const result = resp?.data?.result;
                                    if (!result) { message.info('暂无配货|装箱|贴单快照'); return; }
                                    const resultStageNo = Number.isFinite(Number(result?.stage_idx)) ? Number(result.stage_idx) : null;
                                    if (resultStageNo != null && Number.isFinite(Number(currentStageNo)) && Number(resultStageNo) !== Number(currentStageNo)) {
                                      message.info('本阶段暂无配货|装箱|贴单快照');
                                      return;
                                    }
                                    setSdpSnapshotData(result);
                                    setTimeout(() => setSdpSnapshotVisible(true), 0);
                                  } catch (e:any) {
                                    message.error(e?.response?.data?.detail || '加载配货|装箱|贴单快照失败');
                                  } finally {
                                    setSdpSnapshotLoading(false);
                                  }
                                }}
                              >
                                查看配货|装箱|贴单快照
                              </Button>
                            ) : null}
                            {(statusText === '已完成' && stageHasRegularOutboundPack) ? (
                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                loading={ropSnapshotLoading}
                                onClick={async () => {
                                  if (!taskId) { message.warning('缺少执行任务ID'); return; }
                                  try {
                                    setRopSnapshotLoading(true);
                                    const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/regular-outbound-pack-submissions/`, { params: { latest: true, t: Date.now() } as any });
                                    const result = resp?.data?.result;
                                    if (!result) { message.info('暂无常规出库配货|装箱|贴单快照'); return; }
                                    const resultStageNo = Number.isFinite(Number(result?.stage_idx)) ? Number(result.stage_idx) : null;
                                    if (resultStageNo != null && Number.isFinite(Number(currentStageNo)) && Number(resultStageNo) !== Number(currentStageNo)) {
                                      message.info('本阶段暂无常规出库配货|装箱|贴单快照');
                                      return;
                                    }
                                    setRopSnapshotData(result);
                                    setTimeout(() => setRopSnapshotVisible(true), 0);
                                  } catch (e:any) {
                                    message.error(e?.response?.data?.detail || '加载常规出库配货|装箱|贴单快照失败');
                                  } finally {
                                    setRopSnapshotLoading(false);
                                  }
                                }}
                              >
                                查看常规出库配货|装箱|贴单快照
                              </Button>
                            ) : null}
                            {(statusText === '已完成' && stageHasWrapUp) ? (
                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                loading={wuSnapshotLoading}
                                onClick={async () => {
                                  if (!taskId) { message.warning('缺少执行任务ID'); return; }
                                  try {
                                    setWuSnapshotLoading(true);
                                    const resp = await request.get(`/api/v1/instruction/exec/tasks/${taskId}/wrap-up-submissions/`, { params: { latest: true, t: Date.now() } as any });
                                    const result = resp?.data?.result;
                                    if (!result) { message.info('暂无收尾快照'); return; }
                                    const resultStageNo = Number.isFinite(Number(result?.stage_idx)) ? Number(result.stage_idx) : null;
                                    const currentStageNo = Number.isFinite(Number(cur?.stage)) ? Number(cur.stage) : currentStageIdx;
                                    if (resultStageNo != null && Number.isFinite(Number(currentStageNo)) && Number(resultStageNo) !== Number(currentStageNo)) {
                                      message.info('本阶段暂无收尾快照');
                                      return;
                                    }
                                    setWuSnapshotData(result);
                                    setTimeout(() => setWuSnapshotVisible(true), 0);
                                  } catch (e:any) {
                                    message.error(e?.response?.data?.detail || '加载收尾快照失败');
                                  } finally {
                                    setWuSnapshotLoading(false);
                                  }
                                }}
                              >
                                查看收尾快照
                              </Button>
                            ) : null}
                            {(statusText === '已完成' && nonstdSnapshotStep) ? (
                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                onClick={() => setNonstdSnapshotView(nonstdSnapshotStep.collected_metrics.nonstd_snapshot)}
                              >
                                {String(nonstdSnapshotStep?.step_type || '').toUpperCase() === 'NONSTD_RECORD' ? '查看执行记录快照' : '查看确认完成快照'}
                              </Button>
                            ) : null}
                          </>
                        );
                      })()}
                    </span>
                  );
                  // 根据阶段状态禁用按钮
                  const stepsNow = cur?.steps || [];
                  const allDoneOrSkipped = isStageCompleted(stepsNow);
                  const allSkipped = stepsNow.length > 0 && stepsNow.every((s:any) => !!(s?.meta && s.meta.skipped));
                  const wrapUpGate = getWrapUpPutAwayGate(cur);
                  const sortCountRetentionGate = (() => {
                    const sortCountSteps = (Array.isArray(stepsNow) ? stepsNow : []).filter((s: any) => {
                      const key = String(s?.renderer_key || s?.step_type || '').trim().toUpperCase();
                      return key === 'SORT_AND_COUNT' || key === 'SORT_COUNT';
                    });
                    const hits = sortCountSteps
                      .map((s: any) => {
                        const sid = String(s?.id ?? '');
                        const draft = sid ? (metricsDraft[sid] || {}) : {};
                        const collected = (s?.collected_metrics && typeof s.collected_metrics === 'object') ? s.collected_metrics : {};
                        const merged = { ...collected, ...draft };
                        const blocked = !!merged?.sort_count_has_location_retention;
                        const totalQty = Math.max(0, Number(merged?.sort_count_location_retention_total_qty || 0));
                        const locationCount = Math.max(0, Number(merged?.sort_count_location_retention_location_count || 0));
                        return { blocked, totalQty, locationCount };
                      })
                      .filter((x: any) => x.blocked || x.totalQty > 0 || x.locationCount > 0);
                    const totalQty = hits.reduce((sum: number, x: any) => sum + Math.max(0, Number(x?.totalQty || 0)), 0);
                    const locationCount = hits.reduce((sum: number, x: any) => sum + Math.max(0, Number(x?.locationCount || 0)), 0);
                    return { blocked: hits.length > 0, totalQty, locationCount };
                  })();
                  // 库存非标准操作 · 确认完成：容器还没核对完（或页面还没拿到核对进度）时禁用按钮，全部处理完才启用
                  const nonstdConfirmGate = (() => {
                    if (String(instr?.template_snapshot?.meta?.engine || '') !== 'nonstandard_ops') return { applies: false, blocked: false, pending: 0, unlabeled: 0, noLocation: 0, destroyUnanswered: false };
                    const confirmStep = (Array.isArray(stepsNow) ? stepsNow : []).find((s: any) => String(s?.step_type || '').trim().toUpperCase() === 'NONSTD_SUBMIT');
                    if (!confirmStep || isStepDone(confirmStep)) return { applies: false, blocked: false, pending: 0, unlabeled: 0, noLocation: 0, destroyUnanswered: false };
                    const sid = String(confirmStep?.id ?? '');
                    const gate = sid ? nonstdConfirmGates[sid] : undefined;
                    if (!gate || typeof gate !== 'object') return { applies: true, blocked: true, pending: 0, unlabeled: 0, noLocation: 0, destroyUnanswered: false, unknown: true };
                    const pending = Math.max(0, Number((gate as any).pending || 0));
                    const destroyUnanswered = !!(gate as any).destroy_unanswered;
                    return {
                      applies: true, blocked: pending > 0 || destroyUnanswered, pending, destroyUnanswered,
                      unlabeled: Math.max(0, Number((gate as any).unlabeled || 0)),
                      noLocation: Math.max(0, Number((gate as any).no_location || 0)),
                    };
                  })();
                  const disableFinish = allSkipped || (allDoneOrSkipped && !allSkipped) || (wrapUpGate.hasWrapUp && Number(wrapUpGate.todo || 0) > 0) || sortCountRetentionGate.blocked || nonstdConfirmGate.blocked;
                  const finishDisabledTip = nonstdConfirmGate.blocked
                    ? (nonstdConfirmGate.pending > 0
                      ? `还有 ${nonstdConfirmGate.pending} 个容器没有处理完（${nonstdConfirmGate.unlabeled} 个没有标记“已贴标签”，${nonstdConfirmGate.noLocation} 个没有记录放置位置）。全部处理完（标签打错、实际没有的容器请标记为“未使用”并写明原因）后才能提交本阶段。`
                      : (nonstdConfirmGate.destroyUnanswered
                        ? '还没有确认销毁情况：有直接销毁请添加销毁记录，没有请勾选“本指令没有发生销毁”，才能提交本阶段。'
                        : '正在读取容器核对进度……'))
                    : sortCountRetentionGate.blocked
                    ? `仓位当前留存中仍有已分拣商品未放入仓储容器（${sortCountRetentionGate.locationCount}个仓位 ${sortCountRetentionGate.totalQty}件），全部放入容器后才能提交完成本阶段。`
                    : ((wrapUpGate.hasWrapUp && Number(wrapUpGate.todo || 0) > 0)
                      ? `仍有 ${Number(wrapUpGate.todo || 0)} 个容器未放入仓位，请先完成“步骤3: 所有容器放入仓位”。`
                      : ((wrapUpGate.hasWrapUp && wrapUpGate.blocked)
                        ? '所有指令产出容器和仓储容器必须放入仓位,才能启用此按钮.'
                        : undefined));
                  // “已跳过”时允许“取消跳过”
                  const disableSkip = (statusText === '已跳过')
                    ? false
                    : ((allSkipped || (allDoneOrSkipped && !allSkipped) || !stages[currentStageIdx]?.optional));
                  return (
                    <div style={{ background:'#fafafa', border:'1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
                        {titleLeft}
                        <Space size={8}>
                          {null}
                          <Tooltip title={finishDisabledTip}>
                            <span>
                              <Button
                                size="small"
                                onClick={finishCurrentStage}
                                disabled={disableFinish}
                                style={{
                                  background: disableFinish ? '#d9d9d9' : '#fa541c',
                                  borderColor: disableFinish ? '#d9d9d9' : '#fa541c',
                                  color: '#ffffff',
                                }}
                              >
                                提交记录并完成本阶段
                              </Button>
                            </span>
                          </Tooltip>
                        </Space>
                      </div>
                      {/* 阶段内部：动态渲染步骤 UI（最小可用集） */}
                      <div style={{ display:'flex', flexDirection:'column', gap: 12 }}>
                        {(cur?.steps || []).map((st:any) => {
                          const sid = String(st?.id ?? '');
                          const schema = stageUISchemas[sid] || {};
                          const comps = Array.isArray(schema?.components) ? schema.components : [];
                          const draft = metricsDraft[sid] || {};
                          const setVal = (key: string, val: any) => {
                            setMetricsDraft(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [key]: val } }));
                          };
                            // 组合渲染拦截：RELABEL + BOX/BAG_PACK 同组聚合
                            try {
                              const groupKey = String(st?.group_key || '').trim();
                              const stType = String(st?.step_type || st?.renderer_key || '').toUpperCase();
                              if (groupKey) {
                                const isRelabel = (t:string) => String(t||'').toUpperCase() === 'RELABEL';
                                const isPack = (t:string) => {
                                  const k = String(t||'').toUpperCase();
                                  return k === 'BOX/BAG_PACK' || k === 'BOX_PACK' || k === 'BAG_PACK' || k === 'BOX' || k === 'BAG';
                                };
                                const peers = (cur?.steps || []).filter((x:any) => String(x?.group_key || '').trim() === groupKey);
                                const relabelPeer = peers.find((x:any) => isRelabel(x?.step_type || x?.renderer_key));
                                const packPeer = peers.find((x:any) => isPack(x?.step_type || x?.renderer_key));
                                try {
                                  if (relabelPeer || packPeer) {
                                    console.debug('[RelabelPack][DETECT] groupKey=', groupKey, 'hasRelabel=', !!relabelPeer, 'hasPack=', !!packPeer, 'relabelBaselines=', Array.isArray((relabelPeer as any)?.data?.baselines) ? (relabelPeer as any).data.baselines.length : 'N/A');
                                  }
                                } catch {}
                                if (relabelPeer && packPeer) {
                                  if (isPack(stType)) {
                                    // 同组存在组合，且当前是装箱 → 由组合渲染器接管，跳过本步
                                    return null;
                                  }
                                  if (isRelabel(stType)) {
                                    const doSubmitForStep = async (stepId:number, metrics:any) => {
                                      await startStage();
                                      await request.post(`/api/v1/instruction/exec/steps/${stepId}/submit/`, { metrics });
                                      load();
                                    };
                                    // 将 stage-ui 返回的 data 合并给 relabel/pack 步，确保 baselines 可用
                                    const relabelData = stageUIData[String(relabelPeer?.id ?? '')] || {};
                                    const packData = stageUIData[String(packPeer?.id ?? '')] || {};
                                    try {
                                      console.debug('[RelabelPack][DATA] relabelStepId=', relabelPeer?.id, 'baselinesLen=', Array.isArray((relabelData as any)?.baselines) ? (relabelData as any).baselines.length : 'N/A');
                                    } catch {}
                                    // 当前为贴换标 → 渲染组合视图
                                    return (
                                      <CombinedRelabelBoxRenderer
                                        key={`combined-${groupKey}`}
                                        taskId={Number(taskId || 0)}
                                        stageNo={(Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx)}
                                        executionSessionId={taskExecutionSessionId}
                                        groupKey={groupKey}
                                        relabelStep={{ ...(relabelPeer || {}), data: relabelData }}
                                        packStep={{ ...(packPeer || {}), data: packData }}
                                        submitRelabel={(m:any) => doSubmitForStep(Number(relabelPeer.id), m)}
                                        submitPack={(m:any) => doSubmitForStep(Number(packPeer.id), m)}
                                        canOperate={statusText === '未完成'}
                                      />
                                    );
                                  }
                                }
                              }
                            } catch {}
                            const getByPath = (obj:any, path:string) => {
                            try {
                              return String(path).split('.').reduce((acc:any, k:string) => (acc != null ? acc[k] : undefined), obj);
                            } catch { return undefined; }
                          };
                            const resolveOptions = (c:any) => {
                            if (Array.isArray(c?.options)) {
                              return c.options.map((o:any) => ({ label: o?.label ?? String(o?.value ?? ''), value: o?.value ?? o?.label })).filter((o:any)=> o.value !== undefined);
                            }
                            if (typeof c?.optionsFrom === 'string') {
                              const source = { metrics: draft, step: st, instr };
                              const arr = getByPath(source, c.optionsFrom);
                              if (Array.isArray(arr)) {
                                return arr.map((o:any) => ({ label: o?.label ?? String(o?.value ?? ''), value: o?.value ?? o?.label })).filter((o:any)=> o.value !== undefined);
                              }
                            }
                            return [];
                          };
                            const renderComp = (c:any, idx:number) => {
                            const type = String(c?.type || 'Input');
                            const label = String(c?.label || c?.name || '');
                            const bind = String(c?.bind || c?.key || '');
                            const placeholder = String(c?.placeholder || '');
                            const min = Number.isFinite(Number(c?.min)) ? Number(c?.min) : undefined;
                            const max = Number.isFinite(Number(c?.max)) ? Number(c?.max) : undefined;
                            const v = draft[bind];
                            if (type === 'Section') {
                              const title = String(c?.title || label || '');
                              const content = String(c?.content || '');
                              const children = Array.isArray(c?.children) ? c.children : [];
                              return (
                                <div key={idx} style={{ background:'#fffbe6', border:'1px solid #ffe58f', borderRadius: 4, padding: 8 }}>
                                  {title ? <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div> : null}
                                  {content ? <div style={{ color:'#8c8c8c', whiteSpace: 'pre-wrap' }}>{content}</div> : null}
                                  {children.length > 0 ? (
                                    <div style={{ marginTop: 8, display:'flex', flexDirection:'column', gap: 8 }}>
                                      {children.map((cc:any, ci:number) => renderComp(cc, Number(`${idx}${ci}`)))}
                                    </div>
                                  ) : null}
    </div>
                              );
                            }
                            if (type === 'Table') {
                              const dataFrom = String(c?.dataFrom || '');
                              const rowKey = String(c?.rowKey || 'id');
                              const size = String(c?.size || 'small');
                              const cols = Array.isArray(c?.columns) ? c.columns : [];
                              const source = { step: { data: stageUIData[sid] || {} }, metrics: draft, instr };
                              const data = dataFrom ? (getByPath(source, dataFrom) || []) : [];
                              const columns = cols.map((col:any) => {
                                const r = String(col?.render || '').trim();
                                if (r === 'booleanTag') {
                                  return {
                                    title: col?.title,
                                    dataIndex: col?.dataIndex,
                                    width: col?.width,
                                    render: (val: any) => <Tag color={!!val ? 'green' : 'default'}>{!!val ? '是' : '否'}</Tag>
                                  };
                                }
                                return {
                                  title: col?.title,
                                  dataIndex: col?.dataIndex,
                                  width: col?.width
                                };
                              });
                              return (
                                <div key={idx}>
                                  <Table
                                    size={size as any}
                                    rowKey={rowKey}
                                    columns={columns as any}
                                    dataSource={Array.isArray(data) ? data : []}
                                    pagination={false}
                                  />
                                </div>
                              );
                            }
                            if (type === 'Select') {
                              const opts = resolveOptions(c);
                              return (
                                <div key={idx} style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  {label ? <span style={{ width: 120 }}>{label}</span> : null}
                                  <Select
                                    style={{ minWidth: 200 }}
                                    options={opts}
                                    value={v}
                                    onChange={(nv)=> setVal(bind, nv)}
                                    placeholder={placeholder || '请选择'}
                                  />
                                </div>
                              );
                            }
                            if (type === 'Switch') {
                              return (
                                <div key={idx} style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  {label ? <span style={{ width: 160 }}>{label}</span> : null}
                                  <Switch checked={!!v} onChange={(nv)=> setVal(bind, !!nv)} />
                                </div>
                              );
                            }
                            if (type === 'Number') {
                              return (
                                <div key={idx} style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  {label ? <span style={{ width: 120 }}>{label}</span> : null}
                                  <InputNumber min={min} max={max} value={v} onChange={(nv)=> setVal(bind, nv)} placeholder={placeholder || '请输入数字'} />
                                </div>
                              );
                            }
                            if (type === 'ScanInput') {
                              return (
                                <div key={idx} style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  {label ? <span style={{ width: 120 }}>{label}</span> : null}
                                  <Input value={v} onChange={(e)=> setVal(bind, e.target.value)} placeholder={placeholder || '扫描/输入'} />
                                </div>
                              );
                            }
                            if (type === 'TextArea' || type === 'Textarea') {
                              return (
                                <div key={idx} style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                                  {label ? <span style={{ width: 120, lineHeight: '32px' }}>{label}</span> : null}
                                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} value={v} onChange={(e)=> setVal(bind, e.target.value)} placeholder={placeholder || '请输入'} />
                                </div>
                              );
                            }
                            if (type === 'Text') {
                              return (
                                <div key={idx} style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  {label ? <span style={{ width: 120 }}>{label}</span> : null}
                                  <Input value={v} onChange={(e)=> setVal(bind, e.target.value)} placeholder={placeholder || '请输入'} />
                                </div>
                              );
                            }
                            // 默认 Input
                            return (
                              <div key={idx} style={{ display:'flex', alignItems:'center', gap:8, color:'#8c8c8c' }}>
                                <span style={{ width: 120 }}>{label || '未支持组件'}</span>
                                <span>未支持的组件类型：{type}</span>
                              </div>
                            );
                          };
                          const submitStep = async () => {
                            try {
                              // 自动标记阶段开始（幂等）
                              await startStage();
                              await request.post(`/api/v1/instruction/exec/steps/${st.id}/submit/`, { metrics: metricsDraft[sid] || {} });
                              message.success('已提交');
                              load();
                            } catch (e:any) {
                              message.error(e?.response?.data?.detail || '提交失败');
                            }
                          };
                          // 自定义渲染器（按 step_type）
                          const rendererKey = String(st?.renderer_key || st?.step_type || '').toUpperCase();
                          const pickMode = String((stageUIData[sid] || {})?.pick_mode || '').trim().toLowerCase();
                          const isPackRendererKey = (
                            rendererKey === 'BOX/BAG_PACK'
                            || rendererKey === 'BOX_PACK'
                            || rendererKey === 'BAG_PACK'
                            || rendererKey === 'BOX'
                            || rendererKey === 'BAG'
                          );
                          const tplName = String(
                            instr?.template_detail?.name
                            || instr?.template_snapshot?.name
                            || '',
                          ).trim().toLowerCase();
                          const tplCode = String(
                            instr?.template_detail?.system_code
                            || instr?.template_snapshot?.system_code
                            || instr?.template_detail?.code
                            || instr?.template_snapshot?.code
                            || '',
                          ).trim().toLowerCase();
                          const isRegularOutboundTemplate = (
                            tplName.includes('常规出库')
                            || (tplCode.includes('regular') && tplCode.includes('outbound'))
                          );
                          const CustomRenderer = (
                            rendererKey === 'PICK' && pickMode === 'ioc'
                              ? IocPickStepRenderer
                              : (isPackRendererKey && isRegularOutboundTemplate)
                                ? RegularOutboundPackRenderer
                              : STEP_RENDERERS[rendererKey]
                          );
                          if (CustomRenderer) {
                            return (
                              <CustomRenderer
                                key={sid}
                                step={st}
                                data={stageUIData[sid] || {}}
                                metrics={draft}
                                instructionDetail={instr}
                                isSortCountTemplate={Array.isArray(execSteps) && execSteps.some((s: any) => {
                                  const key = String(s?.step_type || s?.renderer_key || '').trim().toUpperCase();
                                  return key === 'SORT_AND_COUNT' || key === 'SORT_COUNT';
                                })}
                                onChange={getOnChangeForStep(sid)}
                                onNonstdConfirmGate={(gate: any) => setNonstdConfirmGates(prev => ({ ...prev, [sid]: gate }))}
                                onSubmit={submitStep}
                                instructionCodeFromParent={String((instr?.system_code || `指令#${instrId}`))}
                                canOperate={statusText === '未完成'}
                                taskId={taskId}
                                executionSessionId={taskExecutionSessionId}
                                stageNo={(Number.isFinite(Number(cur?.stage)) ? Number(cur?.stage) : currentStageIdx)}
                              />
                            );
                          }
                          // 无自定义渲染器且组件为空：不显示该步骤占位与提交按钮
                          if (!Array.isArray(comps) || comps.length === 0) {
                            return null;
                          }
                          return (
                            <div key={sid} style={{ background:'#ffffff', border:'1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
                              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                {comps.map((c:any, i:number) => renderComp(c, i))}
                              </div>
                              <div style={{ marginTop: 8, textAlign: 'right' }}>
                                <Space>
                                  <Button size="small" type="primary" onClick={submitStep}>提交</Button>
                                </Space>
                              </div>
                            </div>
                          );
                          })}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>工单（兼容旧视图）</div>
                  {Object.keys(legacyByStatus).length === 0 ? <div style={{ color:'#999' }}>暂无工单</div> : (
                    Object.keys(legacyByStatus).map((s) => (
                      <div key={s} style={{ marginBottom: 12 }}>
                        <div style={{ marginBottom: 6 }}>{s}</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap: 8 }}>
                          {legacyByStatus[s].map((w:any) => (
                            <div key={w.id} style={{ border:'1px solid #f0f0f0', borderRadius: 6, padding: 8, width: 260 }}>
                              <div style={{ fontWeight: 600 }}>{w.name || w.template_snapshot?.name || w.type_code || w.template?.name || `工单#${w.id}`}</div>
                              <div style={{ color:'#999' }}>{w.system_code || w.id}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          </div>
        ) : null}
      </Card>
      <Modal
        title="当前操作阶段 - 使用说明"
        open={stageHelpOpen && showStageHelp}
        onCancel={() => setStageHelpOpen(false)}
        footer={<Button type="primary" onClick={() => setStageHelpOpen(false)}>关闭</Button>}
        width={900}
      >
        <div style={{ lineHeight: 1.7, color: '#141414' }}>
          <div style={{ marginBottom: 10, color: '#595959' }}>
            说明：本说明聚焦“换标+装箱”执行阶段，解释如何判断是否可生成记录，以及记录会落库到哪个匹配对象（IU）。内容以页面当前实现为准。
          </div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>1) 扫码模式</div>
          <ul style={{ marginTop: 0, paddingLeft: 18 }}>
            <li><strong>打印并加入记录（默认）</strong>：生成操作记录并打印标签。</li>
            <li><strong>仅加入记录不打印</strong>：只生成记录（适用于已贴新标签）。</li>
            <li><strong>仅打印不加入记录</strong>：只允许输入新条码补打；不生成记录、不做映射/对象审核。</li>
          </ul>

          <div style={{ fontWeight: 700, marginBottom: 6, marginTop: 10 }}>2) 关键数据来源</div>
          <ul style={{ marginTop: 0, paddingLeft: 18 }}>
            <li>新旧条码映射来自基线文件 BARCODE_MAPPING（顺序以文件行号为准）。</li>
            <li>“操作对象匹配信息”决定该映射行可落库的 IU 列表、是否操作全部库存、预计数量。</li>
            <li>IU 的“已完成数量”按<strong>全局口径</strong>统计：同一 IU 被多条映射共享时会同步占用。</li>
          </ul>

          <div style={{ fontWeight: 700, marginBottom: 6, marginTop: 10 }}>3) 审核规则（决定是否能生成记录）</div>
          <ul style={{ marginTop: 0, paddingLeft: 18 }}>
            <li><strong>审核A（映射行上限）</strong>：数量分配(limit) 限制每条映射行可操作的剩余额度。</li>
            <li><strong>审核B（对象上限）</strong>：按匹配对象顺序分配到 IU；expected_qty 缺失视为不限；最后一个对象 use_all_available=是 兜底视为不限（但仍受审核A约束）。</li>
          </ul>

          <div style={{ fontWeight: 700, marginBottom: 6, marginTop: 10 }}>4) 扫旧条码（1旧多新）如何生成记录</div>
          <ul style={{ marginTop: 0, paddingLeft: 18 }}>
            <li>按 BARCODE_MAPPING 顺序遍历该旧条码对应的多条 NEW 映射行。</li>
            <li>输入数量会被拆分分摊：先按映射行可用额度分摊，再在每条映射行内按匹配对象顺序分摊到 IU。</li>
            <li>若总额度不足：会先生成“可容纳部分”的记录，弹窗提示剩余无法生成，并给出本次拆分明细。</li>
          </ul>

          <div style={{ fontWeight: 700, marginBottom: 6, marginTop: 10 }}>5) 扫新条码（多旧1新）如何生成记录</div>
          <ul style={{ marginTop: 0, paddingLeft: 18 }}>
            <li>若新条码对应多条旧条码映射，会弹窗要求选择其中一条映射行。</li>
            <li>选定映射行后：数量在该映射行内按对象顺序拆分分摊到 IU。</li>
          </ul>

          <div style={{ fontWeight: 700, marginBottom: 6, marginTop: 10 }}>6) 打印口径</div>
          <ul style={{ marginTop: 0, paddingLeft: 18 }}>
            <li>打印份数 = <strong>本次实际成功生成记录的总数量</strong>（不是输入数量）。</li>
            <li>扫旧条码拆分到多个新条码时，按新条码汇总打印。</li>
          </ul>
        </div>
      </Modal>
      {/* 分拣清点快照弹窗 */}
      <Modal
        title={`分拣清点快照 - 指令：${String((instr?.system_code || instrId))}`}
        open={scSnapshotVisible}
        onCancel={() => setScSnapshotVisible(false)}
        footer={[<Button key="close" onClick={() => setScSnapshotVisible(false)}>关闭</Button>]}
        width={1120}
      >
        {(() => {
          const root = scSnapshotData?.snapshot || {};
          return <SortCountSnapshotBlock snapshotEnvelope={root} />;
        })()}
      </Modal>
      {/* 拣货快照弹窗（放在顶层，保证始终挂载） */}
      <Modal
        title={renderResultTitle(`拣货结果 - 指令：${String((instr?.system_code || instrId))}`, (snapshotSummary?.flags || { hasOver:false, hasUnder:false, allZero:false }))}
        open={snapshotVisible}
        onCancel={() => setSnapshotVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSnapshotVisible(false)}>关闭</Button>
        ]}
        width={760}
      >
        {snapshotSummary ? (
          String((snapshotData as any)?.pick_mode || (snapshotData as any)?.ui_context?.pick_mode || '').trim().toLowerCase() === 'ioc'
            ? (
              <IocPickSnapshotContent
                rows={Array.isArray(snapshotIocPreview?.rows) ? snapshotIocPreview!.rows : []}
                stats={snapshotIocPreview?.stats || { total: 0, picked: 0, todo: 0 }}
                headerRecordCount={snapshotIocPreview?.headerRecordCount ?? 0}
                detailPickLogs={Array.isArray(snapshotIocPreview?.detailPickLogs) ? snapshotIocPreview!.detailPickLogs : []}
              />
            )
            : renderPickResultContent(snapshotSummary.targetSummary, snapshotSummary.containerSummary)
        ) : null}
      </Modal>
      {/* 换标+装箱快照弹窗（最小版：内容/样式按阶段单独设计；此处仅展示提交时存的 snapshot） */}
      <Modal
        title={`换标+装箱快照 - 指令：${String((instr?.system_code || instrId))}`}
        open={rpSnapshotVisible}
        onCancel={() => setRpSnapshotVisible(false)}
        footer={[<Button key="close" onClick={() => setRpSnapshotVisible(false)}>关闭</Button>]}
        width={1240}
      >
        {(() => {
          const snapRoot = rpSnapshotData?.snapshot || {};
          const snapPayload = (snapRoot && snapRoot.snapshot) ? snapRoot.snapshot : snapRoot; // 后端存的是 {schema_version, stats, snapshot:{...}}，这里取 snapshot 部分
          const auditZip = (snapRoot && typeof snapRoot === 'object') ? (snapRoot as any)?.audit_baselines_zip : null;
          const resolveBackendUrl = (raw: any): string => {
            const u = String(raw || '').trim();
            if (!u) return '';
            if (/^https?:\/\//i.test(u)) return u;
            let backendOrigin = window.location.origin;
            try {
              const x = new URL(backendOrigin);
              if (x.port === '3000') { x.port = '8000'; backendOrigin = x.toString().replace(/\/$/, ''); }
            } catch {}
            const path = u.startsWith('/') ? u : `/${u}`;
            return `${backendOrigin}${path}`;
          };
          const auditZipUrl = resolveBackendUrl((auditZip as any)?.file || (auditZip as any)?.url || '');
          const auditZipName = String((auditZip as any)?.file_name || (auditZip as any)?.name || 'baseline_audit.zip');
          const groups = Array.isArray(snapPayload?.groups) ? snapPayload.groups : [];
          const genLabelMap = (snapPayload && (snapPayload as any)?.generated_labels && typeof (snapPayload as any).generated_labels === 'object')
            ? ((snapPayload as any).generated_labels as Record<string, any>)
            : {};
          const headStats = (snapRoot && snapRoot.stats) ? snapRoot.stats : {};
          const ps = (groups || []).map((g:any) => g?.progress_snapshot).filter(Boolean);
          const overall = ps.find((x:any) => x?.overall)?.overall || null;
          const mappingRows: any[] = ps.flatMap((x:any) => (Array.isArray(x?.mapping_rows) ? x.mapping_rows : []));
          const boxRows: any[] = ps.flatMap((x:any) => (Array.isArray(x?.boxes_rows) ? x.boxes_rows : []));
          const pool = ps.find((x:any) => x?.pool_summary)?.pool_summary || null;
          const recordRows: any[] = (groups || []).flatMap((g:any) => (Array.isArray(g?.records_preview) ? g.records_preview : []));
          const hasProgress = ps.length > 0;
          return (
            <div style={{ lineHeight: 1.7 }}>
              <div style={{ marginBottom: 10, padding: '10px 12px', border: '1px solid #91caff', background: '#e6f4ff', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 700, color: '#0958d9' }}>基线文件审计包（zip）</div>
                  {auditZipUrl ? (
                    <Button
                      size="small"
                      type="primary"
                      onClick={async () => {
                        try {
                          const finalUrl = `${auditZipUrl}${auditZipUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
                          const resp = await fetch(finalUrl, { credentials: 'include' as any });
                          if (!resp.ok) throw new Error('下载失败');
                          const blob = await resp.blob();
                          const href = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = href;
                          a.download = auditZipName || 'baseline_audit.zip';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          setTimeout(() => URL.revokeObjectURL(href), 1500);
                          message.success('已开始下载审计包');
                        } catch (e:any) {
                          message.error(e?.message || '下载失败');
                        }
                      }}
                    >
                      下载审计包
                    </Button>
                  ) : (
                    <span style={{ color: '#8c8c8c' }}>该快照未生成审计包（可能为旧版本提交或后端生成失败）</span>
                  )}
                </div>
                {auditZipUrl ? (
                  <div style={{ marginTop: 6, color: '#595959', fontSize: 12 }}>
                    文件名：<span style={{ fontFamily: 'monospace' }}>{auditZipName}</span>
                    {Number.isFinite(Number((auditZip as any)?.count)) ? (
                      <span style={{ marginLeft: 10 }}>包含基线文件：{Number((auditZip as any)?.count)} 个</span>
                    ) : null}
                  </div>
                ) : null}
                {(snapRoot as any)?.audit_baselines_zip_error ? (
                  <div style={{ marginTop: 6, color: '#cf1322', fontSize: 12 }}>
                    审计包生成失败：{String((snapRoot as any).audit_baselines_zip_error || '')}
                  </div>
                ) : null}
              </div>
              <RelabelPackSnapshotBlock
                overall={overall}
                mappingRows={mappingRows}
                boxRows={boxRows}
                pool={pool}
                records={recordRows}
                genLabelMap={genLabelMap}
                emptyHint={hasProgress ? '（未采集到总进度表快照）' : '未采集到总进度表快照'}
              />
              <RelabelPackRecordsSnapshotBlock
                records={recordRows}
                mappingRows={mappingRows}
                emptyHint="该快照未固化操作记录（请使用新版本前端在该阶段点击“提交记录并完成本阶段”生成快照）"
              />
            </div>
          );
        })()}
      </Modal>
      {/* 常规出库“配货|装箱|贴单”快照弹窗 */}
      <Modal
        title={`常规出库配货|装箱|贴单快照 - 指令：${String((instr?.system_code || instrId))}`}
        open={ropSnapshotVisible}
        onCancel={() => setRopSnapshotVisible(false)}
        footer={[<Button key="close" onClick={() => setRopSnapshotVisible(false)}>关闭</Button>]}
        width={1120}
      >
        {(() => {
          const root = ropSnapshotData?.snapshot || {};
          return <RegularOutboundSnapshotBlock snapshotEnvelope={root} />;
        })()}
      </Modal>
      {/* 单指令代发“配货|装箱|贴单”快照弹窗 */}
      <Modal
        title={(() => {
          const root = sdpSnapshotData?.snapshot || {};
          const snap = (root && root.snapshot) ? root.snapshot : root;
          const metrics = snap?.metrics || {};
          const raw = String(metrics?.process_result_status || '').trim().toLowerCase();
          const text = raw === 'completed' ? '已完成' : (raw === 'unable' ? '无法操作' : '未标注');
          const badgeStyle: React.CSSProperties = raw === 'completed'
            ? { background: '#52c41a', color: '#fff', border: '1px solid #52c41a' }
            : (raw === 'unable'
              ? { background: '#ff4d4f', color: '#fff', border: '1px solid #ff4d4f' }
              : { background: '#f5f5f5', color: '#595959', border: '1px solid #d9d9d9' });
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 24 }}>
              <span>{`配货|装箱|贴单快照 - 指令：${String((instr?.system_code || instrId))}`}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                <span>处理结果：</span>
                <span style={{ ...badgeStyle, borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                  {text}
                </span>
              </span>
            </div>
          );
        })()}
        open={sdpSnapshotVisible}
        onCancel={() => setSdpSnapshotVisible(false)}
        footer={[<Button key="close" onClick={() => setSdpSnapshotVisible(false)}>关闭</Button>]}
        width={980}
      >
        {(() => {
          const root = sdpSnapshotData?.snapshot || {};
          const snap = (root && root.snapshot) ? root.snapshot : root;
          const stats = root?.stats || {};
          const metrics = snap?.metrics || {};
          const opRows = Array.isArray(metrics?.operation_records) ? metrics.operation_records : [];
          const metricsWaybillChargeAmount = String(metrics?.waybill_charge_amount ?? metrics?.buy_waybill_info?.charge_info?.amount ?? '').trim();
          const metricsWaybillChargeCurrency = String(metrics?.waybill_charge_currency ?? metrics?.buy_waybill_info?.charge_info?.currency ?? '').trim();
          const metricsWaybillChargeNote = String(metrics?.waybill_charge_note ?? metrics?.buy_waybill_info?.charge_info?.note ?? '').trim();
          return (
            <div style={{ lineHeight: 1.7 }}>
              <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #91caff', borderRadius: 8, background: '#f0f5ff' }}>
                <div style={{ fontWeight: 600, color: '#0958d9', marginBottom: 6 }}>运单信息</div>
                <div>
                  <span style={{ marginRight: 20 }}>运单：{(metrics?.waybill_carrier && metrics?.waybill_tracking_no) ? '已填写' : (stats?.has_waybill ? '已填写' : '未填写')}</span>
                  <span style={{ marginRight: 20 }}>承运商：{String(metrics?.waybill_carrier || '-')}</span>
                  <span>物流跟踪号：{String(metrics?.waybill_tracking_no || '-')}</span>
                </div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ marginRight: 20 }}>
                    运单金额：{metricsWaybillChargeAmount
                      ? `${metricsWaybillChargeAmount} ${metricsWaybillChargeCurrency || 'USD'}`
                      : '-'}
                  </span>
                  <span>费用备注：{metricsWaybillChargeNote || '-'}</span>
                </div>
              </div>
              <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: 8, background: '#f6ffed' }}>
                <div style={{ fontWeight: 600, color: '#389e0d', marginBottom: 6 }}>包装数据与指令产出容器信息</div>
                <div style={{ marginBottom: 4 }}>包装数据：{
                  (metrics?.actual_pack_length_cm && metrics?.actual_pack_width_cm && metrics?.actual_pack_height_cm && metrics?.actual_gross_weight_g)
                    ? `${metrics.actual_pack_length_cm}×${metrics.actual_pack_width_cm}×${metrics.actual_pack_height_cm} cm, ${metrics.actual_gross_weight_g} g`
                    : '-'
                }</div>
                <div>指令产出容器：{String(metrics?.created_ioc_code || stats?.ioc_code || '-')}</div>
              </div>
              <div style={{ padding: '10px 12px', border: '1px solid #ffd591', borderRadius: 8, background: '#fff7e6' }}>
                <div style={{ fontWeight: 600, color: '#d46b08', marginBottom: 6 }}>
                  操作记录（共 {Number(stats?.operation_records_count || opRows.length || 0)} 条）
                </div>
                <Table
                  size="small"
                  rowKey={(r:any, idx?:number) => String(r?.time || `${idx || 0}`)}
                  pagination={{
                    current: sdpSnapshotOpPage,
                    pageSize: sdpSnapshotOpPageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ['5', '10', '20', '50'] as any,
                    onChange: (page:number, pageSize:number) => {
                      setSdpSnapshotOpPage(page);
                      if (pageSize !== sdpSnapshotOpPageSize) {
                        setSdpSnapshotOpPageSize(pageSize);
                        setSdpSnapshotOpPage(1);
                      }
                    },
                  }}
                  locale={{ emptyText: '暂无操作记录快照' }}
                  dataSource={opRows.map((r:any, idx:number) => ({
                    key: `${idx}-${String(r?.time || '')}`,
                    time: String(r?.time || '-'),
                    action: String(r?.action || '-'),
                    remark: String(r?.remark || '-'),
                  }))}
                  columns={[
                    { title: '时间', dataIndex: 'time', key: 'time', width: 170 },
                    { title: '操作', dataIndex: 'action', key: 'action', width: 180 },
                    { title: '备注', dataIndex: 'remark', key: 'remark' },
                  ] as any}
                />
              </div>
            </div>
          );
        })()}
      </Modal>
      {/* 库存非标准操作：阶段快照弹窗（只读） */}
      <Modal
        title={`${nonstdSnapshotView?.stage === 'confirm' ? '确认完成快照' : '执行记录快照'} - 指令：${String((instr?.system_code || instrId))}`}
        open={!!nonstdSnapshotView}
        onCancel={() => setNonstdSnapshotView(null)}
        footer={[<Button key="close" onClick={() => setNonstdSnapshotView(null)}>关闭</Button>]}
        width={980}
        destroyOnClose
      >
        {nonstdSnapshotView ? <NonStdSnapshotView snapshot={nonstdSnapshotView} /> : null}
      </Modal>
      {/* 收尾快照弹窗（按页面四板块展示；板块即使为空也会保留） */}
      <Modal
        title={`收尾快照 - 指令：${String((instr?.system_code || instrId))}`}
        open={wuSnapshotVisible}
        onCancel={() => setWuSnapshotVisible(false)}
        footer={[<Button key="close" onClick={() => setWuSnapshotVisible(false)}>关闭</Button>]}
        width={980}
      >
        {(() => {
          const snapRoot = wuSnapshotData?.snapshot || {};
          const snapPayload = (snapRoot && (snapRoot as any).snapshot) ? (snapRoot as any).snapshot : snapRoot;
          return (
            <div style={{ lineHeight: 1.7 }}>
              <WrapUpSnapshotBlock snapshot={snapPayload} />
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default StaffInstructionExecutePage;


